// Editing, always on when the editor server is running: every scan ticked in
// the cloud list is loaded for editing (see session.ts), selections span
// scans (one mask per scan, combined with set algebra; see select.ts), and an
// action commits one op to each scan it touches, all with the same time and
// message so history and undo treat them as one. Objects, parts and groups
// with the same name in several scans are one thing in the UI.
import type { Viewer } from '../viewer.ts';
import type { Controls } from '../controls.ts';
import { type Rigid, Grid, apply, compose, identity, inverse, isIdentity, mat4, transformPoints, yawDeg } from '../align.ts';
import { type V3, add, clamp, dist, project } from '../math.ts';
import type { CloudMeta } from '../format.ts';
import { EditApi, EditSession, type OpBody, onHeadMoved } from './session.ts';
import { Uploader, pagePicker, pageUrl } from '../scenes.ts';
import { proc, type Proc } from '../procs.ts';
import { realignOps } from './realign.ts';
import { type FrameScan, alignFrames as alignFramesRun, frameD, markOps, solvePoints, springAll, tiesOf, worldToNodeD } from './frameops.ts';
import { Gizmo } from '../gizmo.ts';
import { BOX_EDGES } from '../volumes.ts';
import { tidyGroups } from './groups.ts';
import * as sel from './select.ts';
import { dissolve as dissolvePts } from './dissolve.ts';
import { linkStretch, stretchColor, stretchScale, tieGap } from './springs.ts';
import { DELETED, MAX_ID, MAX_MARK_W, type Mark, markVal, nextId, type Cls, type By, type Tree, chainOf, frameTransform, GROUP_NAME, groupAndBelow, CLS, MAX_CLASS, nextClassId, isClassName, isTypeName, shapeKey } from './log.ts';
import { type Pts, natural, nextInstanceName, asCloud, centroid, thin, heat } from './instances.ts';
import { type Task, type TaskResult } from './density.ts';
import densityWorkerSrc from 'worker:src/edit/densityworker.ts';
import { type PidSet, decodeSet, encodeSet, maskToPids } from './pidset.ts';
import * as ovl from './overlay.ts';
import { type Action, commitMany, undoNewest, redoAction, stampNow } from './history.ts';
import * as instops from './instops.ts';
import { decodeVoxels } from './voxels.ts';
import { type CamImages, baseFrustum, loadDepth, loadImage, opticalFrustum } from './camimages.ts';
import { type DepthFrame, type WeightOp, applyOp, blurFrame, byFrame, frameSelGrid, frameWeightGrid, pixelsOf } from './weights.ts';
import { idsNamed, place, instId } from './overlay.ts';
import { type ShapeDoc, type ShapeKind, roundDoc } from '../fit/doc.ts';
import { shapeLines } from '../fit/lines.ts';
import { SHAPE_PARAMS, fitShape, shapeParamFields } from '../fit/run.ts';
import { editorFeedback, type Fields } from './feedback.ts';
import { type MemGroups, memCount, memEdit } from './memgroups.ts';
import { type Env, type Field, type Kind, type Macro, type Params, type RunStep, type Step as MacroStep, type StepKind, KINDS, MODES, WEIGHTS, allSteps, commits, describe, edits as editsOf, exprOf, fieldValue, invertible, flatten, kindOf, newStep, paramValue, runSize, shown as fieldShown, takes, tuneCounts, tuneListEdit, varOf, varsOf, whereIs } from './macros.ts';
import { hasIo, isTemp } from './nodes.ts';
import { NodeUI, type Tap } from './nodeui.ts';
import { type TuneStat, sayStat } from './tune.ts';
import { type Cloud, type Ctx, type Dens as EvDens, type Held, type Mem, type Out, type Src, colourBand, dataDensR, evalStep, measuresFor, srcKeys, tuneKey } from './macroeval.ts';
import { type DataGroups, dataCount, dataLanded, dataRange, dataSets } from './datagroups.ts';
import { MEASURES, evalDataExpr, parseDataExpr } from './dataexpr.ts';
import { type End, type PhotoDone, type Source, idOf, roundsText, runMacro as runInWorker, where as whereP } from './macrorun.ts';
import { anyField, askVars } from './macrolaunch.ts';
import { MacroPins } from './macropins.ts';
import photoWorkerSrc from 'worker:src/photo/photoworker.ts';
import type { PhotoResult, PhotoTask, ShowTask, ShownPhoto } from '../photo/photoworker.ts';
import { type PhotoOp, keepsExposure } from '../photo/photofilter.ts';
import { neighbourPairs, pairRatio, solveGains } from '../photo/exposure.ts';
import { makePool } from '../objects/jobs.ts';
import { type PhotoScan, PhotoRun } from '../photo/photorun.ts';
import type { FrameFeats } from '../photo/feats.ts';
import { featCodes, slim } from '../photo/featshade.ts';
import { featKey, getFeats, keep as keepFeats, putFeats, unkeep } from '../photo/featstore.ts';
import { pgHost } from '../photo/pghost.ts';
import type { PG } from '../photo/jobs.ts';
import { overlay } from '../photo/draw.ts';
import meshWorkerSrc from 'worker:src/mesh/meshworker.ts';
import type { MeshDone, MeshJob, RegionSpec } from '../mesh/meshworker.ts';
import type { FrameMasks, MeshFrame, MeshPart } from '../mesh/types.ts';
import { anyMasks, frameMasks } from '../mesh/region.ts';
import { ENGINE_FIELDS } from '../mesh/fields.ts';
import { download } from '../download.ts';
import { ConsUI, PLANE_MAX_PTS } from './consui.ts';
import { planeFit } from './construction.ts';

type Tool = null | 'lasso' | 'brush' | 'seed';
/** Toolbar entries: pointer tools, and rules that show their parameters. */
type Entry = 'lasso' | 'brush' | 'seed' | 'box' | 'normal' | 'band' | 'distance' | 'edges' | 'colour' | 'density' | 'view' | 'frames' | 'points' | 'tune';
const POINTER: Entry[] = ['lasso', 'brush', 'seed'];   // the lasso is the View's action whenever Brush or Grow isn't picked
const CLS_LAYER = [['cls:1', 'floor', '#33cc4d'], ['cls:2', 'solid', '#ff8c1a'], ['cls:3', 'movable', '#b36bff'], ['cls:0', 'unclassified', '#9aa0a6']] as const;
/** A layer row's button: label, tooltip, action, and whether it shows as on. */
type LayerButton = [string, string, () => void, boolean?];
/** A group row's icon (and its tip): saved in the scan, or kept only by this page. */
const SAVED_ICON: [string, string] = ['◆', 'saved group: in the scan\'s edits'];
const MEM_ICON: [string, string] = ['◇', 'memory group: only in this page, not saved (Save writes it to the scan)'];
const DATA_ICON: [string, string] = ['#', 'data group: a number per point, only in this page (Data group / Data math macro steps make and change it)'];
/** Colours offered to new user classes, in order. */
const CLASS_PALETTE = ['#e6194b', '#4363d8', '#f58231', '#42d4f4', '#f032e6', '#bfef45', '#fabed4', '#469990', '#dcbeff', '#9a6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1', '#000075'];
const hexRgb = (h: string) => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
type Rule = Record<string, unknown> | null;
type State = EditSession['state'];

/** One loaded scan and its share of the selection. */
interface Scan {
  s: EditSession; mask: Uint8Array | null; cand: Uint8Array | null; hide: Uint8Array | null; grid: { g: Grid; r: number } | null; tv?: number; fcount?: Uint32Array;
  /** While the scan is unticked in Scans: its selection, set aside (mask is null) and back when it's ticked again. */
  parked?: Uint8Array | null;
  /** The scan path's most stretched link (see drawPath), while it is shown. */
  path?: { cm: number; deg: number; n: number } | null;
  /** Its locked frames' cameras are drawn (drawLocked). */
  locked?: boolean;
  /** Show cameras: the selected frames, as refresh() found them (drawCameras draws these); its cameras are drawn. */
  camFrames?: Set<number>;
  cams?: boolean;
  /** In the instance viewer: each vertex where the overlay draws it (NaN: not shown), and whether it can be selected. */
  view?: { P: Float32Array; N: Float32Array; in: Uint8Array; grid?: { g: Grid; r: number; P: Float32Array } } | null;
}
/** A frame's camera as drawn (Show cameras): its scan and frame, the camera's world position, the way it looks and its frustum lines. */
type CamHit = { sc: Scan; f: number; c: V3; fwd: V3; seg: [V3, V3][] };
/** An instance's box as drawn: the instance, its type, the box's 8 world corners (see ovl.boxCorners). */
type BoxHit = { name: string; type: string; c: V3[] };
/** A selection as it was: per scan, runs of selected vertices [start, length, ...]. */
type SelSnap = { masks: Map<string, Uint32Array>; rule: Rule; mem: Map<string, MemGroups>; data: Map<string, DataGroups> };
/** Per scan id, its points' weights (weights.ts) as they were. */
type WeightSnap = Map<string, Float32Array>;
/** Undo steps, newest last: a selection change (the selection before it) or an edit (the selection before it); w: a macro changed weights (them before it). */
type Step = { k: 'sel'; snap: SelSnap; w?: WeightSnap } | { k: 'edit'; snap: SelSnap; w?: WeightSnap };
/** Redo steps: a selection to go back to, or an edit to redo and the selection after it; w: the weights after it. */
type Redo = { k: 'sel'; snap: SelSnap; w?: WeightSnap } | { k: 'edit'; act: Action; snap: SelSnap; w?: WeightSnap };
const MAX_STEPS = 100;

/** A point density measurement (EditUI.densityOf): per scan, each vertex's points within r (0: deleted); range: 2nd to 98th percentile. */
interface Dens { key: string; r: number; step: number; d: Map<string, Uint16Array>; range: [number, number] }

function runs(m: Uint8Array): Uint32Array {
  const out: number[] = [];
  for (let i = 0, n = m.length; i < n;) {
    if (!m[i]) { i++; continue; }
    let j = i;
    while (j < n && m[j]) j++;
    out.push(i, j - i);
    i = j;
  }
  return Uint32Array.from(out);
}
function unruns(r: Uint32Array, n: number): Uint8Array {
  const m = new Uint8Array(n);
  for (let k = 0; k < r.length; k += 2) m.fill(1, Math.min(n, r[k]), Math.min(n, r[k] + r[k + 1]));
  return m;
}

const el = (tag: string, attrs: Record<string, string> = {}, text = '') => {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  if (text) e.textContent = text;
  return e;
};
const hueCss = (id: number, off = 0) => `hsl(${Math.round(((id * 0.618 + off) % 1) * 360)} 80% 55%)`;
const ago = (t: string) => {
  const s = (Date.now() - Date.parse(t)) / 1000;
  return s < 60 ? 'now' : s < 3600 ? `${Math.round(s / 60)} min` : s < 86400 ? `${Math.round(s / 3600)} h` : t.slice(0, 10);
};
const OP_LABEL: Record<string, string> = {
  delete: 'Delete', restore: 'Restore', classify: 'Classify', 'object.new': 'New object', 'object.assign': 'Add to object',
  'object.release': 'Release', 'object.transform': 'Move object', 'object.meta': 'Rename object', 'object.of': 'Set type / fit', 'object.frames': 'Fit object frames', 'part.new': 'Split off',
  'part.merge': 'Merge part', 'group.save': 'Save group', 'group.delete': 'Delete group',
  'tree.group': 'Group frames', 'tree.move': 'Move group', 'tree.ungroup': 'Ungroup', 'tree.transform': 'Align', 'tree.lock': 'Lock frames',
  'label.new': 'New label', 'label.meta': 'Rename label', 'label.delete': 'Delete label', 'label.assign': 'Label points',
  'shape.set': 'Fit shape', 'shape.drop': 'Drop shape', 'type.box': 'Set box', 'type.voxels': 'Set voxels',
};
/** An alignment-tree node: the scan, a group, or one frame. */
type TreeNode = { k: 'scan' } | { k: 'g'; name: string } | { k: 'f'; frame: number };
const nodeKey = (n: TreeNode) => (n.k === 'scan' ? 'scan' : n.k === 'g' ? `g:${n.name}` : `f:${n.frame}`);
/** The node's own D and the transform of the frame it sits in (D_scan · … · parent), both in the scan's file frame. */
function nodeD(t: Tree, n: TreeNode): { D: Rigid; prefix: Rigid } {
  const I: Rigid = { R: [1, 0, 0, 0, 1, 0, 0, 0, 1], t: [0, 0, 0] };
  if (n.k === 'scan') return { D: t.scanD, prefix: I };
  if (n.k === 'g') { const g = t.groups.get(n.name)!; return { D: g.D, prefix: chainOf(t, g.parent) }; }
  return { D: t.frameD.get(n.frame) ?? I, prefix: chainOf(t, t.frameGroup.get(n.frame) ?? null) };
}
/** Row i of per-frame matrices (12 floats each: rows of R | t) as a Rigid. */
const matRow = (m: Float32Array, i: number): Rigid => { const o = 12 * i; return { R: [m[o], m[o + 1], m[o + 2], m[o + 4], m[o + 5], m[o + 6], m[o + 8], m[o + 9], m[o + 10]], t: [m[o + 3], m[o + 7], m[o + 11]] }; };
/** Is frame f under node n? */
function under(t: Tree, n: TreeNode, f: number) {
  if (n.k === 'scan') return true;
  if (n.k === 'f') return n.frame === f;
  for (let g = t.frameGroup.get(f) ?? null, k = 0; g !== null && k <= t.groups.size; k++) { if (g === n.name) return true; g = t.groups.get(g)!.parent; }
  return false;
}
/** "moved 3.2 cm, 0.4°" for a non-identity D. */
function moved(D: Rigid) {
  if (isIdentity(D, 1e-7)) return '';
  const cm = Math.hypot(...D.t) * 100, deg = (Math.acos(Math.min(1, Math.max(-1, (D.R[0] + D.R[4] + D.R[8] - 1) / 2))) * 180) / Math.PI;
  return ` · ${cm.toFixed(1)} cm, ${deg.toFixed(2)}°`;
}
/** Ops that act on the selected points (the rest name an object, part or group). */
const SET_OPS = new Set<string>(['delete', 'restore', 'classify', 'object.new', 'object.assign', 'object.release', 'part.new', 'group.save', 'label.assign']);
/** A move's preview (see ghost()). */
const GHOST = '~ghost';
/** Instances drawn as their type's voxel model: an edit cloud each, '~vox:<instance name>'. */
const VOX = '~vox:';
/** The instance overlay's edit cloud. */
const OVERLAY = '~overlay';
/** How far (m) from an instance's points Absorb looks for points to add to it. */
const ABSORB_REACH = instops.ABSORB_REACH;
/** One-off shapes' wireframe colours. */
const SHAPE_RGBA: Record<ShapeKind, [number, number, number, number]> = { pole: [1, 0.72, 0.15, 0.95], surface: [0.3, 0.85, 1, 0.95] };
/** Planes' wireframe colour (Construction). */
const PLANE_RGBA: [number, number, number, number] = [0.55, 1, 0.45, 0.9];
/** The construction overlays' show toggles, all ticked while the Construction tab is in front. */
const CONS_TOGGLES = ['eshshow', 'eboxshow', 'eptshow', 'evoxshow', 'eplshow'];
/** The Display tab's show toggles, each a copy of one in the tab that makes what it shows: [Display's, its own]. */
const DISPLAY_MIRRORS = [['dshowcls', 'eshowcls'], ['dshowobj', 'eshowobj'], ['dptshow', 'eptshow'], ['dboxshow', 'eboxshow'],
  ['dvoxshow', 'evoxshow'], ['dshshow', 'eshshow'], ['dshtop', 'eshtop'], ['dplshow', 'eplshow']] as const;
/** Most selected points a one-off shape is fitted to (more are thinned evenly). */
const SHAPE_MAX_PTS = 300_000;
const nextFrame = () => new Promise(r => requestAnimationFrame(() => setTimeout(r, 0)));
/** A node graph's node of kind t edits groups or frames (macros.ts EDITS). */
const nodeEdits = (t: string) => !!kindOf(t) && editsOf({ t: t as StepKind, p: {} });

/** What a rule tree selects by, for a split's lineage: only frame ranges, only boxes, or anything. */
function ruleBy(r: Rule): By {
  const leaves: string[] = [];
  const walk = (x: Rule) => { if (!x) return; if ('a' in x) { walk(x.a as Rule); walk(x.b as Rule); } else leaves.push(String(x.t)); };
  walk(r);
  if (leaves.length && leaves.every(t => t === 'frames')) return 'frames';
  if (leaves.length && leaves.every(t => t === 'box')) return 'volume';
  return 'selection';
}

export class EditUI {
  api: EditApi;
  scans = new Map<string, Scan>();
  editable = new Set<string>();          // scans the server can edit (orig/manifest.json)
  rule: Rule = null;                     // how the selection was built (stored with ops)
  mode: sel.Combine = 'replace';
  tool: Tool = 'lasso';                  // the pointer's: the lasso unless Brush or Grow is picked
  entry: Entry | null = null;            // the toolbar entry whose parameters are showing
  /** Layers switched off: 'deleted', 'cls:<k>' (built-in), 'clsn:<name>' (user class), 'obj:<name>', 'part:<name>', 'group:<name>'. */
  hidden = new Set<string>(['deleted']);
  /** A point group shown on its own (with the groups inside it): every other point is hidden too. */
  soloGroup: string | null = null;
  /** Memory groups (see memgroups.ts), per scan id: a macro's Group edits make them; Group save writes one to the scan. */
  private mem = new Map<string, MemGroups>();
  /** A frame shown on its own while its named point is moved (movePoint): every other point, in every scan, is hidden. */
  private soloFrame: { scan: string; f: number } | null = null;
  /** A class shown on its own (by name: floor, unclassified, a user class…): only its points show, in objects or not. */
  soloClass: string | null = null;
  busy = false;
  private on = false;
  private loading: string | null = null;
  private past: Step[] = [];
  /** The last tapped grow, so Apply can grow again from its seed with new settings; the selection before it. */
  private lastGrow: { scan: string; seed: number; mode: sel.Combine; before: SelSnap; rule: Rule } | null = null;
  private growing = false;
  private future: Redo[] = [];
  private poly: [number, number][] = [];
  private stroke: { mode: sel.Combine; fresh: boolean } | null = null;   // fresh: intersecting nothing, so a new selection
  private plane: [number, number, number, number] | null = null;
  /** The macro being built in the Macros dialog (a copy: Save stores it), and the step a run stopped at. */
  private macro: Macro = { name: '', steps: [] };
  private macros: Macro[] = [];
  /** The Nodes tab: a macro as a node graph (nodeui.ts). */
  private nodes: NodeUI | null = null;
  /** Saved macros' buttons dragged out of the Saved macros tab onto the header, the View and panels (macropins.ts). */
  private pins: MacroPins | null = null;
  private macroBad = -1;
  /** The dialog's step running now (its macro running), and the run's Cancel. */
  private macroAt = -1;
  private macroStop: (() => void) | null = null;
  /** The run's Finish for an Auto tune step under way (it ends with the best it has; the run goes on). */
  private macroFinish: (() => void) | null = null;
  /** Per Auto tune step of the dialog's macro (by its place), how its last run went or the one under way is going. */
  private tuneLive = new Map<number, TuneStat>();
  /** The Tools tab's and the View's Auto tune, apart from any macro: its settings and lists (kept while the page is open). */
  private soloTune: MacroStep = newStep('tune', {}, 'replace');
  /** How its run under way is going (or the last went), and whether one is running. */
  private soloLive: TuneStat | undefined;
  private soloRunning = false;
  /** A list stroke landed while it ran: run it again when that run has stopped. */
  private tuneAgain = false;
  /** The list the lasso puts its points in (add) or takes them out of (remove), instead of selecting them; null: it selects. */
  private tuneArm: { l: 'req' | 'deny'; act: 'add' | 'remove' } | null = null;
  /** A macro is running: its selection changes are one undo step. */
  private batching = false;
  /** The instance viewer: the type shown, instances switched off, and the camera to go back to. */
  private viewing: { type: string; off: Set<string>; masterOnly?: boolean; density?: boolean; thresh?: number; below?: boolean; cam: { eye: V3; yaw: number; pitch: number; pc: [number, number]; ps: number } | null } | null = null;
  private lastType = '';
  /** The last frame fit's summary, shown in the instance viewer. */
  private viewNote = '';
  /** The overlay's points: which scan (index into scans) and vertex each one is, and its word before selection bits; where each is drawn, and its instance. */
  private ov: { scans: Scan[]; si: Uint16Array; v: Uint32Array; base: Uint32Array; xyz: Float32Array; ik: Int32Array } | null = null;
  /** The one-off shape being set up: its kind and the settings per kind; shapes not drawn ('<scan>|<name>'). */
  private shKind: ShapeKind = 'pole';
  private shParams: Record<ShapeKind, Record<string, string | number>> = {
    pole: Object.fromEntries(SHAPE_PARAMS.pole.map(q => [q.key, q.def])), surface: Object.fromEntries(SHAPE_PARAMS.surface.map(q => [q.key, q.def])),
  };
  private shHidden = new Set<string>();
  /** Named points switched off in the list. */
  private ptHidden = new Set<string>();
  /** The named points drawn now (as drawPoints left them): what a click in the view can hit. */
  private ptHits: { name: string; sc: Scan; f: number; q: V3 }[] = [];
  /** A named point's menu, open over the view: its element, the point it follows, its tick. */
  private ptMenu: { box: HTMLElement; hit: { name: string; sc: Scan; f: number; q: V3 }; raf: number; off: (e: PointerEvent) => void } | null = null;
  /** The view's menu (right-click / long press off a named point): its element and its click-away listener. */
  private viewMenu: { box: HTMLElement; off: (e: PointerEvent) => void } | null = null;
  /** The instances' boxes as drawn. */
  private boxHits: BoxHit[] = [];
  /** Instances (by name) drawn as their type's voxel model, their own points hidden; and what they were drawn from. */
  private voxed = new Map<string, { M: Rigid; type: string; hue: number }>();
  private voxedKey = '';
  /** An instance box's menu, open over the view: its element, the box it follows, its tick. */
  private boxMenu: { box: HTMLElement; hit: BoxHit; raf: number; off: (e: PointerEvent) => void } | null = null;
  /** The frames' cameras drawn now (Show cameras): what a click in the view can hit. */
  private camHits: CamHit[] = [];
  /** A camera's menu, open over the view: its element, the camera it follows, its tick. */
  private camMenu: { box: HTMLElement; hit: CamHit; raf: number; off: (e: PointerEvent) => void } | null = null;
  /** Camera images shown ('<scan>:<frame>'), each Viewer image 'eimg:<scan>:<frame>'; a load in progress is in loading. */
  /** What the photo steps found (kept after the macro: a camera's menu draws it on its photo), and whether to draw it. */
  private photo = new PhotoRun();
  private photoMarks = true;
  /** The photogrammetry worker (matching, 3D edges, camera alignment), started on first use. */
  private pg: PG | null = null;
  /** Mesh export steps' parts kept for the next one's file, and the regions they own. */
  private meshKept: { part: MeshPart; region: RegionSpec }[] = [];
  private shownImgs = new Set<string>();
  private loadingImgs = new Set<string>();
  /** The name last given or added to (Name point, the view's menu): the view's menu offers to add to it. */
  private ptLast: string | null = null;

  constructor(private v: Viewer, private ctl: Controls, private base: string, private $: (id: string) => HTMLElement,
    private volSelected: () => boolean = () => false, private scene: string | null = null) {
    this.api = scene ? new EditApi(`api/scenes/${scene}/`) : new EditApi();
    this.cons = new ConsUI(this.consHost(), $);
  }

  /** The Construction tab (consui.ts). */
  readonly cons: ConsUI;

  /** What the Construction tab works with: the loaded scans shown, and the editor's own moves, menus and commits. */
  private consHost(): import('./consui.ts').ConsHost {
    const shown = () => [...this.scans.values()].filter(sc => !this.off(sc));
    return {
      v: this.v,
      scans: () => shown().map(sc => ({ id: sc.s.scan, name: this.v.clouds.find(c => c.meta.id === sc.s.scan)?.meta.name ?? sc.s.scan, state: sc.s.state, stale: sc.s.graph.stale })),
      place: (scan, f) => { const sc = this.scans.get(scan); return sc ? this.framePlace(sc, f) : identity(); },
      commit: (label, bodies) => this.commitOut(label, bodies),
      idle: () => this.idle(),
      waits: () => this.editWaits(),
      say: (m, w) => this.say(m, w),
      gizmo: (c, title, h, opts) => {
        this.gizmo?.cancel();
        this.gizmo = new Gizmo(this.v, c, title, {
          change: h.change,
          apply: (W, made) => { this.gizmo = null; h.apply(W, made); },
          cancel: () => { this.gizmo = null; h.cancel(); },
        }, opts);
      },
      moved: name => this.pubMoves().get(name) ?? identity(),
      moveInstance: name => this.moveObject(name),
      fitShape: (kind, name, p, row) => this.fitOneOff(kind, name, { ...p }, row),
      selection: cap => { const r = this.selInFrame(cap); return r && { scan: r.own.sc.s.scan, frame: r.frame, xyz: r.X, n: r.n }; },
      screen: p => this.screen(p),
      boxes: () => this.boxHits,
      info: () => this.objectInfo(),
      typeBox: type => {
        const b = ovl.typeBoxes(shown().map(sc => sc.s)).get(type);
        return b ? { box: b.box, scans: [...this.scans.values()].filter(sc => [...sc.s.state.objects.values()].some(o => o.of === type)).map(sc => sc.s.scan) } : null;
      },
      points: () => new Map([...this.namedPoints()].map(([n, ms]) => [n, ms.map(m => ({ scan: m.sc.s.scan, f: m.f, mark: m.mark, nw: m.nw, q: m.q }))])),
      movePoint: (name, scan, f) => { const sc = this.scans.get(scan); if (sc) this.movePoint(name, sc, f); },
      dropPoint: name => this.unmark(name, (this.namedPoints().get(name) ?? []).map(m => ({ sc: m.sc, f: m.f }))),
      voxTypes: () => [...ovl.typeVoxels(shown().map(sc => sc.s)).keys()].sort(),
      openType: type => this.openObjects(type),
      colour: k => (k === 'plane' ? PLANE_RGBA : SHAPE_RGBA[k]),
      showAll: () => {
        const was = CONS_TOGGLES.map(id => this.inp(id).checked), sh = new Set(this.shHidden), pt = new Set(this.ptHidden);
        for (const id of CONS_TOGGLES) this.inp(id).checked = true;
        this.shHidden.clear(); this.ptHidden.clear();
        return () => {
          CONS_TOGGLES.forEach((id, i) => { this.inp(id).checked = was[i]; });
          this.shHidden = sh; this.ptHidden = pt;
        };
      },
      redraw: () => this.refresh(),
    };
  }

  private inp = (id: string) => this.$(id) as HTMLInputElement;

  /** If the editor server answers: show the tools and load every ticked scan. */
  async init() {
    const info = await this.api.info();
    if (!info) return;
    for (const s of info.scans) this.editable.add(s.id);
    if (!this.v.clouds.some(c => this.editable.has(c.meta.id))) return;
    this.on = true;
    this.onEditor?.();   // the editor's tabs into the layout
    this.$('ecount').hidden = false;   // the selection count, in the status bar
    this.$('selonlyl').hidden = false;
    this.$('vsel').hidden = false;   // front only, bottom right of the View
    this.$('dedit').hidden = false;
    this.$('eobjsec').hidden = false;   // the Objects tab's editor list   // the Display tab's editor options (tint classes, show cameras)
    // Point density shading: measured here, on the scans loaded for editing
    // and Image features: found here, in the photos of the scans loaded for editing
    // and Image weights: the scans' photos, blended by their points' weights
    for (const o of document.querySelectorAll<HTMLElement>('#shade option[value="8"], #shadetop button[data-m="8"], #shade option[value="9"], #shadetop button[data-m="9"], #shade option[value="10"], #shadetop button[data-m="10"], #shade option[value="11"], #shadetop button[data-m="11"]')) o.hidden = false;
    this.$('elive').hidden = !info.deploy;
    this.$('asave').hidden = false;   // the Align tool can save into the scene here
    editorFeedback(() => this.feedbackFields());
    // the scene: in the top bar, a picker of every scene, this one and its object types; here, adding scans to it
    if (this.scene) {
      pagePicker(this.$('title'), { scene: this.scene, title: info.title || this.scene, type: null,
        types: () => this.typeNames().map(t => [t, this.instancesOf(t).length]), canLeave: () => this.canLeave() });
      const box = this.$('escene');
      box.textContent = '';
      const t = document.createElement('b');
      t.textContent = info.title || this.scene;
      box.append(t, ' ', new Uploader(this.scene, () => { this.say('a new scan is in: reload to see it', true); this.$('ereload').hidden = false; }).box);
      box.hidden = false;
    }
    this.wire();
    this.fillStatic();
    this.refresh();
    this.sync();
    // edits from another page (the object editor): follow them
    onHeadMoved(m => {
      const sc = this.scans.get(m.scan);
      if (m.root !== this.api.root || !sc || sc.s.head === m.head || this.busy) return;
      sc.s.reload().then(() => { sc.hide = null; this.say(`${m.scan} was edited in another page: reloaded`); this.refresh(); }, () => {});
    });
  }

  /** What the feedback button sends unseen (feedback.ts): the state a report was written in. */
  private feedbackFields(): Fields {
    const c = this.v.cam;
    const scans = [...this.scans.entries()];
    return {
      scene: this.scene, suspended: this.suspended, busy: this.busy,
      scans: scans.map(([id, sc]) => `${id}@${sc.s.head ?? '-'}${sc.s.graph.stale ? ' stale' : ''}`).join(', ') || null,
      shown: this.v.clouds.filter(c => c.visible).map(c => c.meta.id).join(', ') || null,
      selected: scans.reduce((n, [, sc]) => n + (sc.mask ? sel.count(sc.mask) : 0), 0),
      tool: this.tool, combine: this.mode, rule: this.rule ? JSON.stringify(this.rule).slice(0, 300) : null,
      viewing: this.viewing?.type ?? null, hidden: [...this.hidden].join(', ') || null,
      undo: this.past.length, redo: this.future.length,
      camera: `${c.mode} eye ${c.eye.map(x => x.toFixed(2)).join(',')} yaw ${c.yaw.toFixed(2)} pitch ${c.pitch.toFixed(2)}`,
    };
  }

  /** Ticked scans loaded for editing so far, of how many; null when none are still to load. */
  get loadProgress(): [number, number] | null {
    if (!this.on || this.suspended || this.loadHeld) return null;
    const want = this.v.clouds.filter(c => c.visible && this.editable.has(c.meta.id));
    const done = want.filter(c => this.scans.has(c.meta.id)).length;
    return done < want.length ? [done, want.length] : null;
  }

  /** Called when this page frees its memory for the object editor (main.ts stops the background copy). */
  onSuspend: () => void = () => {};
  /** Point density: a measurement is shown (its range is the viewer's densRange). */
  onDensity: () => void = () => {};
  /** Once the editor answers: its tabs into the layout. */
  onEditor?: () => void;
  /** Bring a tab of the layout to the front (History, Macro editor), or its window if it is in one. */
  showTab: (id: string) => void = () => {};
  /** Open a tab's panel in a movable window, titled so (the Macro editor, from a saved macro's ✎). */
  popOut: (id: string, title: string) => void = () => {};
  private suspended = false;

  /** Open the object editor on a type: this page, in its object mode (?scene=…&type=…). */
  private openObjects(type: string) {
    if (this.canLeave()) location.href = pageUrl(this.scene ?? 'church', type);
  }

  /** Whether this page may be left now: not mid-way through writing edits, unless the user says so. */
  private canLeave() {
    return !this.busy || confirm('edits are still being written: leave anyway?');
  }

  /** Drop the loaded scans (their points, GPU buffers and selection); edits made elsewhere are seen on resume. */
  suspend() {
    this.closeView();
    this.v.clearEditCloud();
    this.v.removeLines('epath:');
    this.v.removeLines('elock:');
    this.v.removeLines('ecam:');
    this.v.removeImages('eimg:');
    this.dropWeightImages();
    this.camHits = [];
    this.shownImgs.clear(); this.loadingImgs.clear();
    this.closeCamMenu();
    this.v.removeLines('eshape:');
    this.v.removeLines('ept:');
    this.ptHits = [];
    this.closePtMenu();
    this.closeViewMenu();
    this.scans.clear();
    this.voxed.clear(); this.voxedKey = '';   // (their clouds went with the rest)
    this.past = []; this.future = [];
    this.suspended = true;
    this.loadProc?.end(); this.loadProc = null;
    this.$('eresume').hidden = false;
    this.$('eresume').onclick = () => this.resume();
    this.onSuspend();
    this.say('this page\'s scans are put away (Resume editing loads them again)');
    this.refresh();
  }

  resume() {
    this.suspended = false;
    this.loadHeld = false;
    this.$('eresume').hidden = true;
    this.sync();
  }

  /** Loading scans for editing, in the status bar; and whether it was stopped (Resume editing goes on). */
  private loadProc: Proc | null = null;
  private loadHeld = false;

  /** Stop loading scans for editing: the one loading now is dropped, those loaded stay. */
  private holdLoading() {
    this.loadHeld = true;
    this.loadProc?.end(); this.loadProc = null;
    this.$('eresume').hidden = false;
    this.$('eresume').onclick = () => this.resume();
    this.say('loading scans stopped: Resume editing loads the rest', true);
    this.refresh();
  }

  /** Load ticked scans; unticked ones stay loaded but hidden (call when cloud visibility changes). */
  sync() {
    if (!this.on || this.suspended) return;
    let changed = false;
    for (const c of this.v.clouds) {
      const sc = this.scans.get(c.meta.id), e = this.v.edits.get(c.meta.id);
      if (!sc || !e || e.visible === c.visible) continue;
      e.visible = c.visible;
      // a hidden scan's selection is set aside (nothing edits what can't be seen) and comes back when it's shown
      if (!c.visible) { sc.parked = sc.mask; sc.mask = sc.cand = null; } else { sc.mask = sc.parked ?? null; sc.parked = null; }
      changed = true;
    }
    if (changed) this.refresh();
    if (this.loading || this.loadHeld) return;
    const next = this.v.clouds.find(c => c.visible && this.editable.has(c.meta.id) && !this.scans.has(c.meta.id));
    if (!next) { this.loadProc?.end(); this.loadProc = null; return; }
    this.loading = next.meta.id;
    this.say(`loading ${next.meta.name} for editing…`);
    this.loadProc ??= proc('loading scans for editing', { cancel: () => this.holdLoading() });
    const [done, of] = this.loadProgress ?? [0, 1];
    this.loadProc.set(`${next.meta.name} (${done + 1} of ${of})`, done / of);
    EditSession.open(this.api, this.base, next.meta, next.T).then(s => {
      if (!this.loadHeld) {   // unticked meanwhile: kept, hidden (sync below)
        this.scans.set(s.scan, { s, mask: null, cand: null, hide: null, grid: null });
        this.v.setEditCloud(next.meta, s.idx, s.recs, next.T, s.frameIx, s.frameMats);
      }
    }).catch(e => { this.say(`${next.meta.name}: ${(e as Error).message}`, true); this.editable.delete(next.meta.id); })
      .finally(() => { this.loading = null; this.fillStatic(); this.refresh(); this.sync(); });
  }

  private wire() {
    const $ = this.$, on = (id: string, f: () => unknown) => { $(id).onclick = () => { f(); }; };
    on('epublish', () => this.run('publish', () => this.publish()));
    on('elive', () => this.run('go live', () => this.goLive()));
    $('emode').addEventListener('click', e => {
      const m = (e.target as HTMLElement).dataset.m as sel.Combine | undefined;
      if (m) { this.mode = m; this.syncMode(); }
    });
    on('eclear', () => this.clearSel());
    on('einvert', () => this.invert());
    on('eerode', () => this.morph('erode'));
    on('edilate', () => this.morph('dilate'));
    // Data group shading: which group, the ramp's ends (auto: its least to most, or 2nd to 98th percentile), and its band selected
    (this.$('dataname') as HTMLSelectElement).onchange = () => { this.dataShow.name = (this.$('dataname') as HTMLSelectElement).value; this.dataShown(); };
    const ends = () => { (this.$('dataauto') as HTMLSelectElement).value = ''; this.dataShown(); };
    this.inp('datalo').onchange = ends; this.inp('datahi').onchange = ends;
    (this.$('dataauto') as HTMLSelectElement).onchange = () => this.dataShown();
    const cut = (hi: boolean) => {   // Min and Max in the View: the other pushed along so Min stays at or under Max
      const lo = +this.inp('datacutlo').value, h = +this.inp('datacuthi').value;
      if (lo > h) this.inp(hi ? 'datacutlo' : 'datacuthi').value = String(hi ? h : lo);
      this.dataShown();
    };
    this.inp('datacutlo').oninput = () => cut(false); this.inp('datacuthi').oninput = () => cut(true);
    // the Data view's quick eval, c = a * b: Enter runs it, Up / Down the ones run before, Escape leaves it
    this.inp('dataeval').title = 'make or replace a data group from others, point by point, then Enter: c = a * b + 1. Numbers, data group names ([in brackets] with spaces), + - * / %, brackets\n\n'
      + 'Its points: those of the groups it reads (each its default where a point isn\'t in it); reading none, the group\'s own points, or the selection\n\n'
      + 'c += a (-= *= /= %=): changes c\'s own points only, as Data math does (one that comes to no number keeps its value)\n\n'
      + 'Known values too (where no data group has the name):\n' + Object.entries(MEASURES).map(([n, m]) => `  ${n}: ${m.what}`).join('\n')
      + '\n\nUp / Down: the ones run before';
    this.inp('dataeval').addEventListener('keydown', e => {
      const i = this.inp('dataeval'), h = this.dataEvals;
      if (e.key === 'Enter') { e.preventDefault(); this.dataEval(i.value); }
      else if (e.key === 'Escape') i.blur();
      else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && h.length) {
        e.preventDefault();
        this.dataEvalAt = Math.max(0, Math.min(h.length, (this.dataEvalAt < 0 ? h.length : this.dataEvalAt) + (e.key === 'ArrowUp' ? -1 : 1)));
        i.value = h[this.dataEvalAt] ?? '';
      } else return;
      e.stopPropagation();
    });
    on('databand', () => { const p = this.toolbarParams('dataBand'); if (!this.dataShow.name) this.say('no data group to select from', true); else this.runRule('dataBand', p); });
    on('ewidenf', () => this.widen('frames'));
    on('ewidens', () => this.widen('scan'));
    on('ealignf', () => this.alignFrames());
    on('elock', () => this.lockFrames(true));
    on('eunlock', () => this.lockFrames(false));
    on('eptset', () => this.namePoint());
    on('eptsolve', () => this.solvePoints());
    this.inp('eptshow').onchange = () => this.drawPoints();
    this.inp('eboxshow').onchange = () => this.drawBoxes();
    this.inp('eshowcams').onchange = () => {
      if (!this.inp('eshowcams').checked) { this.hideImages(); this.closeCamMenu(); }
      this.refresh();
    };
    this.inp('evoxshow').onchange = () => this.refresh();
    $('etb').addEventListener('click', e => {   // the Tools tab's toolbar
      const t = (e.target as HTMLElement).dataset.t as Entry | undefined;
      if (t) this.pick(this.entry === t ? null : t);
    });
    on('eggo', () => this.regrow());
    on('evolgo', () => this.applyVolume());
    on('engo', () => this.applyNormal());
    on('ebfit', () => this.fitPlane());
    on('ebgo', () => this.applyBand());
    on('edgo', () => this.applyDistance());
    on('eego', () => this.applyEdges());
    on('evgo', () => this.applyView());
    on('ecgo', () => this.applyColour());
    on('edengo', () => this.applyDensity());
    on('ecpick', () => this.pickColour());
    for (const id of ['ech0', 'ech1', 'ecs0', 'ecs1', 'ecv0', 'ecv1']) this.$(id).addEventListener('input', () => this.drawSwatch());
    this.drawSwatch();
    on('efgo', () => this.applyFrames());
    on('efpgo', () => this.applyFewPoints());
    on('esrcgo', () => this.applySourceKind((this.$('esrc') as HTMLSelectElement).value));
    this.wireMacros();
    on('edel', () => this.act('delete', () => ({ op: 'delete' }) as OpBody));
    on('erestore', () => this.act('restore', () => ({ op: 'restore' }) as OpBody, { keepDeleted: true }));
    on('eclassnew', () => this.newClass());
    on('edissolve', () => this.dissolve());
    on('eobj', () => this.newObject());
    on('eobjadd', () => this.addToObject((this.$('eobjsel') as HTMLSelectElement).value));
    on('eobjinst', () => this.newInstance());
    on('eobjrel', () => this.act('release from objects', () => ({ op: 'object.release' }) as OpBody));
    on('esplit', () => this.split());
    on('egroup', () => this.saveGroup());
    $('eshkind').addEventListener('click', e => {
      const k = (e.target as HTMLElement).dataset.k as ShapeKind | undefined;
      if (k && k !== this.shKind) { this.shKind = k; this.shapeForm(); }
    });
    on('eshfit', () => this.fitOneOff());
    for (const id of ['eshshow', 'eshtop']) this.inp(id).onchange = () => this.drawShapes();
    this.shapeForm();
    on('eundo', () => this.run('undo', () => this.undoStep()));
    on('eredo', () => this.run('redo', () => this.redoStep()));
    on('eref', () => this.checkpoint());
    on('ehundo', () => this.run('undo', () => this.undoStep()));
    on('ehredo', () => this.run('redo', () => this.redoStep()));
    on('ehistopen', () => this.showTab('history'));
    const show = () => {
      // deleted points are drawn (tinted) unless their layer is off: hiding is per point (bit 7)
      this.v.s.editShow = 1 | (this.inp('eshowcls').checked ? 2 : 0) | (this.inp('eshowobj').checked ? 4 : 0);
      this.v.dirty = true;
    };
    for (const id of ['eshowcls', 'eshowobj']) this.inp(id).onchange = show;
    // the Display tab's copies of the show toggles, each following its own tab's (and set ticked by code: syncDisplay)
    for (const [d, e] of DISPLAY_MIRRORS) {
      this.inp(d).onchange = () => { this.inp(e).checked = this.inp(d).checked; this.inp(e).dispatchEvent(new Event('change')); };
      this.inp(e).addEventListener('change', () => { this.inp(d).checked = this.inp(e).checked; });
    }
    this.inp('dpath').onchange = () => { this.showPath = this.inp('dpath').checked; this.refresh(); };
    show();
    addEventListener('keydown', e => {
      if ((e.target as HTMLElement).closest?.('input,select,textarea')) return;
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') {
        e.preventDefault();
        this.run(e.shiftKey ? 'redo' : 'undo', () => (e.shiftKey ? this.redoStep() : this.undoStep()));
      }
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.code === 'KeyA') { e.preventDefault(); this.selectAll(); return; }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.code === 'Escape' && (this.ptMenu || this.viewMenu || this.boxMenu || this.camMenu)) { this.closePtMenu(); this.closeViewMenu(); this.closeBoxMenu(); this.closeCamMenu(); return; }
      if (e.code === 'Escape' && this.macroStop) { this.macroStop(); return; }
      if (e.code === 'Escape' && this.tuneArm) { this.armTune(null); return; }
      if (e.code === 'Escape') { if (this.entry) this.pick(null); else if (this.hasSel()) this.clearSel(); else if (this.viewing) this.closeView(); }
      const key: Record<string, Entry> = { KeyB: 'brush', KeyG: 'seed' };
      if (key[e.code] && this.v.cam.mode !== 'walk') this.pick(this.entry === key[e.code] ? null : key[e.code]);
      if (e.code === 'KeyF' && !e.shiftKey && this.hasSel()) this.widen('frames');
      if ((e.code === 'Delete' || e.code === 'Backspace') && this.hasSel() && !this.volSelected()) {
        e.preventDefault();
        this.act('delete', () => ({ op: 'delete' }) as OpBody);
      }
    });
    this.ctl.capture = (e, phase) => this.pointer(e, phase);
    this.ctl.dragStrokes = () => !!this.tuneArm && this.tool === 'lasso';   // an armed Auto tune list: a left drag lassos
    // over a named point's cross: it can be clicked
    let hov = false;
    this.v.canvas.addEventListener('pointermove', e => {
      const on = !e.buttons && ((!!this.ptHits.length && !!this.ptAt(e.offsetX, e.offsetY)) || (!!this.boxHits.length && !!this.boxAt(e.offsetX, e.offsetY))
        || (!!this.camHits.length && !!this.camAt(e.offsetX, e.offsetY)));
      if (on !== hov) { hov = on; this.v.canvas.classList.toggle('ptover', on); }
    });
  }

  async publish() {
    this.say('publishing (prep/build_tiles.py)…');
    const head = proc('publishing', { cancel: () => { void this.api.cancelBuild(); } });
    head.set('rebuilding the clouds from the edits (prep/build_tiles.py)');
    const r = await this.api.build().finally(() => head.end());
    if (r.cancelled) { this.say('publish cancelled: the published clouds are as they were', true); return; }   // kept (as a warning) past the refresh
    if (!r.ok) throw new Error('build failed:\n' + r.log.slice(-1500));
    location.reload();
  }

  /** Put the published data on the public site (the server runs deploy-cloudview). */
  async goLive() {
    if (!confirm('Put the published clouds on the public site now?')) return;
    const log = this.$('elog');
    log.textContent = 'deploying… (a minute or two if the code changed too)';
    const head = proc('going live', { cantStop: 'a deploy swaps the live site over: it runs to the end (or rolls back)' });
    head.set('deploy-cloudview');
    const r: { ok?: boolean; log?: string } = await this.api.deploy().catch(e => ({ ok: false, log: (e as { body?: { log?: string } }).body?.log ?? String(e) }))
      .finally(() => head.end());
    log.textContent = (r.ok ? 'live.\n' : 'deploy FAILED:\n') + (r.log ?? '').trim().split('\n').slice(-6).join('\n');
  }

  /** Run an async action with errors shown in the panel. */
  private async run(what: string, f: () => Promise<unknown>) {
    if (this.busy) return;
    this.busy = true;
    try { await f(); } catch (e) { this.say(`${what}: ${(e as Error).message}`, true); console.warn(e); return; } finally { this.busy = false; }
    this.refresh();
  }

  private say(msg: string, warn = false) {
    const i = this.$('einfo');   // one line in the status bar; the whole message in its tooltip
    i.textContent = msg.replace(/\n/g, ' · ');
    i.title = msg;
    i.classList.toggle('warn', warn);
    this.sayWarn = warn;
  }
  private sayWarn = false;

  // ── selection ─────────────────────────────────────────────────────────
  /** Keep each scan's world copy in step with its alignment. */
  private placed(sc: Scan) {
    const c = this.v.edits.get(sc.s.scan);
    if (c && c.T !== sc.s.T) { sc.s.setTransform(c.T); sc.grid = null; }
    return sc.s;
  }

  /** A scan's memory groups (none: empty). */
  private memOf(scan: string): MemGroups { return this.mem.get(scan) ?? new Map(); }
  /** Per scan, its data groups (datagroups.ts): like memory groups, this page's until it is closed. */
  private data = new Map<string, DataGroups>();
  private dataOf(scan: string): DataGroups { return this.data.get(scan) ?? new Map(); }
  /** Every data group name any scan being edited has. */
  private dataNames() { return [...new Set([...this.scans.values()].flatMap(sc => [...this.dataOf(sc.s.scan).keys()]))].sort(); }

  /** A group's points in a scan: its memory group, else the saved group and the groups inside it. */
  private groupMask(s: EditSession, name: string): Uint8Array { return this.memOf(s.scan).get(name)?.slice() ?? s.groupDeep(name); }

  /** Whether any loaded scan has a group by this name, saved or in memory. */
  private hasGroup(name: string) { return [...this.scans.values()].some(x => x.s.state.groups.has(name) || this.memOf(x.s.scan).has(name)); }

  /** Vertices of a scan whose layers are all on. */
  private live(sc: Scan): Uint8Array {
    const h = this.hiddenOf(sc), m = new Uint8Array(h.length);
    for (let i = 0; i < m.length; i++) m[i] = h[i] ^ 1;
    if (this.viewing && sc.view) for (let i = 0; i < m.length; i++) m[i] &= sc.view.in[i];   // only what the instance viewer shows
    return m;
  }

  /** The scan as the selection tools see it: in the instance viewer, its points where the overlay draws them. */
  private selSpace(sc: Scan): EditSession {
    const s = this.placed(sc);
    if (!this.viewing || !sc.view) return s;
    const o = Object.create(s) as EditSession;
    o.P = sc.view.P; o.N = sc.view.N;
    return o;
  }

  /** Per vertex: 1 if a layer it belongs to is switched off. */
  private hiddenOf(sc: Scan): Uint8Array {
    if (sc.hide) return sc.hide;
    const s = sc.s, H = this.hidden, st = s.state, m = new Uint8Array(s.nv);
    if (this.v.edits.get(s.scan)?.visible === false) return (sc.hide = m.fill(1));   // unticked in Scans: loaded, not shown
    const del = H.has('deleted'), clsOff = new Uint8Array(MAX_CLASS + 1);
    for (let k = 0; k < CLS.length; k++) clsOff[k] = H.has(`cls:${k}`) ? 1 : 0;
    for (const [id, c] of st.classes) clsOff[id] = H.has(`clsn:${c.name}`) ? 1 : 0;
    // a soloed class: only its points show (a scan without it shows none)
    let clsOut: Uint8Array | null = null;
    if (this.soloClass !== null) {
      clsOut = new Uint8Array(MAX_CLASS + 1).fill(1);
      const k = CLS.indexOf((this.soloClass === 'unclassified' ? 'none' : this.soloClass) as Cls);
      if (k >= 0) clsOut[k] = 0;
      for (const [id, c] of st.classes) if (c.name === this.soloClass) clsOut[id] = 0;
    }
    // an instance drawn as its type's voxels (drawVoxels) gives way too, and an object being moved (its preview moves instead)
    const obj = new Set([...st.objects].filter(([, o]) => H.has(`obj:${o.name}`) || (o.of !== undefined && H.has(`type:${o.of}`)) || this.voxed.has(o.name) || o.name === this.movingObj).map(([id]) => id));
    const part = new Set([...st.parts].filter(([, p]) => H.has(`part:${p.name}`)).map(([id]) => id));
    for (let v = 0; v < s.nv; v++) {
      const p = s.pidOfV[v], f = st.flags[p];
      // a movable object's points follow its layer, not their class
      m[v] = (del && (f & DELETED)) || (st.obj[p] ? obj.has(st.obj[p]) : clsOff[f >> 1]) || part.has(st.part[p]) || (clsOut && clsOut[f >> 1]) ? 1 : 0;
    }
    for (const k of H) if (k.startsWith('group:')) { const g = this.groupMask(s, k.slice(6)); for (let v = 0; v < m.length; v++) m[v] |= g[v]; }
    for (const k of H) if (k.startsWith('data:')) { const g = this.dataOf(s.scan).get(k.slice(5))?.v; if (g) for (let v = 0; v < m.length; v++) m[v] |= g[v] === g[v] ? 1 : 0; }
    // a soloed group (while some scan still has it): only its points show; a scan without it shows none
    const solo = this.soloGroup;
    if (solo !== null && this.hasGroup(solo)) { const g = this.groupMask(s, solo); for (let v = 0; v < m.length; v++) m[v] |= g[v] ^ 1; }
    const sf = this.soloFrame;
    if (sf) { if (sf.scan !== s.scan || !s.frame) m.fill(1); else for (let v = 0; v < m.length; v++) m[v] |= +(s.frame[v] !== sf.f); }
    // alignment-tree nodes switched off: 'tree:<scan>|<node key>'
    const off = [...H].filter(k => k.startsWith(`tree:${s.scan}|`)).map(k => this.parseNode(k.slice(k.indexOf('|') + 1))).filter(n => n !== null);
    if (off.length) {
      const fh = s.frameIds.map(f => off.some(n => under(st.tree, n!, f)) ? 1 : 0);
      for (let v = 0; v < m.length; v++) m[v] |= fh[s.frameIx[v]];
    }
    return (sc.hide = m);
  }

  private toggleLayer(k: string) {
    if (this.hidden.has(k)) this.hidden.delete(k); else this.hidden.add(k);
    this.refresh();
  }

  /**
   * Dilate: add every shown point within r of a selected one; erode: drop every selected point within r of a shown
   * unselected one. Across the ticked scans (they overlap); unticked ones keep their parked selection.
   */
  private morph(kind: 'erode' | 'dilate', r = +this.inp('emorphr').value) { this.runRule(kind, { r }); }

  /** Widen the selection to the whole of each frame (or scan) it touches; hidden points stay out. */
  private widen(to: 'frames' | 'scan') { this.runRule(to === 'scan' ? 'widenScan' : 'widenFrames', {}); }

  /**
   * Align frames: widen the selection to its frames, then fit each of those frames onto everything else that
   * is shown (frameops.ts), on the worker pool with progress under the selection row. One action at the end:
   * a tree.transform per scan with the frames that moved.
   */
  alignFrames() {
    if (this.busy || this.job) { if (this.job) this.say(`${this.job.title} is running: wait for it (or Cancel it)`, true); return; }
    if (!this.hasSel()) { this.say('select some points of the frames to align first'); return; }
    this.widen('frames');
    const scans = [...this.scans.values()].filter(sc => sc.mask && sel.count(sc.mask) && sc.s.frame && !sc.s.graph.stale)
      .map(sc => ({ s: this.placed(sc), live: this.live(sc), frames: this.selectedFrames(sc) }));
    const nf = scans.reduce((a, x) => a + x.frames.size, 0);
    if (!nf) { this.say('none of the selected points have frame ids', true); return; }
    // the target is everything shown: the scans with no selection too
    for (const sc of this.scans.values()) if (!scans.some(x => x.s === sc.s)) scans.push({ s: this.placed(sc), live: this.live(sc), frames: new Set() });
    const reach = Math.max(0, +(this.$('ereach') as HTMLInputElement).value || 0);
    const row = this.$('ewidenf').parentElement!;
    const job = this.startJob(`aligning ${nf} frame${nf === 1 ? '' : 's'}`, row.parentElement!, row.nextElementSibling);
    alignFramesRun({ scans, exec: job.exec, K: job.K, progress: job.set, reach }).then(r => {
      job.end();
      if (r.bodies.size) this.previewFrames(`align ${nf} frame${nf === 1 ? '' : 's'}`, r); else { this.say(r.note); this.refresh(); }
    }, (e: Error) => job.fail(e));
  }

  // ── one-off shapes: a pole or a surface fitted to the selection ───────
  /** The loaded scans' one-off shapes (of ''), by name, with the scan that holds each. */
  private oneOffs() {
    const out = new Map<string, { sc: Scan; shape: ShapeDoc }>();
    for (const sc of this.scans.values()) for (const x of sc.s.state.shapes.values()) if (x.of === '') out.set(x.name, { sc, shape: x.shape });
    return new Map([...out].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true })));
  }

  /** Where a one-off shape is drawn: T (as the cloud is drawn now) · E(its frame), or that frame as an alignment preview has it. */
  private shapePlace(sc: Scan, shape: ShapeDoc): Rigid { return this.framePlace(sc, +shape.ref.slice(2) || 0); }

  /** Where a frame is drawn: T (as the cloud is drawn now) · E(f), or E(f) as an alignment preview has it. */
  private framePlace(sc: Scan, f: number): Rigid {
    const s = sc.s;
    return compose(this.v.edits.get(s.scan)?.T ?? s.T, this.framePreview?.get(s.scan)?.E.get(f) ?? frameTransform(s.state.tree, f));
  }

  /** The one-off shapes as wireframes, each with its scan. */
  private drawShapes() {
    this.v.removeLines('eshape:');
    this.cons.draw();   // the planes too, and what the Construction tab has picked
    if (!this.inp('eshshow').checked) return;
    for (const [name, { sc, shape }] of this.oneOffs()) {
      const key = `${sc.s.scan}|${name}`;
      if (this.shHidden.has(key)) continue;
      this.v.setLines(`eshape:${key}`, shapeLines(shape, this.shapePlace(sc, shape)), SHAPE_RGBA[shape.kind], true, !this.inp('eshtop').checked, sc.s.scan);
    }
  }

  /** The settings for the kind chosen (kept per kind while the page is open); a free name for it, unless keepName or one is typed. */
  private shapeForm(keepName = false) {
    for (const b of this.$('eshkind').querySelectorAll<HTMLElement>('button')) b.classList.toggle('on', b.dataset.k === this.shKind);
    this.$('eshparams').replaceChildren(...shapeParamFields(this.shKind, this.shParams[this.shKind]));
    const nm = this.inp('eshname'), names = this.oneOffs(), auto = /^(pole|surface) \d+$/.exec(nm.value);
    if (!keepName && (!nm.value || (auto && (auto[1] !== this.shKind || names.has(nm.value))))) {
      let k = 1;
      while (names.has(`${this.shKind} ${k}`)) k++;
      nm.value = `${this.shKind} ${k}`;
    }
  }

  /** The one-off shapes list: show / hide, what each came to, fit again to the selection, drop. */
  private shapeList() {
    const box = this.$('eshapes'), all = this.oneOffs(), cm = (v: number) => (100 * v).toFixed(1);
    box.textContent = '';
    for (const [name, { sc, shape: sh }] of all) {
      const key = `${sc.s.scan}|${name}`, hid = this.shHidden.has(key), r = el('div', { class: 'layer' + (hid ? ' off' : '') }), pcs = sh.pieces;
      const eye = el('button', { class: 'eye', title: hid ? 'show' : 'hide' }, hid ? '◌' : '●'), c = SHAPE_RGBA[sh.kind];
      eye.style.color = `rgb(${c.slice(0, 3).map(v => Math.round(255 * v)).join(' ')})`;
      eye.onclick = () => { if (hid) this.shHidden.delete(key); else this.shHidden.add(key); this.drawShapes(); this.shapeList(); };
      const size = sh.kind === 'pole' ? `r ${cm(Math.max(...pcs.flatMap(q => (q as { r: number[] }).r)))}` : `t ${cm(Math.max(...pcs.map(q => (q as { t: number }).t)))}`;
      const scanName = this.v.clouds.find(x => x.meta.id === sc.s.scan)?.meta.name ?? sc.s.scan, f = sh.ref.slice(2);
      const btns = el('span', { class: 'row' }), b = (t: string, tip: string, go: () => void) => { const e = el('button', { class: 'small', title: tip }, t); e.onclick = go; return e; };
      btns.append(
        b('Re-fit', 'fit it again, with its settings (shown above), to the selected points', () => {
          this.shKind = sh.kind;
          Object.assign(this.shParams[sh.kind], sh.p);
          this.inp('eshname').value = name;
          this.shapeForm(true);
          this.fitOneOff();
        }),
        b('✕', 'drop the shape', () => this.commitOut(`drop ${sh.kind} "${name}"`, new Map([[sc.s.scan, [{ op: 'shape.drop', of: '', name } as OpBody]]]))));
      r.append(eye, el('span'), el('span', { class: 'name', title: `${sh.kind} fitted to ${sh.n.toLocaleString()} points, kept in ${scanName}${f !== '0' ? `, frame ${f}` : ''}` }, name),
        el('span', { class: 'n', title: 'pieces · size (cm) · rms (cm)' }, `${pcs.length} · ${size} · ±${cm(Math.max(...pcs.map(q => q.rms)))}`), btns);
      box.append(r);
    }
  }

  /**
   * Fit a one-off pole or surface (the kind and settings in the Shapes row, unless given) to the selected points, on the
   * worker pool with progress under that row (or the one given: Construction's). Resolves to the scan it is kept in (null: none). It is kept in the scan with most of the points, in the frame
   * most of them came from (src/fit/doc.ts); one action. A name already in use is fitted again (and moves
   * to that scan if it was in another).
   */
  private fitOneOff(kind = this.shKind, name = this.inp('eshname').value.trim(), p = { ...this.shParams[kind] }, row = this.$('eshfit').parentElement!): Promise<string | null> {
    if (this.busy || this.job) { if (this.job) this.say(`${this.job.title} is running: wait for it (or Cancel it)`, true); return Promise.resolve(null); }
    if (!GROUP_NAME.test(name)) { this.say('name the shape: letters, digits, space . : + -', true); return Promise.resolve(null); }
    const sf = this.selInFrame(SHAPE_MAX_PTS, true);
    if (!sf || sf.n < 30) { this.say(`select the points to fit a ${kind} to first${sf ? ` (only ${sf.n} selected)` : ''}`); return Promise.resolve(null); }
    const { own, frame, X, N: Nn, n } = sf;
    const job = this.startJob(`fitting ${kind} "${name}" to ${n.toLocaleString()} points`, row.parentElement!, row.nextElementSibling);
    return fitShape(kind, X, Nn, p, job.exec, job.set).then(r => {
      job.end();
      if (!r.pieces) { this.say(`${kind} "${name}": ${r.note}`, true); this.refresh(); return null; }
      const shape = roundDoc({ kind, ref: `f:${frame}`, n, p, pieces: r.pieces }), bodies = new Map<string, OpBody[]>();
      for (const sc of this.scans.values()) if (sc !== own.sc && sc.s.state.shapes.has(shapeKey('', name))) bodies.set(sc.s.scan, [{ op: 'shape.drop', of: '', name } as OpBody]);
      bodies.set(own.sc.s.scan, [{ op: 'shape.set', of: '', name, shape } as OpBody]);
      this.commitOut(`fit ${kind} "${name}"`, bodies);
      return this.idle().then(() => { this.say(`${kind} "${name}": ${r.note}`); this.shapeForm(); return own.sc.s.scan; });
    }, (e: Error) => { job.fail(e); return null; });
  }

  /**
   * The selected points shown (with normals, if withN: those without are left out), per scan; the scan with most
   * of them, the frame most of its came from, and all of them (thinned evenly to at most cap) in that frame's file
   * coordinates. null: none selected.
   */
  private selInFrame(cap: number, withN = false) {
    const picked: { sc: Scan; v: Uint32Array }[] = [];
    let n = 0;
    for (const sc of this.scans.values()) {
      if (!sc.mask || sc.s.graph.stale) continue;
      const live = this.live(sc), v: number[] = [];
      for (let i = 0; i < sc.mask.length; i++) if (sc.mask[i] && live[i] && (!withN || sc.s.N[3 * i] === sc.s.N[3 * i])) v.push(i);
      if (v.length) { picked.push({ sc, v: Uint32Array.from(v) }); n += v.length; }
    }
    if (!n) return null;
    const own = picked.reduce((a, b) => (b.v.length > a.v.length ? b : a)), s = this.placed(own.sc);
    const fc = new Map<number, number>();
    for (const i of own.v) { const f = s.frame?.[i] ?? 0; fc.set(f, (fc.get(f) ?? 0) + 1); }
    const frame = [...fc].reduce((a, b) => (b[1] > a[1] ? b : a))[0], Minv = inverse(compose(s.T, frameTransform(s.state.tree, frame)));
    // into that frame's file coordinates, thinned evenly to at most cap
    const step = Math.max(1, Math.ceil(n / cap)), m = Math.ceil(n / step), xyz = new Float32Array(3 * m), nrm = new Float32Array(3 * m);
    let j = 0, k = 0;
    for (const { sc, v } of picked) {
      const P = this.placed(sc).P, N = sc.s.N;
      for (const i of v) if (k++ % step === 0 && j < m) { xyz.set(P.subarray(3 * i, 3 * i + 3), 3 * j); nrm.set(N.subarray(3 * i, 3 * i + 3), 3 * j); j++; }
    }
    const X = xyz.subarray(0, 3 * j), Nn = nrm.subarray(0, 3 * j);
    transformPoints(Minv, X, X); transformPoints(Minv, Nn, Nn, false);
    return { own, frame, X, N: Nn, n };
  }

  /**
   * A Plane step: fit the construction plane p.name (blank: the next free name) to the selection as the run has it,
   * kept in the frame most of the points came from (as the Construction tab's Plane from selection), with the stamp.
   */
  private async planeStep(p: Params, stamp: { t: string; msg: string }, done: Action) {
    const r = this.selInFrame(PLANE_MAX_PTS);
    const f = planeFit(this.cons.planes(), String(p.name ?? ''), r && { scan: r.own.sc.s.scan, frame: r.frame, xyz: r.X, n: r.n });
    if (typeof f === 'string') throw new Error(f);
    const jobs = [...f.bodies].map(([scan, bodies]) => {
      const sc = this.scans.get(scan);
      if (!sc || sc.s.graph.stale) throw new Error(`plane "${f.name}" is in a read-only scan: fit it again by hand there, or pick another name`);
      return { s: sc.s, bodies };
    });
    const head = proc(`fitting: plane "${f.name}"`, { delay: 400, cantStop: 'a commit is all or nothing: stopping it midway could leave it half made' });
    try { await commitMany(jobs, describe({ t: 'plane', p }), stamp, done); } finally { head.end(); }
    this.say(f.said);
  }

  /** While a frame alignment is previewed: per scan id, the frames' previewed E (and all of them as frameMats rows). */
  private framePreview: Map<string, { E: Map<number, Rigid>; mats: Float32Array }> | null = null;

  /**
   * Show what Align frames would do (the frames drawn where they would go, the scan path too) with Apply and
   * Cancel under the selection row; nothing is committed until Apply. Other edits wait meanwhile.
   */
  /** Draw frames moved by world moves (per scan, per frame) without committing them: points, path, cameras, photos. */
  private setFramePreview(moves: Map<string, Map<number, Rigid>>) {
    const pv = new Map<string, { E: Map<number, Rigid>; mats: Float32Array }>();
    for (const [id, m] of moves) {
      const sc = this.scans.get(id);
      if (!sc) continue;
      const s = sc.s, t = s.state.tree, ix = new Map(s.frameIds.map((f, i) => [f, i])), mats = s.frameMats.slice(), E = new Map<number, Rigid>(), Ti = inverse(s.T);
      for (const [f, W] of m) {
        const e = compose(Ti, compose(W, compose(s.T, frameTransform(t, f))));   // T⁻¹ · W · T · E(f)
        E.set(f, e);
        const i = ix.get(f);
        if (i !== undefined) mats.set([e.R[0], e.R[1], e.R[2], e.t[0], e.R[3], e.R[4], e.R[5], e.t[1], e.R[6], e.R[7], e.R[8], e.t[2]], 12 * i);
      }
      pv.set(id, { E, mats });
      this.v.setEditFrames(id, mats);
    }
    // scans moved before and not now: back as they are
    for (const id of this.framePreview?.keys() ?? []) if (!pv.has(id)) { const sc = this.scans.get(id); if (sc) this.v.setEditFrames(id, sc.s.frameMats); }
    const ids = new Set([...pv.keys(), ...(this.framePreview?.keys() ?? [])]);
    this.framePreview = pv;
    for (const id of ids) { const sc = this.scans.get(id); if (sc) this.drawPath(sc); }
    this.drawShapes();
    this.drawPoints();
  }

  /** The frames drawn as they are again (no preview). */
  private clearFramePreview() {
    const pv = this.framePreview;
    this.framePreview = null;
    for (const id of pv?.keys() ?? []) { const sc = this.scans.get(id); if (sc) { this.v.setEditFrames(id, sc.s.frameMats); this.drawPath(sc); } }
    this.drawShapes();
    this.drawPoints();
  }

  private previewFrames(label: string, r: { bodies: Map<string, OpBody[]>; note: string; moves: Map<string, Map<number, Rigid>> }) {
    this.setFramePreview(r.moves);
    const row = this.$('ewidenf').parentElement!, wrap = el('div', { class: 'ejob' }), btns = el('div', { class: 'row' });
    const ok = el('button', { class: 'small', title: 'commit these frame moves (one edit, undoable)' }, 'Apply'), no = el('button', { class: 'small', title: 'leave the frames where they were' }, 'Cancel');
    btns.append(ok, no);
    wrap.append(el('div', { class: 'mono' }, `Preview: ${r.note}`), btns);
    row.parentElement!.insertBefore(wrap, row.nextElementSibling);
    this.job = { title: 'the frame alignment preview' };
    const end = () => { wrap.remove(); this.job = null; this.clearFramePreview(); };
    ok.onclick = async () => { end(); this.commitOut(label, r.bodies); await this.idle(); this.say(r.note); this.refresh(); };
    no.onclick = () => { end(); this.say('frame alignment cancelled: nothing was changed'); this.refresh(); };
  }

  /** Lock (or unlock) the frames of the selected points: one tree.lock per scan that changes. */
  private lockFrames(on: boolean) {
    if (!this.hasSel()) { this.say(`select some points of the frames to ${on ? 'lock' : 'unlock'} first`); return; }
    let n = 0;
    const bodies = new Map<string, OpBody[]>();
    for (const [id, sc] of this.scans) {
      const lk = sc.s.state.tree.locked, fr = [...this.selectedFrames(sc)].filter(f => lk.has(f) !== on).sort((a, b) => a - b);
      if (!fr.length) continue;
      n += fr.length;
      bodies.set(id, [{ op: 'tree.lock', frames: fr, on } as OpBody]);
    }
    if (!n) { this.say(`those frames are ${on ? 'already locked' : 'not locked'}`); return; }
    this.commitOut(`${on ? 'lock' : 'unlock'} ${n} frame${n === 1 ? '' : 's'}`, bodies);
  }

  // ── named points: frames that share one are pulled together (frameops.ts springAll) ───
  /** Per name, its points in the loaded scans: scan, frame, the mark, the name's weight there, where it is drawn now (or in the alignment preview). */
  private namedPoints() {
    const scans = [...this.scans.values()], of = new Map(scans.map(sc => [sc.s, sc]));
    return new Map([...tiesOf(scans.map(sc => sc.s))].map(([name, ms]) => [name, ms.map(m => {
      const sc = of.get(m.s)!;
      return { sc, f: m.f, mark: { p: m.p, o: m.o, w: m.w }, nw: m.nw, q: apply(this.framePlace(sc, m.f), m.p) };
    })]));
  }

  /** A name's own weight: as its first scan has it (the editor sets it alike in each). */
  private nameW(name: string) { return this.namedPoints().get(name)?.[0]?.nw ?? 1; }

  private editWaits() {
    if (!this.busy && !this.job) return false;
    if (this.job) this.say(`${this.job.title} is running: wait for it (or Cancel it)`, true);
    return true;
  }

  /**
   * Name point (or + on a name's row): the centroid of the selected points of each frame they come from, under the name.
   * masks: those points instead of the selection (the spot under the cursor, from the view's menu).
   */
  private namePoint(name = this.inp('eptname').value.trim(), masks?: Map<string, Uint8Array>) {
    if (this.editWaits()) return;
    if (!GROUP_NAME.test(name)) { this.say('name the point: letters, digits, space . : + -', true); return; }
    const scans = [...this.scans.values()].map(sc => ({ sc, m: masks ? masks.get(sc.s.scan) : sc.mask }))
      .filter(({ sc, m }) => m && sel.count(m) && !sc.s.graph.stale)
      .map(({ sc, m }) => ({ s: this.placed(sc), sel: m!, live: this.live(sc) }));
    const { bodies, n } = markOps(scans, name, this.nameW(name));
    if (!n) {
      this.say(masks ? 'none of the points there have frame ids'
        : scans.length ? 'none of the selected points (shown) have frame ids' : 'select the points of the spot to name first (in each frame that sees it)');
      return;
    }
    this.ptLast = name;
    this.commitOut(`name point "${name}" in ${n} frame${n === 1 ? '' : 's'}`, bodies);
    this.idle().then(() => {
      const nm = this.inp('eptname');
      if (nm.value.trim() === name) nm.value = this.nextPointName();
      const k = this.namedPoints().get(name)?.length ?? 0;
      this.say(`"${name}" is in ${k} frame${k === 1 ? '' : 's'}` + (k < 2 ? ': name the same spot in another frame to tie them' : ': Solve points pulls them together'));
    });
  }

  /** Take frames' points out of a name: those given, or (frames null) the frames of the selected points. */
  private unmark(name: string, frames: { sc: Scan; f: number }[] | null) {
    if (this.editWaits()) return;
    const ms = this.namedPoints().get(name) ?? [];
    const out = frames ?? ms.filter(m => this.selectedFrames(m.sc).has(m.f));
    if (!out.length) { this.say(`none of the selected points are in a frame with "${name}"`); return; }
    const bodies = new Map<string, OpBody[]>();
    for (const sc of new Set(out.map(m => m.sc)))
      bodies.set(sc.s.scan, [{ op: 'tree.mark', name, p: Object.fromEntries(out.filter(m => m.sc === sc).map(m => [`f:${m.f}`, null])) } as OpBody]);
    this.commitOut(out.length === ms.length ? `drop named point "${name}"` : `take ${out.length} frame${out.length === 1 ? '' : 's'} out of "${name}"`, bodies);
  }

  /** Change one frame's point of a name: where it is, its offset or weight. */
  private setMark(name: string, sc: Scan, f: number, m: Mark, msg = `offset ${m.o.map(v => (100 * v).toFixed(1)).join(' ')} cm, weight ${m.w}`) {
    if (this.editWaits()) { this.pointList(); return; }
    this.commitOut(`named point "${name}", frame ${f}: ${msg}`,
      new Map([[sc.s.scan, [{ op: 'tree.mark', name, p: { [`f:${f}`]: markVal(m) } } as OpBody]]]));
  }

  /** Set a name's own weight (multiplied into each of its frames'), in every scan that has it. */
  private setNameW(name: string, w: number) {
    if (this.editWaits()) { this.pointList(); return; }
    const scs = new Set((this.namedPoints().get(name) ?? []).map(m => m.sc));
    this.commitOut(`named point "${name}": weight ${w}`, new Map([...scs].map(sc => [sc.s.scan, [{ op: 'tree.mark', name, p: {}, w } as OpBody]])));
  }

  /** Select the points of these frames (a name's, or one of them); inverted, every shown point but theirs (in every scan). */
  private selFrames(ms: { sc: Scan; f: number }[], rule: Rule, invert = false) {
    const out = new Map<string, Uint8Array>();
    for (const sc of invert ? this.scans.values() : new Set(ms.map(m => m.sc))) {
      const fr = new Set(ms.filter(m => m.sc === sc).map(m => m.f)), s = sc.s, live = this.live(sc), m = new Uint8Array(s.nv), k = +!invert;
      if (s.frame) for (let v = 0; v < s.nv; v++) m[v] = live[v] & +(+fr.has(s.frame[v]) === k);
      else if (invert) m.set(live);
      out.set(s.scan, m);
    }
    this.setSel(out, rule);
  }

  /** Solve points: preview the frames that share a name pulled together (the springs carrying their neighbours), then Apply. */
  private solvePoints() {
    if (this.editWaits()) return;
    const scans = [...this.scans.values()].filter(sc => !sc.s.graph.stale).map(sc => this.placed(sc));
    const reach = Math.max(0, +(this.$('ereach') as HTMLInputElement).value || 0);
    this.say('solving the named points…');
    setTimeout(() => {
      const r = solvePoints(scans, reach);
      if (r.bodies.size) this.previewFrames('solve named points', r); else this.say(r.note);
    }, 0);
  }

  /** A name's points as a tie (its gap, each point plus its offset). */
  private static tie = (ms: { f: number; q: V3; mark: Mark }[]) => ms.map(m => ({ f: m.f, q: m.q, o: m.mark.o }));

  /** The named points: a cross at each (and a tick to where its offset puts it), a line between them while they are apart, coloured by the gap. */
  private drawPoints() {
    this.v.removeLines('ept:');
    this.ptHits = [];
    if (this.inp('eptshow').checked) this.drawPointLines();
    const m = this.ptMenu?.hit;   // its point gone (dropped, hidden, its scan put away): close the menu; else show it as it is now
    if (m) { const h = this.ptHits.find(x => x.name === m.name && x.sc === m.sc && x.f === m.f); if (h) this.openPtMenu(h); else this.closePtMenu(); }
  }

  private drawPointLines() {
    const a = 0.04;
    for (let [name, ms] of this.namedPoints()) {
      if (this.ptHidden.has(name)) continue;
      // a frame's point being moved with the gizmo: drawn where it would go
      const mv = this.ptMove?.name === name ? this.ptMove : null;
      ms = ms.map(m => mv && mv.sc === m.sc && mv.f === m.f ? { ...m, q: add(m.q, mv.d) } : m);
      for (const m of ms) this.ptHits.push({ name, sc: m.sc, f: m.f, q: m.q });
      const gap = tieGap(EditUI.tie(ms)), c = stretchColor(stretchScale(gap, 0)), xyz: number[] = [];
      const at = ms.map(({ q, mark: { o } }) => [q[0] + o[0], q[1] + o[1], q[2] + o[2]]);
      ms.forEach(({ q }, i) => {
        for (let d = 0; d < 3; d++) {
          const u = [0, 0, 0]; u[d] = a;
          xyz.push(q[0] - u[0], q[1] - u[1], q[2] - u[2], q[0] + u[0], q[1] + u[1], q[2] + u[2]);
        }
        if (Math.hypot(at[i][0] - q[0], at[i][1] - q[1], at[i][2] - q[2]) > 1e-4) xyz.push(...q, ...at[i]);
      });
      if (gap > 1e-3) for (let i = 1; i < ms.length; i++) xyz.push(...at[i - 1], ...at[i]);
      this.v.setLines(`ept:${name}`, new Float32Array(xyz), [c[0], c[1], c[2], 1], true, false);
    }
  }

  /** A frame's point being moved with the gizmo (movePoint): its name, scan, frame and the move so far (m, world). */
  private ptMove: { name: string; sc: Scan; f: number; d: V3 } | null = null;

  /**
   * Move one frame's point of a name with the gizmo as a square: its long x / y lines turn (⟳) to lie along a
   * wall or edge, and the point itself is dragged along them (or up, or across the floor), drawn live where it
   * would go; Apply puts it there in its frame (its offset kept), one undoable edit. Only that frame's points
   * show meanwhile.
   */
  private movePoint(name: string, sc: Scan, f: number) {
    if (this.editWaits()) return;
    this.gizmo?.cancel();
    const m = this.namedPoints().get(name)?.find(x => x.sc === sc && x.f === f);
    if (!m) return;
    if (this.ptHidden.has(name)) { this.ptHidden.delete(name); this.pointList(); }
    if (!this.inp('eptshow').checked) { this.inp('eptshow').checked = true; }
    const done = () => { this.gizmo = null; this.ptMove = null; this.soloFrame = null; this.refresh(); this.drawPoints(); };
    this.ptMove = { name, sc, f, d: [0, 0, 0] };
    this.soloFrame = { scan: sc.s.scan, f };
    this.refresh();
    this.gizmo = new Gizmo(this.v, m.q, `Move "${name}", frame ${f}`, {
      change: W => { if (this.ptMove) { this.ptMove.d = [...W.t] as V3; this.drawPoints(); } },
      apply: (W, made) => {
        done();
        if (Math.hypot(...made.shift) < 1e-6) return;
        const cur = this.namedPoints().get(name)?.find(x => x.sc === sc && x.f === f);
        if (!cur) { this.say(`frame ${f} is no longer in "${name}"`); return; }
        this.setMark(name, sc, f, { ...cur.mark, p: apply(inverse(this.framePlace(sc, f)), add(cur.q, made.shift)) },
          `moved ${made.shift.map(v => (100 * v).toFixed(1)).join(' ')} cm`);
      },
      cancel: done,
    }, { square: true });
    this.drawPoints();
  }

  /** Where a world point is on the canvas (px), or null behind the camera. */
  private screen(p: V3): [number, number] | null {
    const c = this.v.cam, q = project(c.vp, p);
    return q[3] > 0 && Math.abs(q[2]) <= 1 ? [((q[0] + 1) / 2) * c.w, ((1 - q[1]) / 2) * c.h] : null;
  }

  /** The drawn named point nearest (x, y) on the canvas, within r px. */
  private ptAt(x: number, y: number, r = 10) {
    let best: (typeof this.ptHits)[number] | null = null, bd = r;
    for (const h of this.ptHits) {
      const s = this.screen(h.q), d = s ? Math.hypot(s[0] - x, s[1] - y) : Infinity;
      if (d <= bd) { bd = d; best = h; }
    }
    return best;
  }

  /**
   * A named point's menu, by its cross in the view: add the selection's frames to its name (or take them out),
   * select its points or this frame's, take this frame out, open it in the list, or drop it. Follows the point
   * as the view moves; Esc, a click anywhere else or any of its buttons closes it.
   */
  private openPtMenu(hit: (typeof this.ptHits)[number]) {
    this.closeBoxMenu(); this.closeCamMenu();
    const { name, sc, f } = hit, ms = this.namedPoints().get(name), m = ms?.find(x => x.sc === sc && x.f === f);
    if (!ms || !m) { this.closePtMenu(); return; }
    let pm = this.ptMenu;
    if (!pm) {
      const box = el('div', { class: 'ptmenu' });
      const off = (e: PointerEvent) => { if (!box.contains(e.target as Node) && e.target !== this.v.canvas) this.closePtMenu(); };   // on the view: a drag moves it, a tap elsewhere closes it (tap)
      addEventListener('pointerdown', off, true);
      document.body.append(box);
      pm = this.ptMenu = { box, hit, raf: 0, off };
      const tick = () => { this.placePtMenu(); if (this.ptMenu) this.ptMenu.raf = requestAnimationFrame(tick); };
      pm.raf = requestAnimationFrame(tick);
    }
    pm.hit = hit;
    const box = pm.box, has = this.hasSel(), multi = this.scans.size > 1;
    const scanName = this.v.clouds.find(x => x.meta.id === sc.s.scan)?.meta.name ?? sc.s.scan;
    const gap = tieGap(EditUI.tie(ms)), c = stretchColor(stretchScale(gap, 0));
    const go = (fn: () => void) => () => { this.closePtMenu(); fn(); };
    const b = (t: string, tip: string, fn: () => void, off = false) => { const e = el('button', { class: 'small', title: tip }, t) as HTMLButtonElement; e.onclick = go(fn); e.disabled = off; return e; };
    box.textContent = '';
    const dot = el('span', {}, '● ');
    dot.style.color = `rgb(${c.map(v => Math.round(255 * v)).join(' ')})`;
    const head = el('div', { class: 'pmhead' });
    head.append(dot, el('b', {}, name));
    box.append(head,
      el('div', { class: 'help' }, `${multi ? `${scanName} · ` : ''}frame ${f}`),
      el('div', { class: 'help' }, `${ms.length} frame${ms.length === 1 ? '' : 's'} · gap ${(100 * gap).toFixed(1)} cm`),
      b('Add selection', `add the selected points' frames to "${name}" (the centroid of each frame's selected points)`, () => this.namePoint(name), !has),
      b('Remove selection', `take the selected points' frames out of "${name}"`, () => this.unmark(name, null), !has),
      b('Select point', `select the points of every frame of "${name}"`, () => this.selFrames(ms, { t: 'named point', name })),
      b('Select all but point', `select every shown point except those of the frames of "${name}"`, () => this.selFrames(ms, { t: 'named point', name, invert: true }, true)),
      b(`Select frame ${f}`, `select the points of frame ${f}`, () => this.selFrames([m], { t: 'named point', name })),
      b(`Move frame ${f}`, `move frame ${f}'s point with a square gizmo (long lines, turned with ⟳ to lie along a wall or edge), then Apply`, () => this.movePoint(name, sc, f)),
      b(`Remove frame ${f}`, `take frame ${f} out of "${name}"`, () => this.unmark(name, [m])),
      b('Show in list', 'open it in the Named points list', () => {
        this.ptOpen.add(name); this.pointList();
        [...this.$('epoints').querySelectorAll<HTMLElement>('.layer .name')].find(e => e.textContent?.endsWith(` ${name}`))?.scrollIntoView({ block: 'nearest' });
      }),
      b('Drop point', `drop "${name}" from every frame`, () => this.unmark(name, ms)));
    this.placePtMenu();
  }

  /** Put the menu beside its point, inside the window; hidden while the point is behind the camera. */
  private placePtMenu() {
    const pm = this.ptMenu;
    if (!pm) return;
    const s = this.screen(pm.hit.q), box = pm.box;
    box.style.visibility = s ? '' : 'hidden';
    if (!s) return;
    const r = this.v.canvas.getBoundingClientRect(), w = box.offsetWidth, h = box.offsetHeight;
    box.style.left = `${Math.max(8, Math.min(innerWidth - w - 8, r.left + s[0] + 12))}px`;
    box.style.top = `${Math.max(8, Math.min(innerHeight - h - 8, r.top + s[1] - 12))}px`;
  }

  private closePtMenu() {
    const pm = this.ptMenu;
    if (!pm) return;
    this.ptMenu = null;
    cancelAnimationFrame(pm.raf);
    removeEventListener('pointerdown', pm.off, true);
    pm.box.remove();
  }

  // ── instance boxes ─────────────────────────────────────────────────────
  /**
   * Each fitted instance's bounding box: its type's box (set on the object page, kept in its reference instance's
   * frame) placed by the instance's fit, tinted as the instance is. Instances in shown scans only; none while
   * the instance viewer overlays them.
   */
  private drawBoxes() {
    this.v.removeLines('ebox:');
    this.boxHits = [];
    if (this.inp('eboxshow').checked && !this.viewing) {
      const shown = [...this.scans.values()].filter(sc => !this.off(sc)).map(sc => sc.s), boxes = ovl.typeBoxes(shown);
      if (boxes.size) {
        const info = this.objectInfo(), tint = new Map<string, number>(), moved = this.pubMoves();
        for (const s of shown) for (const [id, o] of s.state.objects) if (!tint.has(o.name)) tint.set(o.name, id);
        for (const [name, o] of info) {
          const b = o.of ? boxes.get(o.of)?.box : undefined;
          if (!b || !tint.has(name) || !ovl.isFitted(info, name)) continue;   // unfitted: no place for its box
          const Pd = moved.get(name), c = ovl.instanceBox(b, info, name).map(q => (Pd ? apply(Pd, q) : q));   // where it will be published
          this.boxHits.push({ name, type: o.of!, c });
          this.v.setLines(`ebox:${name}`, ovl.boxEdges(c), [...ovl.hueRgb(tint.get(name)!), 1], true, false);
        }
      }
    }
    const m = this.boxMenu?.hit;   // its box gone: close the menu; else follow it as it is now
    if (m) { const h = this.boxHits.find(x => x.name === m.name); if (h) this.openBoxMenu(h); else this.closeBoxMenu(); }
  }

  // ── instances as voxels ────────────────────────────────────────────────
  /**
   * Which instances are drawn as their type's voxel model (built on the object page): each fitted instance, in a
   * shown scan, of a type with a model, placed as its box is (F⁻¹ · F_ref, then its publish move). None while the instance viewer
   * overlays them (it shows their points), with voxels unticked, or for a type with its voxels unticked in the Classes window.
   */
  private findVoxed() {
    this.voxed.clear();
    if (!this.inp('evoxshow').checked || this.viewing) return;
    const shown = [...this.scans.values()].filter(sc => !this.off(sc)).map(sc => sc.s), models = ovl.typeVoxels(shown);
    if (!models.size) return;
    const info = this.objectInfo(), hue = new Map<string, number>(), moved = this.pubMoves();
    for (const s of shown) for (const [id, o] of s.state.objects) if (!hue.has(o.name)) hue.set(o.name, id);
    for (const [name, o] of info) {
      const m = o.of ? models.get(o.of)?.vox : undefined, Pd = moved.get(name);
      if (m && !this.rawTypes.has(o.of!) && hue.has(name) && ovl.isFitted(info, name)) {
        const M = ovl.instanceVoxelM(m, info, name);
        this.voxed.set(name, { M: Pd ? compose(Pd, M) : M, type: o.of!, hue: hue.get(name)! });   // where it will be published
      }
    }
  }

  /** The voxel clouds of findVoxed's instances (one decode per type; redrawn only when a model or a fit changes). */
  private drawVoxels() {
    const models = ovl.typeVoxels([...this.scans.values()].map(sc => sc.s));
    const key = JSON.stringify([...this.voxed].map(([n, x]) => [n, x.M, x.hue, models.get(x.type)?.t]));
    if (key === this.voxedKey) return;
    this.voxedKey = key;
    for (const id of [...this.v.edits.keys()]) if (id.startsWith(VOX)) this.v.clearEditCloud(id);
    const built = new Map<string, { idx: ReturnType<typeof asCloud>['idx']; rec: Uint8Array; n: number }>();
    for (const [name, x] of this.voxed) {
      const m = models.get(x.type)?.vox;
      if (!m) continue;
      let c = built.get(x.type);
      if (!c) { const d = decodeVoxels(m); c = { ...asCloud(d.xyz, d.nrm, d.rgb, m.size), n: m.n }; built.set(x.type, c); }
      const meta = { ...this.v.clouds[0].meta, id: VOX + name, name: `${name} (voxels)`, color: '#ffffff' } as CloudMeta;
      this.v.setEditCloud(meta, c.idx, [c.rec], x.M, new Uint16Array(c.n), new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0]));
      this.v.setEditState(VOX + name, new Uint32Array(c.n).fill(32 | ((x.hue & 255) << 8)));   // tinted as the instance's points are
    }
  }

  /** The drawn instance box with an edge nearest (x, y) on the canvas, within r px. */
  private boxAt(x: number, y: number, r = 8) {
    let best: BoxHit | null = null, bd = r;
    for (const h of this.boxHits) {
      const s = h.c.map(q => this.screen(q));
      for (const [i, j] of BOX_EDGES) {
        const a = s[i], b = s[j];
        if (!a || !b) continue;
        const dx = b[0] - a[0], dy = b[1] - a[1], L = dx * dx + dy * dy, t = L ? Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / L)) : 0;
        const d = Math.hypot(a[0] + t * dx - x, a[1] + t * dy - y);
        if (d <= bd) { bd = d; best = h; }
      }
    }
    return best;
  }

  /** Where a box's menu sits: the middle of its top face. */
  private static boxTop(c: V3[]): V3 { return [4, 5, 6, 7].reduce<V3>((a, i) => [a[0] + c[i][0] / 4, a[1] + c[i][1] / 4, a[2] + c[i][2] / 4], [0, 0, 0]); }

  /**
   * An instance box's menu: add the selection to that instance, merge in the others of its type at the same
   * place, select its points, fit it and its frames onto the master and each other, move it in the world with the gizmo, or open its type on the object page. Follows the box as the view moves; Esc, a click anywhere else or any of its buttons closes it.
   */
  private openBoxMenu(hit: BoxHit) {
    this.closePtMenu(); this.closeViewMenu(); this.closeCamMenu();
    let bm = this.boxMenu;
    if (!bm) {
      const box = el('div', { class: 'ptmenu' });
      const off = (e: PointerEvent) => { if (!box.contains(e.target as Node) && e.target !== this.v.canvas) this.closeBoxMenu(); };   // on the view: a drag moves it, a tap elsewhere closes it (tap)
      addEventListener('pointerdown', off, true);
      document.body.append(box);
      bm = this.boxMenu = { box, hit, raf: 0, off };
      const tick = () => { this.placeBoxMenu(); if (this.boxMenu) this.boxMenu.raf = requestAnimationFrame(tick); };
      bm.raf = requestAnimationFrame(tick);
    }
    bm.hit = hit;
    const { name, type } = hit, box = bm.box, has = this.hasSel();
    const go = (fn: () => void) => () => { this.closeBoxMenu(); fn(); };
    const b = (t: string, tip: string, fn: () => void, off = false) => { const e = el('button', { class: 'small', title: tip }, t) as HTMLButtonElement; e.onclick = go(fn); e.disabled = off; return e; };
    const id = [...this.scans.values()].map(sc => idsNamed(sc.s.state.objects, name)[0]).find(x => x) ?? 0;
    const tb = ovl.typeBoxes([...this.scans.values()].filter(sc => !this.off(sc)).map(sc => sc.s)).get(type)?.box;
    const info = this.objectInfo(), near = tb ? instops.nearInstances(this.placedAll(), info, tb, name) : [];
    box.textContent = '';
    const dot = el('span', {}, '■ ');
    dot.style.color = hueCss(id);
    const head = el('div', { class: 'pmhead' });
    head.append(dot, el('b', {}, name));
    box.append(head,
      el('div', { class: 'help' }, `an instance of ${type}`),
      b(`Add selection to "${name}"`, `the selected points join "${name}" (one Undo)`, () => this.addToObject(name), !has),
      b(near.length ? `Merge ${near.length} here into "${name}"` : 'Merge (none here)',
        near.length ? `the other ${type} instances at this place (${near.join(', ')}: their centre in this box, or this one's in theirs) join "${name}", labels kept; one Undo`
          : `no other ${type} instance has its centre in this box (or this one's in its box)`,
        () => { const r = instops.mergeInstances(this.placedAll(), info, name, near); this.commitOut(`merge into "${name}"`, r.bodies); this.say(r.note); }, !near.length),
      b('Select instance', `select the points of "${name}"`, this.pickSrc(`object:${name}`)),
      b('Fit', `fit "${name}" onto ${this.masterLabel(type)}, then each of its frames onto the master and its other frames (only its points move; one Undo)`, () => this.fitInstanceAll(type, name)),
      b('Move', 'move / turn it with the gizmo (shown as a ghost; applies when published)', () => this.moveObject(name)),
      b(`Edit ${type}`, `the object editor on ${type} (where its box is set)`, () => this.openObjects(type)));
    this.placeBoxMenu();
  }

  /** Put the box menu by the top of its box, inside the window; hidden while that is behind the camera. */
  private placeBoxMenu() {
    const bm = this.boxMenu;
    if (!bm) return;
    const s = this.screen(EditUI.boxTop(bm.hit.c)), box = bm.box;
    box.style.visibility = s ? '' : 'hidden';
    if (!s) return;
    const r = this.v.canvas.getBoundingClientRect(), w = box.offsetWidth, h = box.offsetHeight;
    box.style.left = `${Math.max(8, Math.min(innerWidth - w - 8, r.left + s[0] + 12))}px`;
    box.style.top = `${Math.max(8, Math.min(innerHeight - h - 8, r.top + s[1] - 12))}px`;
  }

  private closeBoxMenu() {
    const bm = this.boxMenu;
    if (!bm) return;
    this.boxMenu = null;
    cancelAnimationFrame(bm.raf);
    removeEventListener('pointerdown', bm.off, true);
    bm.box.remove();
  }

  /** Names open in the list (their points shown, each with its offset and weight). */
  private ptOpen = new Set<string>();

  /**
   * The named points list: per name, show / hide, how many frames and how far apart; + adds the selection's
   * frames, − takes them out, Sel selects its frames, ✕ drops it. Click a name to open it: each frame's point,
   * with its offset (cm, world) and weight, and ✕ to take that one out.
   */
  private pointList() {
    const box = this.$('epoints'), multi = this.scans.size > 1;
    box.textContent = '';
    const b = (t: string, tip: string, go: () => void) => { const e = el('button', { class: 'small', title: tip }, t); e.onclick = go; return e; };
    const scanName = (sc: Scan) => this.v.clouds.find(x => x.meta.id === sc.s.scan)?.meta.name ?? sc.s.scan;
    for (const [name, ms] of this.namedPoints()) {
      const hid = this.ptHidden.has(name), open = this.ptOpen.has(name), r = el('div', { class: 'layer' + (hid ? ' off' : '') });
      const gap = tieGap(EditUI.tie(ms)), c = stretchColor(stretchScale(gap, 0)), nsc = new Set(ms.map(m => m.sc)).size;
      const eye = el('button', { class: 'eye', title: hid ? 'show' : 'hide' }, hid ? '◌' : '●');
      eye.style.color = `rgb(${c.map(v => Math.round(255 * v)).join(' ')})`;
      eye.onclick = () => { if (hid) this.ptHidden.delete(name); else this.ptHidden.add(name); this.drawPoints(); this.pointList(); };
      const nm = el('span', { class: 'name', title: open ? 'close' : 'open: each frame\'s point, its offset and weight' }, `${open ? '▾' : '▸'} ${name}`);
      nm.style.cursor = 'pointer';
      nm.onclick = () => { if (open) this.ptOpen.delete(name); else this.ptOpen.add(name); this.pointList(); };
      const btns = el('span', { class: 'row' });
      btns.append(
        b('+', `add the selected points' frames to "${name}" (the centroid of each frame's selected points)`, () => this.namePoint(name)),
        b('−', `take the selected points' frames out of "${name}"`, () => this.unmark(name, null)),
        b('Sel', 'select the points of its frames', () => this.selFrames(ms, { t: 'named point', name })),
        b('✕', 'drop the named point from every frame', () => this.unmark(name, ms)));
      const nw = ms[0].nw;
      r.append(eye, el('span'), nm, el('span', { class: 'n', title: 'frames (scans) · widest gap (cm) · its weight' }, `${ms.length}${nsc > 1 ? ` (${nsc})` : ''} · ${(100 * gap).toFixed(1)}${nw !== 1 ? ` · ×${nw}` : ''}`), btns);
      box.append(r);
      if (!open) continue;
      // the name's own weight, multiplied into each frame's
      const nwr = el('div', { class: 'ptmember' }), nwc = el('div', { class: 'ptctl' });
      const nwi = el('input', { type: 'number', step: '0.1', min: '0', max: String(MAX_MARK_W), value: String(nw), style: 'width:3.2em',
        title: `weight of "${name}": multiplies each frame's weight below (2 twice as much give in all its frames, 0 holds them all)` }) as HTMLInputElement;
      nwi.onchange = () => { const w = Math.min(MAX_MARK_W, Math.max(0, +nwi.value || 0)); if (w !== nw) this.setNameW(name, w); else nwi.value = String(nw); };
      nwc.append(el('span', { class: 'help', title: nwi.title }, 'point weight ×'), nwi);
      nwr.append(nwc);
      box.append(nwr);
      // each frame's point: how far it is from where the others put it on average, its offset and weight
      const at = ms.map(({ q, mark: { o } }) => [q[0] + o[0], q[1] + o[1], q[2] + o[2]]);
      ms.forEach((m, i) => {
        const others = at.filter((_, j) => j !== i), mean = [0, 1, 2].map(d => others.reduce((s, x) => s + x[d], 0) / Math.max(1, others.length));
        const off = others.length ? Math.hypot(at[i][0] - mean[0], at[i][1] - mean[1], at[i][2] - mean[2]) : 0;
        const row = el('div', { class: 'ptmember' });
        const num = (v: number, tip: string, step: string, w: string) => el('input', { type: 'number', step, value: String(v), title: tip, style: `width:${w}` }) as HTMLInputElement;
        const ox = ['x', 'y', 'z'].map((k, d) => num(+(100 * m.mark.o[d]).toFixed(2), `offset ${k} (cm, world): where this point should meet the others, from where it is`, '0.5', '3.4em'));
        const wt = num(m.mark.w, `weight: how much this frame gives to meet the others (1 as much as any, 2 twice, 0 holds still)`, '0.1', '3.2em');
        wt.min = '0'; wt.max = String(MAX_MARK_W);
        const commit = () => {
          const o = ox.map(i => (+i.value || 0) / 100) as V3, w = Math.min(MAX_MARK_W, Math.max(0, +wt.value || 0));
          if (o.every((v, d) => Math.abs(v - m.mark.o[d]) < 1e-9) && w === m.mark.w) return;
          this.setMark(name, m.sc, m.f, { p: m.mark.p, o, w });
        };
        for (const i of [...ox, wt]) i.onchange = commit;
        const head = el('div', { class: 'pthead' }), ctl = el('div', { class: 'ptctl' });
        const right = el('span', { class: 'ptright' });
        right.append(el('span', { class: 'n', title: 'how far its point (with its offset) is from the others\' (cm)' }, `${(100 * off).toFixed(1)} cm off`),
          b('Sel', `select the points of frame ${m.f}`, () => this.selFrames([m], { t: 'named point', name })),
          b('Move', `move this point with a square gizmo (long lines, turned with ⟳ to lie along a wall or edge), then Apply`, () => this.movePoint(name, m.sc, m.f)));
        head.append(el('span', { class: 'name', title: `${scanName(m.sc)}, frame ${m.f}` }, `${multi ? `${scanName(m.sc)} · ` : ''}frame ${m.f}`), right);
        ctl.append(el('span', { class: 'help', title: 'offset x y z (cm, world)' }, 'offset'), ...ox, el('span', { class: 'help', title: wt.title }, 'w'), wt,
          b('✕', `take frame ${m.f} out of "${name}"`, () => this.unmark(name, [m])));
        row.append(head, ctl);
        box.append(row);
      });
    }
    const nm = this.inp('eptname');
    if (!nm.value) nm.value = this.nextPointName();
  }

  /** The first "point N" no frame has yet. */
  private nextPointName() {
    const names = this.namedPoints();
    let k = 1;
    while (names.has(`point ${k}`)) k++;
    return `point ${k}`;
  }

  /** Right-click / long press in the view: a named point's menu over its cross, else the view's. False with no scan to edit. */
  context(x: number, y: number): boolean {
    if (!this.on || this.suspended || !this.scans.size) return false;
    this.closePtMenu(); this.closeViewMenu(); this.closeBoxMenu(); this.closeCamMenu();
    if (this.cons.tap(x, y)) return true;   // Construction: a thing's inspector, not its menu
    const pt = this.ptAt(x, y), bx = pt ? null : this.boxAt(x, y), cm = pt || bx ? null : this.camAt(x, y);
    if (pt) this.openPtMenu(pt); else if (bx) this.openBoxMenu(bx); else if (cm) this.openCamMenu(cm); else this.openViewMenu(x, y);
    return true;
  }

  /**
   * The view's menu at (x, y): create a named point ("point N") at the spot under the cursor — the centroid, in each
   * frame that sees it, of its front points within a few px — or add the spot to the name last used (the frames not
   * in it yet); name the selection; solve. Esc, a tap anywhere else or any of its buttons closes it.
   */
  private openViewMenu(x: number, y: number) {
    const r = 10, spot = this.lassoMasks(sel.circle(x, y, r), true);
    // only the points shown (not deleted or hidden, nor culled backfaces), with a frame
    const frames = new Map<Scan, Set<number>>(), c = this.v.cam;
    for (const [id, m] of spot) {
      const sc = this.scans.get(id)!, s = sc.s, live = this.live(sc), fs = new Set<number>();
      if (this.v.s.cull) { const q = this.selSpace(sc), f = sel.facing(q.P, q.N, c.e, c.f, c.ortho, this.v.s.cullT); for (let v = 0; v < m.length; v++) m[v] &= f[v]; }
      for (let v = 0; v < m.length; v++) { m[v] &= live[v]; if (m[v] && s.frame?.[v]) fs.add(s.frame[v]); }
      if (fs.size) frames.set(sc, fs);
    }
    const nf = [...frames.values()].reduce((k, fs) => k + fs.size, 0), multi = this.scans.size > 1;
    const scanName = (sc: Scan) => this.v.clouds.find(c => c.meta.id === sc.s.scan)?.meta.name ?? sc.s.scan;
    const where = [...frames].map(([sc, fs]) => `${multi ? `${scanName(sc)} ` : ''}frame${fs.size === 1 ? '' : 's'} ${[...fs].sort((a, b) => a - b).join(', ')}`).join(' · ');
    const names = this.namedPoints(), name = this.nextPointName(), last = this.ptLast && names.has(this.ptLast) ? this.ptLast : null;
    // adding to a name: only the frames not in it yet (those that are keep their point; Move shifts one)
    const fresh = (nm: string) => {
      const has = new Map<Scan, Set<number>>();
      for (const m of names.get(nm) ?? []) (has.get(m.sc) ?? has.set(m.sc, new Set()).get(m.sc)!).add(m.f);
      let k = 0;
      const out = new Map<string, Uint8Array>();
      for (const [id, m] of spot) {
        const sc = this.scans.get(id)!, fr = sc.s.frame, h = has.get(sc), o = new Uint8Array(m.length), seen = new Set<number>();
        if (fr) for (let v = 0; v < m.length; v++) if (m[v] && fr[v] && !h?.has(fr[v])) { o[v] = 1; seen.add(fr[v]); }
        k += seen.size;
        out.set(id, o);
      }
      return { out, k };
    };
    const more = last ? fresh(last) : null;
    const solvable = [...names.values()].some(ms => ms.length > 1);
    const box = el('div', { class: 'ptmenu' });
    const off = (e: PointerEvent) => { if (!box.contains(e.target as Node)) this.closeViewMenu(); };
    addEventListener('pointerdown', off, true);
    this.viewMenu = { box, off };
    const go = (fn: () => void) => () => { this.closeViewMenu(); fn(); };
    const b = (t: string, tip: string, fn: () => void, dis = false) => { const e = el('button', { class: 'small', title: tip }, t) as HTMLButtonElement; e.onclick = go(fn); e.disabled = dis; return e; };
    box.append(el('div', { class: 'help' }, nf ? where : 'no points with frame ids here'),
      b(`Create point "${name}"`, `name this spot "${name}": the centroid of its points within ${r} px in each frame that sees it`, () => this.namePoint(name, spot), !nf));
    if (last && more) box.append(b(`Add to "${last}"`, `add this spot to "${last}" in the frames that see it and aren't in it yet` + (more.k < nf ? ` (${nf - more.k} already are: Move shifts theirs)` : ''),
      () => this.namePoint(last, more.out), !more.k));
    if (this.hasSel()) box.append(b(`Name selection "${name}"`, `name the centroid of the selected points in each frame they come from "${name}"`, () => this.namePoint(name)));
    box.append(b('Solve points', 'move the frames that share a name until their points meet; preview, then Apply', () => this.solvePoints(), !solvable));
    document.body.append(box);
    const cr = this.v.canvas.getBoundingClientRect(), w = box.offsetWidth, h = box.offsetHeight;
    box.style.left = `${Math.max(8, Math.min(innerWidth - w - 8, cr.left + x + 4))}px`;
    box.style.top = `${Math.max(8, Math.min(innerHeight - h - 8, cr.top + y + 4))}px`;
  }

  private closeViewMenu() {
    const vm = this.viewMenu;
    if (!vm) return;
    this.viewMenu = null;
    removeEventListener('pointerdown', vm.off, true);
    vm.box.remove();
  }

  private hasSel() { for (const sc of this.scans.values()) if (sc.mask && sel.count(sc.mask)) return true; return false; }

  /** Unticked in Scans: loaded, not shown, its selection parked. */
  private off(sc: Scan) { return this.v.edits.get(sc.s.scan)?.visible === false; }

  /** The scan's selection, shown or parked. */
  private held(sc: Scan) { return this.off(sc) ? sc.parked ?? null : sc.mask; }

  /** Set the scan's selection: parked while it's unticked. */
  private hold(sc: Scan, m: Uint8Array | null) { if (this.off(sc)) { sc.parked = m; sc.mask = null; } else sc.mask = m; }

  private hasHeld() { for (const sc of this.scans.values()) { const m = this.held(sc); if (m && sel.count(m)) return true; } return false; }

  private clearSel() { this.setSel(new Map(), null); }

  /** Every shown point not selected, and none of those selected; unticked scans keep theirs. */
  private invert() { this.runRule('invert', {}); }

  /** Ctrl+A: every point shown (layers on, not cut away by the view or a hide volume); replaces the selection. */
  private selectAll() {
    if (this.busy || this.stroke) return;
    this.selectShown('replace');
  }
  private selectShown(mode: sel.Combine) { this.runRule('all', {}, mode); }

  /** Replace the selection: a mask per scan (missing = nothing there). A step undo can take back. */
  private setSel(ms: Map<string, Uint8Array>, rule: Rule) {
    this.step({ k: 'sel', snap: this.snapSel() });
    this.future = [];
    for (const [id, sc] of this.scans) this.hold(sc, ms.get(id) ?? null);
    this.rule = this.hasHeld() ? rule : null;
    this.refresh();
  }

  private snapSel(): SelSnap {
    const masks = new Map<string, Uint32Array>();
    for (const [id, sc] of this.scans) { const m = this.held(sc); if (m) masks.set(id, runs(m)); }
    return { masks, rule: this.rule, mem: new Map([...this.mem].map(([k, g]) => [k, new Map(g)])), data: new Map([...this.data].map(([k, g]) => [k, new Map(g)])) };
  }

  private restoreSel(s: SelSnap) {
    for (const [id, sc] of this.scans) { const r = s.masks.get(id); this.hold(sc, r ? unruns(r, sc.s.nv) : null); }
    this.rule = this.hasHeld() ? s.rule : null;
    this.mem = new Map([...s.mem].map(([k, g]) => [k, new Map(g)]));
    this.data = new Map([...s.data].map(([k, g]) => [k, new Map(g)]));
  }

  private step(x: Step) {
    if (this.batching && x.k === 'sel') return;   // a macro's selections: one step for the lot (runMacro)
    this.past.push(x);
    if (this.past.length > MAX_STEPS) this.past.shift();
  }

  /** Undo the newest step: a selection change, or an edit (then the selection before it comes back too). */
  private async undoStep() {
    const top = this.past.pop();
    const w = top?.w ? this.swapWeights(top.w) : undefined;
    if (top?.k === 'sel') { this.future.push({ k: 'sel', snap: this.snapSel(), w }); this.restoreSel(top.snap); return; }
    // an edit; with no step recorded (edits from before this page was loaded), the newest edit
    const after = this.snapSel(), act = await this.undo();
    if (act) this.future.push({ k: 'edit', act, snap: after, w });
    if (top) this.restoreSel(top.snap);
  }

  private async redoStep() {
    const top = this.future.pop();
    if (!top) return;
    const before = this.snapSel(), w = top.w ? this.swapWeights(top.w) : undefined;
    if (top.k === 'edit') await this.redo(top.act);
    this.step({ k: top.k, snap: before, w });
    this.restoreSel(top.snap);
  }

  /** Put these weights back (and save them): the weights they replace. */
  private swapWeights(w: WeightSnap): WeightSnap {
    const was: WeightSnap = new Map();
    for (const [id, a] of w) {
      const sc = this.scans.get(id);
      if (!sc || sc.s.weight.length !== a.length) continue;
      was.set(id, sc.s.weight);
      sc.s.weight = a.slice();
    }
    this.weightsChanged([...was.keys()]);
    return was;
  }

  /** Scans' weights changed: save them, and show them (Image weights). */
  private weightsChanged(ids: string[]) {
    if (!ids.length) return;
    this.wVer++;
    for (const id of ids) {
      const sc = this.scans.get(id);
      if (sc) sc.s.saveWeights().catch(e => this.say(`${sc.s.meta.name}: weights not saved: ${(e as Error).message}`, true));
    }
    this.shading();
  }

  /** A mask per loaded scan from f. */
  private each(f: (s: EditSession, sc: Scan) => Uint8Array | null): Map<string, Uint8Array> {
    const out = new Map<string, Uint8Array>();
    for (const [id, sc] of this.scans) { const m = f(this.selSpace(sc), sc); if (m) out.set(id, m); }
    return out;
  }

  /** Combine a primitive's masks into the selection with the current (or given) mode. */
  /** base: combine into that selection instead of the current one (still one undo step back to the current). */
  /** Intersecting with an empty selection replaces it instead. Backfaces culled in the view (Settings.cull, cullT) can't be selected. */
  private applyMasks(ms: Map<string, Uint8Array>, leaf: Record<string, unknown>, mode = this.mode, keepDeleted = false, base?: SelSnap) {
    const out = new Map<string, Uint8Array>(), wases = new Map<string, Uint8Array | null>(), c = this.v.cam, cull = this.v.s.cull;
    if (cull) leaf = { ...leaf, cull: true, cullT: this.v.s.cullT };
    for (const [id, sc] of this.scans) {
      const b = base ? base.masks.get(id) : null;
      wases.set(id, base ? (b ? unruns(b, sc.s.nv) : null) : this.held(sc));
    }
    if (mode === 'inter' && ![...wases.values()].some(w => w && sel.count(w))) mode = 'replace';   // intersecting nothing: start a new selection
    for (const [id, sc] of this.scans) {
      const m = ms.get(id) ?? new Uint8Array(sc.s.nv);
      if (!keepDeleted) { const live = this.live(sc); for (let i = 0; i < m.length; i++) m[i] &= live[i]; }
      if (cull) { const s = this.selSpace(sc), f = sel.facing(s.P, s.N, c.e, c.f, c.ortho, this.v.s.cullT); for (let i = 0; i < m.length; i++) m[i] &= f[i]; }
      out.set(id, sel.combine(mode, wases.get(id) ?? new Uint8Array(sc.s.nv), m));
    }
    const rule = base ? base.rule : this.rule;
    this.setSel(out, mode === 'replace' || !rule ? leaf : { op: mode, a: rule, b: leaf });
  }

  private syncMode() {
    for (const b of this.$('emode').querySelectorAll<HTMLElement>('button')) b.classList.toggle('on', b.dataset.m === this.mode);
  }

  /** Choose a toolbar entry: Brush or Grow becomes the pointer's tool, anything else leaves it the lasso; any entry shows its parameters. */
  pick(t: Entry | null) {
    this.entry = t;
    this.setTool(t && POINTER.includes(t) ? (t as Tool) : 'lasso');
    for (const b of document.querySelectorAll<HTMLElement>('#etb button')) b.classList.toggle('on', b.dataset.t === t);
    for (const r of document.querySelectorAll<HTMLElement>('#etools [data-p]')) r.classList.toggle('on', !!t && r.dataset.p!.split(' ').includes(t));
  }

  setTool(t: Tool) {
    this.tool = t;
    this.poly = [];
    this.drawPoly();
  }

  /** The mouse's keys: ctrl intersects, shift subtracts, alt adds; a touch (no keys): the mode buttons. */
  private modeFor(e: PointerEvent): sel.Combine { return e.ctrlKey ? 'inter' : e.shiftKey ? 'sub' : e.altKey ? 'add' : this.mode; }

  private pointer(e: PointerEvent, phase: 'down' | 'move' | 'up' | 'cancel'): boolean {
    if (phase === 'cancel') {   // a second finger: the gesture is navigation, not a stroke
      this.stroke = null;
      this.poly = [];
      for (const sc of this.scans.values()) sc.cand = null;
      this.drawPoly();
      this.refresh();
      return false;
    }
    if (!this.scans.size || (this.tool !== 'lasso' && this.tool !== 'brush')) return false;
    if (phase === 'down') {
      if (e.type === 'pointerdown') {   // a touch or pen (a mouse strokes with its keys, from a move)
        if (e.button !== 0) return false;
        if (this.ptAt(e.offsetX, e.offsetY) || this.camAt(e.offsetX, e.offsetY)) return false;   // a named point's cross or a camera: a tap opens its menu
      }
      const mode = this.modeFor(e);
      this.stroke = { mode, fresh: mode === 'inter' && !this.hasSel() };
      this.poly = [[e.offsetX, e.offsetY]];
      if (this.tool === 'brush') { for (const sc of this.scans.values()) sc.cand = new Uint8Array(sc.s.nv); this.stamp(e.offsetX, e.offsetY); }
      this.drawPoly();
      return true;
    }
    if (phase === 'move') {
      const last = this.poly[this.poly.length - 1];
      if (last && Math.hypot(e.offsetX - last[0], e.offsetY - last[1]) < 3) return true;
      this.poly.push([e.offsetX, e.offsetY]);
      if (this.tool === 'brush') this.stamp(e.offsetX, e.offsetY);
      this.drawPoly();
      return true;
    }
    // up
    const mode = this.stroke?.mode ?? this.mode, front = this.inp('efront').checked;
    this.stroke = null;
    // a click that never moved, on an instance box or with a menu open: a tap (opens / closes the menu), not a stroke
    if (e.type === 'pointerup' && this.poly.length === 1 && (this.boxMenu || this.ptMenu || this.viewMenu || this.boxAt(e.offsetX, e.offsetY))) {
      this.poly = [];
      this.drawPoly();
      if (this.tool === 'brush') { for (const sc of this.scans.values()) sc.cand = null; this.refresh(); }
      this.tap(e.offsetX, e.offsetY);
      return true;
    }
    if (this.tool === 'lasso' && this.poly.length >= 3 && this.tuneArm) {
      this.tuneStroke(this.tuneArm, this.lassoMasks(this.poly));
    } else if (this.tool === 'lasso' && this.poly.length >= 3) {
      this.applyMasks(this.lassoMasks(this.poly), { t: 'lasso', poly: this.poly.length, front }, mode);
    } else if (this.tool === 'brush') {
      const ms = new Map<string, Uint8Array>();
      for (const [id, sc] of this.scans) { if (sc.cand) ms.set(id, sc.cand); sc.cand = null; }
      this.applyMasks(ms, { t: 'brush', r: +this.inp('ebrushr').value, front }, mode);
    }
    this.poly = [];
    this.drawPoly();
    return true;
  }

  /** Points of every scan inside a screen polygon; front only: one depth image of all scans, so scans hide each other. */
  private lassoMasks(poly: [number, number][], front = this.inp('efront').checked) {
    const c = this.v.cam;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const [x, y] of poly) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
    const depth = front ? this.v.editDepth(x0 - 2, y0 - 2, x1 - x0 + 4, y1 - y0 + 4) : null;
    return this.each(s => sel.lasso(s.P, c.vp, c.view, c.w, c.h, poly, depth));
  }

  private stamp(x: number, y: number) {
    const r = Math.max(2, +this.inp('ebrushr').value || 24);
    for (const [id, m] of this.lassoMasks(sel.circle(x, y, r))) {
      const sc = this.scans.get(id)!, c = sc.cand!;
      for (let i = 0; i < m.length; i++) c[i] |= m[i];
      this.v.setEditState(id, sc.s.words(sc.mask, sc.cand, this.hiddenOf(sc), this.clsIx(sc)));
    }
    if (this.viewing) this.viewWords();
  }

  private drawPoly() {
    const p = this.$('elassop');
    p.classList.toggle('fresh', !!this.stroke?.fresh);
    if (this.tool === 'lasso' && this.poly.length > 1) p.setAttribute('d', 'M' + this.poly.map(q => q.join(' ')).join('L') + 'Z');
    else if (this.tool === 'brush' && this.poly.length) {
      const r = +this.inp('ebrushr').value || 24, [x, y] = this.poly[this.poly.length - 1];
      p.setAttribute('d', `M${x - r} ${y}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`);
    } else p.setAttribute('d', '');
  }

  /** Points and grids grow works in at radius r: where the instance viewer draws them, else placed. */
  private growSpace(r: number) {
    // in the instance viewer the points are where the overlay draws them (not shown: far away, so never reached)
    const V = (x: Scan) => (this.viewing ? x.view ?? null : null);
    const Pof = (x: Scan) => {
      const v = V(x);
      if (!v) return this.placed(x).P;
      if (!v.grid || v.grid.r !== r) { const P = Float32Array.from(v.P, q => (q === q ? q : 1e7)); v.grid = { g: new Grid(P, r), r, P }; }
      return v.grid.P;
    };
    const gridOf = (x: Scan) => { const v = V(x); if (v) { Pof(x); return v.grid!.g; } const xs = this.placed(x); if (!x.grid || x.grid.r !== r) x.grid = { g: new Grid(xs.P, r), r }; return x.grid.g; };
    return { V, Pof, gridOf };
  }

  private growR() { return Math.max(0.01, +this.inp('egr').value || 0.08); }

  /** Tap while picking a grow seed: grows over the tapped scan's surface. Returns true if it used the tap. */
  tap(x: number, y: number): boolean {
    if (this.cons.tap(x, y)) { this.closePtMenu(); this.closeBoxMenu(); this.closeCamMenu(); this.closeViewMenu(); return true; }
    const pt = this.ptAt(x, y);
    if (pt) { this.closeBoxMenu(); this.closeCamMenu(); this.openPtMenu(pt); return true; }
    if (this.ptMenu) { this.closePtMenu(); return true; }
    const cm = this.camAt(x, y);
    if (cm) { this.openCamMenu(cm); return true; }
    if (this.camMenu) { this.closeCamMenu(); return true; }
    const bx = this.tool === 'seed' ? null : this.boxAt(x, y);
    if (bx) { this.openBoxMenu(bx); return true; }
    if (this.boxMenu) { this.closeBoxMenu(); return true; }
    if (this.viewMenu) { this.closeViewMenu(); return true; }
    if (this.tool !== 'seed') return false;
    const h = this.v.pickAt(x, y), r = this.growR();
    const { Pof, gridOf } = this.growSpace(r);
    let sc = h?.chunk.edit ? this.scans.get(h.chunk.cloud.meta.id) : undefined;
    if (h && this.viewing && h.chunk.cloud.meta.id === OVERLAY) {   // the overlay: the scan with a shown point nearest the tap
      let best = Infinity;
      for (const x of this.scans.values()) {
        if (!x.view) continue;
        const i = gridOf(x).nearest(h.p[0], h.p[1], h.p[2], Math.max(0.05, 2 * r)), P = Pof(x);
        const d = i < 0 ? Infinity : Math.hypot(P[3 * i] - h.p[0], P[3 * i + 1] - h.p[1], P[3 * i + 2] - h.p[2]);
        if (d < best) { best = d; sc = x; }
      }
    }
    if (!h || !sc) { this.say('tap a point of a scan being edited'); return true; }
    const seed = gridOf(sc).nearest(h.p[0], h.p[1], h.p[2], Math.max(0.05, 2 * r));
    if (seed < 0) { this.say('no scan point there'); return true; }
    this.grow(sc, seed, this.mode);
    return true;
  }

  /** Apply: grow again from the last tapped seed with the current settings, in place of that grow's result. */
  private regrow() {
    const g = this.lastGrow, sc = g && this.scans.get(g.scan);
    if (!g || !sc || g.seed >= sc.s.nv) { this.say('tap the cloud first to pick a seed'); return; }
    if (this.growing) return;
    // the selection is still that grow's: redo it from what was there before; changed since: combine into what is there now
    this.grow(sc, g.seed, g.mode, this.rule === g.rule ? g.before : undefined);
  }

  private grow(sc: Scan, seed: number, mode: sel.Combine, base?: SelSnap) {
    const r = this.growR(), { V, Pof, gridOf } = this.growSpace(r);
    const s = this.placed(sc);
    const deg = +this.inp('egdeg').value || 25;
    const color = this.inp('egcol').checked
      ? { tol: Math.max(1, +this.inp('egctol').value || 40), ref: (this.$('egcref') as HTMLSelectElement).value as 'seed' | 'neighbour' } : undefined;
    const use = this.inp('egall').checked ? [...this.scans.values()] : [sc];
    if (use.length > 1) this.say('growing… (indexing the other scans the first time)');
    const clouds = use.map(x => ({ P: Pof(x), N: V(x)?.N ?? x.s.N, rgb: x.s.rgb, allowed: this.live(x), grid: gridOf(x) }));
    const g = sel.growMany(clouds, use.indexOf(sc), seed, r, deg, color);
    this.growing = true;
    const step = () => {
      const t0 = performance.now();
      for (;;) {
        const it = g.next();
        if (it.done) {
          this.growing = false;
          const before = base ?? this.snapSel();
          this.applyMasks(new Map(use.map((x, k) => [x.s.scan, it.value[k]])),
            { t: 'grow', scan: s.scan, seed: s.pidOfV[seed], r, deg, ...(use.length > 1 ? { scans: use.length } : {}), ...(color ? { color } : {}) }, mode, false, base);
          this.lastGrow = { scan: sc.s.scan, seed, mode, before, rule: this.rule };
          return;
        }
        if (performance.now() - t0 > 12) { this.say(`growing… ${it.value} points`); requestAnimationFrame(step); return; }
      }
    };
    step();
  }

  // Each rule takes its parameters (a macro step's, or the toolbar's: see toolbarParams) and a mode, and
  // says whether it selected (false: it said why not, and the selection is as it was).
  private applyVolume(p: Params = this.toolbarParams('box'), mode = this.mode) { return this.runRule('box', p, mode); }
  private applyNormal(p: Params = this.toolbarParams('normal'), mode = this.mode) { return this.runRule('normal', p, mode); }
  /** The floor, or the plane fitted last (the toolbar's Fit; a macro's band fits the selection as it is at that step). */
  private applyBand(p: Params = this.toolbarParams('band'), mode = this.mode) { return this.runRule('band', p, mode); }
  /** Points captured within a distance band of their frame's camera, in every scan that recorded it. */
  private applyDistance(p: Params = this.toolbarParams('distance'), mode = this.mode) { return this.runRule('distance', p, mode); }
  /** Points by the edges of the depth image they came from (Sobel strength, Canny), in every scan that has them. */
  private applyEdges(p: Params = this.toolbarParams('edges'), mode = this.mode) { return this.runRule('edges', p, mode); }
  /** Points by the hue, saturation and value of their colour. */
  private applyColour(p: Params = this.toolbarParams('colour'), mode = this.mode) { return this.runRule('colour', p, mode); }
  /** Points by the dot product of their normal with the direction to the eye of the current view. */
  private applyView(p: Params = this.toolbarParams('view'), mode = this.mode) { return this.runRule('view', p, mode); }
  /** A frame range, in every scan that has frame ids (each scan numbers its own frames). */
  private applyFrames(p: Params = this.toolbarParams('frames'), mode = this.mode) { return this.runRule('frames', p, mode); }
  /**
   * Frames with fewer than N named points that tie them to another frame (a name in one frame alone pulls
   * nothing), in every scan that has frame ids: the frames Solve points holds loosely, or not at all.
   */
  private applyFewPoints(p: Params = this.toolbarParams('few'), mode = this.mode) { return this.runRule('few', p, mode); }
  private applySourceKind(k: string, mode = this.mode) { return this.runRule('points', { src: k }, mode); }

  /**
   * Points by their density: how many points (of the ticked scans, deleted ones aside) are within r of each, from lo
   * to hi, once the measurement is in (the worker pool, unless the points are as last measured).
   */
  private applyDensity(p: Params = this.toolbarParams('density'), mode = this.mode) {
    this.say('measuring point density…');
    this.densityOf(+p.r).then(res => this.runRule('density', p, mode, res),
      e => { const m = (e as Error).message; if (m !== 'replaced') this.say(m === 'cancelled' ? 'density select cancelled' : `point density: ${m}`, m !== 'cancelled'); });
    return true;
  }

  /** Fit a plane to the selected points of every scan. */
  private fitPlane() {
    const pts: number[] = [];
    for (const sc of this.scans.values()) {
      if (!sc.mask) continue;
      const s = this.selSpace(sc);
      for (let i = 0; i < sc.mask.length; i++) if (sc.mask[i]) pts.push(s.P[3 * i], s.P[3 * i + 1], s.P[3 * i + 2]);
    }
    const p = sel.fitPlane(new Float32Array(pts), new Uint8Array(pts.length / 3).fill(1));
    if (!p) { this.say('select at least 3 points of the plane first'); return false; }
    this.plane = p;
    (this.$('ebplane') as HTMLSelectElement).value = 'fit';
    this.say(`plane n=(${p.slice(0, 3).map(x => x.toFixed(3)).join(', ')}), ${(-p[3]).toFixed(3)} m from the origin`);
    return true;
  }

  /** The Colour bands set to the colours of the selected points. */
  private pickColour() {
    const rgb: number[] = [];
    for (const sc of this.scans.values()) if (sc.mask) for (let i = 0; i < sc.mask.length; i++)
      if (sc.mask[i]) rgb.push(sc.s.rgb[3 * i], sc.s.rgb[3 * i + 1], sc.s.rgb[3 * i + 2]);
    const b = sel.hsvBand(new Uint8Array(rgb), new Uint8Array(rgb.length / 3).fill(1));
    if (!b) { this.say('select some points of the colour first'); return; }
    const put = (id: string, v: number, dp: number) => { this.inp(id).value = String(+v.toFixed(dp)); };
    put('ech0', Math.floor(b.h0), 0); put('ech1', Math.ceil(b.h1), 0);
    put('ecs0', Math.floor(b.s0 * 100) / 100, 2); put('ecs1', Math.ceil(b.s1 * 100) / 100, 2);
    put('ecv0', Math.floor(b.v0 * 100) / 100, 2); put('ecv1', Math.ceil(b.v1 * 100) / 100, 2);
    this.drawSwatch();
    this.say(`colour of ${(rgb.length / 3).toLocaleString()} points: hue ${this.inp('ech0').value} to ${this.inp('ech1').value}°, sat ${this.inp('ecs0').value} to ${this.inp('ecs1').value}, value ${this.inp('ecv0').value} to ${this.inp('ecv1').value}`);
  }

  /** The Colour band as a strip: its hues across, at the middle of its saturation and value. */
  private drawSwatch() {
    const b = colourBand(this.toolbarParams('colour')), s = (b.s0 + b.s1) / 2, v = (b.v0 + b.v1) / 2;
    const span = sel.allHues(b) ? 360 : (((b.h1 - b.h0) % 360) + 360) % 360, stops: string[] = [];
    for (let k = 0; k <= 8; k++) {
      const h = (b.h0 + (span * k) / 8) % 360, f = (n: number) => { const x = (n + h / 60) % 6; return Math.round(255 * v * (1 - s * Math.max(0, Math.min(x, 4 - x, 1)))); };
      stops.push(`rgb(${f(5)},${f(3)},${f(1)})`);
    }
    Object.assign((this.$('ecswatch') as HTMLElement).style, { display: 'inline-block', width: '5em', height: '0.9em', verticalAlign: 'middle', border: '1px solid #888', background: `linear-gradient(to right, ${stops.join(',')})` });
  }

  /** Under the Density rule: what the last measurement at its radius found. */
  private densHint(res: { r: number; range: [number, number] }) {
    this.$('edenhint').textContent = `measured: most points ${res.range[0].toLocaleString()} to ${res.range[1].toLocaleString()} within ${Math.round(res.r * 100)} cm`;
  }

  // ── the rules (macroeval.ts): the toolbar's here and now, a macro's in the macro worker ──
  /** A scan's points as the rules see them (selSpace: in the instance viewer, where the overlay draws them). */
  private cloudOf(sc: Scan): Cloud {
    const s = this.selSpace(sc);
    return { id: sc.s.scan, name: sc.s.meta.name, P: s.P, N: s.N, rgb: s.rgb, frame: s.frame, range: s.range, edge: s.edge, edgeSign: s.edgeSign };
  }

  /** The same for the macro worker, with a key that changes when its points do (they move in place). */
  private macroSource(sc: Scan): Source {
    const c = this.cloudOf(sc), id = (a: object | null) => (a ? idOf(a) : 0);
    return { id: c.id, key: `${[c.P, c.N, c.rgb, c.frame, c.range, c.edge].map(id).join(',')}|${whereP(c.P)}`, cloud: () => c };
  }

  /** What steps need of the page as it is now: per scan (in order) what is shown, the view, the sources they name. */
  private ruleCtx(steps: MacroStep[], scans: Scan[]): Ctx {
    const need = (t: StepKind) => steps.some(s => s.t === t || (s.t === 'tune' && !!s.p[t]));
    const dataSrc = (k: string) => steps.some(s => (s.t === 'dataNew' || s.t === 'dataOp') && s.p.src === k);
    const dens = need('density') || steps.some(s => dataDensR(s) !== null) ? this.densInput(0) : null, ties = new Map<Scan, Map<number, number>>();
    if (need('few')) for (const ms of this.namedPoints().values()) if (ms.length > 1) for (const m of ms) {
      const c = ties.get(m.sc) ?? ties.set(m.sc, new Map()).get(m.sc)!;
      c.set(m.f, (c.get(m.f) ?? 0) + 1);
    }
    const at: Held[] = scans.map(sc => ({
      live: this.live(sc), off: this.off(sc), cut: this.viewing && sc.view ? OVERLAY : sc.s.scan,
      keep: dens?.pts.find(x => x.s === sc.s)?.keep ?? null, ties: [ties.get(sc) ?? new Map()],
    }));
    const src: Record<string, Src> = {};
    for (const k of srcKeys(steps)) src[k] = this.srcOf(k, scans);
    steps.forEach((s, i) => {   // an Auto tune step's lists, by its place in the run
      if (s.t === 'tune') for (const l of ['req', 'deny'] as const) src[tuneKey(i, l)] = { m: scans.map(sc => this.tuneMask(s, sc, l)) };
    });
    const c = this.v.cam, vs = this.v.s, { volumes, volShow } = vs;
    return {
      host: 'edit', at, vols: vs.volumes, cam: { e: [...c.e], f: [...c.f], ortho: c.ortho }, cull: vs.cull ? vs.cullT : null,
      cuts: { volumes, volShow }, floorZ: this.v.floorZ, plane: this.plane, src,
      densKey: dens ? dens.key.slice(dens.key.indexOf('|') + 1) : '', dens: this.densCache && this.evalDens(this.densCache, scans),
      data: dataSets(scans.map(sc => this.dataOf(sc.s.scan))), ...(dataSrc('weight') ? { weights: scans.map(sc => sc.s.weight.slice()) } : {}),
    };
  }

  /** A Points source's masks per scan: all, a class, objects / a type / an object, parts / a part, a group, deleted, a scan. */
  private srcOf(k: string, scans: Scan[]): Src {
    const i = k.indexOf(':'), kind = i < 0 ? k : k.slice(0, i), arg = i < 0 ? '' : k.slice(i + 1);
    const cls = (c: number) => (st: State, p: number) => !st.obj[p] && st.flags[p] >> 1 === c;   // objects are their own kind
    const f: Record<string, (s: EditSession) => Uint8Array> = {
      all: s => s.where(() => true),
      floor: s => s.where(cls(1)), solid: s => s.where(cls(2)), movable: s => s.where(cls(3)), unclassified: s => s.where(cls(0)),
      class: s => { const id = [...s.state.classes].find(([, c]) => c.name === arg)?.[0]; return id === undefined ? new Uint8Array(s.nv) : s.where(cls(id)); },
      objects: s => s.where((st, p) => st.obj[p] > 0),
      type: s => { const ids = new Set([...s.state.objects].filter(([, o]) => o.of === arg).map(([id]) => id)); return s.where((st, p) => ids.has(st.obj[p])); },
      object: s => { const ids = new Set(idsNamed(s.state.objects, arg)); return s.where((st, p) => ids.has(st.obj[p])); },
      parts: s => s.where((st, p) => st.part[p] > 0),
      part: s => { const ids = new Set(idsNamed(s.state.parts, arg)); return s.where((st, p) => ids.has(st.part[p])); },
      group: s => this.groupMask(s, arg),
      deleted: s => s.where((st, p) => (st.flags[p] & DELETED) !== 0),
      scan: s => s.where(() => s.scan === arg),
    };
    if (!f[kind]) return { err: `no such points: ${k}` };
    return { m: scans.map(sc => f[kind](this.placed(sc))), keepDeleted: kind === 'deleted' };
  }

  /** A density measurement as the rules have it (per scan in order) and as the page does (by scan id). */
  private evalDens(d: Dens, scans: Scan[]): EvDens { return { key: d.key, r: d.r, step: d.step, d: scans.map(sc => d.d.get(sc.s.scan) ?? null), range: d.range }; }
  private editDens(d: EvDens, scans: Scan[]): Dens {
    return { key: d.key, r: d.r, step: d.step, d: new Map(scans.flatMap((sc, k) => (d.d[k] ? [[sc.s.scan, d.d[k]!] as const] : []))), range: d.range };
  }

  /** A rule (or plain button) from the toolbar, now; false: it couldn't (and said why). dens: the measurement a Density rule waited for. */
  private runRule(t: StepKind, p: Params, mode: sel.Combine = this.mode, dens?: Dens): boolean {
    const scans = [...this.scans.values()], s: MacroStep = { t, p, mode }, ctx = this.ruleCtx([s], scans);
    if (dens) ctx.dens = this.evalDens(dens, scans);
    const out = evalStep(scans.map(sc => this.cloudOf(sc)), scans.map(sc => this.held(sc) ?? new Uint8Array(sc.s.nv)), ctx, s);
    if (!out.ok) { this.say(out.say ?? ''); return false; }
    if (out.changed) { this.step({ k: 'sel', snap: this.snapSel() }); this.future = []; }
    this.landed(out, scans);
    if (out.changed) this.refresh();
    return true;
  }

  /** A step's outcome on the page (no undo step, no redraw): the selection, its rule, a fitted plane, a measurement, what it says. */
  private landed(out: Out, scans: Scan[]) {
    if (out.changed) {
      scans.forEach((sc, k) => { if (this.scans.get(sc.s.scan) === sc) this.hold(sc, out.sel[k].indexOf(1) >= 0 ? out.sel[k] : null); });
      const r = out.rule, was = this.rule;
      this.rule = !this.hasHeld() || !r || 'clear' in r ? null : 'wrap' in r ? { ...r.wrap, of: was } : r.mode === 'replace' || !was ? r.leaf : { op: r.mode, a: was, b: r.leaf };
    }
    if (out.plane) { this.plane = out.plane; (this.$('ebplane') as HTMLSelectElement).value = 'fit'; }
    if (out.dens) this.densCache = this.editDens(out.dens, scans);
    if (out.densRange) this.densHint(out.densRange);
    const data = out.data && Object.fromEntries(Object.entries(out.data).filter(([n]) => !isTemp(n)));   // a node graph's temporary ones: the run's only
    if (data && Object.keys(data).length) {
      scans.forEach((sc, k) => { const g = dataLanded(this.dataOf(sc.s.scan), data, k); if (g) this.data.set(sc.s.scan, g); });
      if (this.dataShow.name === '') this.dataShow.name = Object.entries(data).find(([, d]) => d)?.[0] ?? '';   // the first one made: shown
    }
    if (out.say) this.say(out.say, out.warn);
  }

  // ── macros ────────────────────────────────────────────────────────────
  /** A rule's parameters as the toolbar has them (its Apply buttons; new macro steps start from them). */
  /** A rule's parameters as the toolbar has them, its invert box among them. */
  private toolbarParams(t: StepKind): Params {
    const p = this.toolbarParamsRaw(t);
    if (!invertible(t)) return p;
    const box = this.$('etools').querySelector<HTMLInputElement>(`[data-p~="${t === 'few' ? 'frames' : t}"] .einv`);
    return { ...p, inv: !!box?.checked };
  }

  private toolbarParamsRaw(t: StepKind): Params {
    const n = (id: string) => +this.inp(id).value, v = (id: string) => (this.$(id) as HTMLSelectElement).value, c = (id: string) => this.inp(id).checked;
    switch (t) {
      case 'box': return { vol: v('evol') };
      case 'normal': return { axis: v('enaxis'), deg: n('endeg') };
      case 'band': return { plane: v('ebplane'), lo: n('eblo'), hi: n('ebhi') };
      case 'distance': return { lo: n('edlo'), hi: n('edhi') };
      case 'edges': return { side: v('eeside'), lo: n('eelo'), hi: n('eehi'), canny: v('eecanny') };
      case 'view': return { at: n('evat'), spread: n('evspread') };
      case 'density': return { r: v('edenr'), lo: n('edenlo'), hi: n('edenhi') };
      case 'colour': return { h0: n('ech0'), h1: n('ech1'), s0: n('ecs0'), s1: n('ecs1'), v0: n('ecv0'), v1: n('ecv1') };
      case 'frames': return { a: n('efa'), b: n('efb') };
      case 'few': return { n: n('efpn') };
      case 'erode': case 'dilate': return { r: n('emorphr') };
      case 'points': return { src: v('esrc') };
      case 'group': case 'groupEdit': return { name: this.groupNames()[0] ?? 'group', act: 'add' };
      case 'groupSave': return { name: [...new Set([...this.mem.values()].flatMap(g => [...g.keys()]))].sort()[0] ?? 'group', as: '' };
      case 'macro': return { name: this.macros.find(x => x.name !== this.macro.name.trim())?.name ?? '' };
      case 'weightBlur': return { r: 3 };
      case 'dataBand': return { name: this.dataShow.name || 'data', lo: n('datalo') || 0, hi: n('datahi') || 0, nulls: false };
      case 'dataNew': case 'dataOp': case 'dataEdit': return { name: this.dataShow.name || 'data' };
      default: return {};
    }
  }

  private wireMacros() {
    const on = (id: string, f: () => unknown) => { this.$(id).onclick = () => { f(); }; };
    on('emacopen', () => this.openMacros());
    const add = this.$('emacadd') as HTMLSelectElement;
    for (const [t, k] of Object.entries(KINDS)) if (!(k as Kind).hidden) add.append(el('option', { value: t, title: k.title }, k.label));
    on('emacaddgo', () => {
      const t = add.value as StepKind;
      this.macro.steps.push(newStep(t, this.toolbarParams(t), this.macro.steps.length ? 'inter' : 'replace'));
      this.macroBad = -1;
      this.drawMacro();
    });
    (this.$('emacsel') as HTMLSelectElement).onchange = e => {
      const nm = (e.target as HTMLSelectElement).value, m = this.macros.find(x => x.name === nm);
      if (m?.graph) { this.openNodes(m); (e.target as HTMLSelectElement).value = this.macros.some(x => x.name === this.macro.name.trim()) ? this.macro.name.trim() : ''; return; }
      this.macro = m ? structuredClone(m) : { name: '', steps: [] };
      this.macroBad = -1;
      this.tuneLive.clear();
      const d = this.droppedOf(nm);
      this.macroSay(d, !!d);
      this.drawMacro();
    };
    this.inp('emacname').oninput = () => { this.macro.name = this.inp('emacname').value; this.drawMacro(false); };
    on('emacsave', () => this.saveMacro());
    on('emacdel', () => this.dropMacro());
    on('emacrun', () => void this.runMacro());
    // the Tools tab's Auto tune (no macro's), and the View's Lasso / Auto tune group on the same lists
    on('etunerun', () => this.runSoloTune());
    on('etunefin', () => { if (this.soloRunning) this.macroFinish?.(); });
    const clearTune = () => {
      this.soloTune.tune = { sets: {} };
      this.soloLive = undefined;
      if (this.soloRunning) this.macroStop?.();
      this.drawTuneTools();
      this.say('Auto tune: its require and deny lists are empty (the selection is as it was)');
    };
    on('etuneclr', clearTune);
    on('vtuneclr', clearTune);
    on('etunemac', () => this.tuneToMacro());
    for (const b of this.$('vtune').querySelectorAll<HTMLElement>('button')) b.onclick = () => this.viewTune(b.dataset.v === 'tune');
    for (const b of this.$('vtunebtns').querySelectorAll<HTMLElement>('button[data-act]')) b.onclick = () => this.armTune({ l: b.dataset.l as 'req' | 'deny', act: b.dataset.act as 'add' | 'remove' }, true);
    this.drawTuneTools();
    this.nodes = new NodeUI(this.$('enodes'), {
      macros: () => this.macros,
      save: async m => { this.macros = (await this.api.saveMacro(m.name, [], m.vars, m.graph)).macros; this.drawMacro(); this.drawMacroBar(); },
      drop: async name => { this.macros = (await this.api.dropMacro(name)).macros; this.drawMacro(); this.drawMacroBar(); },
      run: async (m, tap) => { const ok = await this.runMacro(m, {}, tap); return { ok, say: this.$('emacinfo').textContent ?? '' }; },
      field: (f, get, put, o) => this.macroField(f, get, put, o),
      toolbarParams: t => this.toolbarParams(t),
      openList: m => void this.openMacros(m),
    });
    this.pins = new MacroPins({ macros: () => this.macros, run: m => void this.runSaved(m), title: m => this.macroTitle(m), top: this.$('top'), view: this.$('viewport'), before: this.$('winbtn') });
    void this.loadMacros();
  }

  /** The Nodes tab on node graph m. */
  private openNodes(m: Macro) {
    this.showTab('nodes');   // first: the canvas has its size to fit the graph to
    this.nodes?.open(m);
  }

  /** What loading the saved macros dropped (steps there are no more), a line per macro; and what to say of macro name's. */
  private dropped: string[] = [];
  private droppedOf(name: string) {
    const d = this.dropped.find(x => x.startsWith(`${name}: `));
    return d ? `steps dropped on loading (saving it saves it without them): ${d.slice(name.length + 2)}` : '';
  }

  /** The saved macros, for the bar under the header (and the dialog). */
  private async loadMacros() {
    try {
      const r = await this.api.macros();
      this.macros = r.macros; this.dropped = r.dropped;
      if (r.dropped.length) this.say(`saved macros: steps dropped (saving the macro saves it without them): ${r.dropped.join('; ')}`, true);
    } catch (e) { console.warn('macros', e); return false; }
    this.drawMacroBar();
    this.nodes?.refresh();
    return true;
  }

  /** The Saved macros tab: each a button that runs it, and ✎ to edit it in a window. */
  private drawMacroBar() {
    const bar = this.$('emacbar');
    bar.textContent = '';
    for (const m of this.macros) {
      const b = el('span', { class: 'emacb' });
      const run = el('button', { class: 'run' + (m.graph ? ' graph' : ''), title: `${this.macroTitle(m)}\n(drag it to the header, the View or a panel to pin it there)` }, m.name) as HTMLButtonElement;
      run.disabled = !runSize(m);
      run.onclick = () => this.runSaved(m);
      run.onpointerdown = e => this.pins?.startDrag(e, m.name);
      const ed = el('button', { class: 'ed', title: `edit ${m.name}${m.graph ? ' in the Nodes tab' : ' in a window'}` }, '✎');
      ed.onclick = () => void (m.graph ? this.openNodes(m) : this.openMacros(m, true));
      b.append(run, ed);
      bar.append(b);
    }
    if (!this.macros.length) bar.append(el('div', { class: 'help' }, 'No saved macros yet: build one in the Macro editor and Save it.'));

    this.pins?.draw();
  }

  /** A saved macro's button's tooltip: what it runs. */
  private macroTitle(m: Macro) {
    const ran = runSize(m), edits = m.graph ? m.graph.nodes.some(n => n.t === 'macro' || n.t === 'subgraph' || n.t === 'blobs' || nodeEdits(n.t)) : m.steps.some(s => !s.off && (editsOf(s) || s.t === 'macro'));
    const vars = Object.entries(m.vars ?? {}).map(([n, v]) => `<${n}> = ${v}`).join(', ');
    const what = m.graph ? `${ran} node${ran === 1 ? '' : 's'}` : `${ran} step${ran === 1 ? '' : 's'}`, list = m.graph ? 'a node graph (the Nodes tab)' : m.steps.filter(s => !s.off).map(describe).join('\n');
    return `run ${m.name}: ${what} (one Undo${edits ? ', its edits too' : ''})\n${list}${vars ? `\nvariables: ${vars}` : ''}`;
  }

  /** A saved macro from the bar (with variables: their values asked for first); stopped at a step, its dialog opens there with why. */
  private async runSaved(m: Macro) {
    const env = await askVars(m, this.macros, (f, get, put, say) => this.macroField(f, get, put, { redraw: () => {}, say }));
    if (!env) return;
    this.macroBad = -1;
    if (await this.runMacro(m, env)) return;
    if (this.macroBad < 0 || m.graph) { this.say(this.$('emacinfo').textContent ?? '', true); return; }   // busy, or nothing to run on; a graph: says which node
    this.macro = structuredClone(m);
    this.tuneLive.clear();
    this.showTab('macroedit');
    this.drawMacro();
  }

  /** Mark the step running in the dialog's list. */
  private markMacroStep() {
    this.$('emacsteps').querySelectorAll(':scope > .emstep').forEach((r, i) => r.classList.toggle('run', i === this.macroAt));
  }

  private macroSay(msg: string, warn = false) {
    const i = this.$('emacinfo');
    i.textContent = msg;
    i.classList.toggle('warn', warn);
  }

  /** The dialog; m: on that saved macro (the bar's ✎); win: in a movable window rather than its tab. */
  private async openMacros(m?: Macro, win = false) {
    if (m) { this.macro = structuredClone(m); this.macroBad = -1; this.tuneLive.clear(); }
    if (win) this.popOut('macroedit', `Edit macro: ${this.macro.name || '(new macro)'}`); else this.showTab('macroedit');
    this.macroSay('');
    this.drawMacro();
    try { const r = await this.api.macros(); this.macros = r.macros; this.dropped = r.dropped; const d = this.droppedOf(this.macro.name); if (d) this.macroSay(d, true); } catch (e) { this.macroSay(`can't read the saved macros: ${(e as Error).message}`, true); }
    this.drawMacro();
    this.drawMacroBar();
  }

  /** The dialog from this.macro; steps: redraw the step list too (not while typing a name). */
  private drawMacro(steps = true) {
    const m = this.macro, name = m.name.trim(), saved = this.macros.find(x => x.name === name);
    const ms = this.$('emacsel') as HTMLSelectElement;
    ms.textContent = '';
    ms.append(el('option', { value: '' }, '(new macro)'));
    for (const x of this.macros) ms.append(el('option', { value: x.name, ...(x.graph ? { title: 'a node graph: opens in the Nodes tab' } : {}) }, x.graph ? `${x.name} (nodes)` : x.name));
    ms.value = saved ? name : '';
    if (this.inp('emacname').value !== m.name) this.inp('emacname').value = m.name;
    this.drawMacroVars();
    const dirty = !saved || JSON.stringify(saved.steps) !== JSON.stringify(m.steps) || JSON.stringify(saved.vars ?? {}) !== JSON.stringify(m.vars ?? {});
    this.$('emacsave').classList.toggle('on', dirty && !!name && m.steps.length > 0);
    this.$('emacsave').title = saved ? (dirty ? `save these steps over the saved ${name}` : 'saved') : 'save these steps under this name (for this scene)';
    (this.$('emacdel') as HTMLButtonElement).disabled = !saved;
    (this.$('emacrun') as HTMLButtonElement).disabled = !m.steps.some(s => !s.off);
    if (!steps) return;
    const box = this.$('emacsteps');
    box.textContent = '';
    if (!m.steps.length) { box.append(el('div', { class: 'help' }, 'no steps yet: pick a selection below and Add step')); return; }
    this.drawSteps(m.steps, box, true);
  }

  /** Rows for a list of steps: the macro's own (top: marked running / stopped there), or a For each step's, with theirs inside. */
  private drawSteps(list: MacroStep[], box: HTMLElement, top: boolean) {
    list.forEach((s, i) => {
      const k = KINDS[s.t], row = el('div', { class: 'emstep' + (top && i === this.macroBad ? ' bad' : '') + (top && i === this.macroAt ? ' run' : '') + (s.off ? ' off' : ''), title: k.title });
      const on = el('input', { type: 'checkbox', title: 'run this step (untick to skip it for now)' }) as HTMLInputElement;
      on.checked = !s.off;
      on.onchange = () => { if (on.checked) delete s.off; else s.off = true; row.classList.toggle('off', !!s.off); this.macroBad = -1; row.classList.remove('bad'); this.drawMacro(false); };
      const n = el('label', { class: 'n' });
      n.append(on, String(i + 1));
      row.append(n);
      if (s.mode) {
        const md = el('select', { title: 'how this step combines with the selection as it is by then' }) as HTMLSelectElement;
        for (const [v, t] of MODES) md.append(el('option', { value: v }, t));
        md.value = s.mode;
        md.onchange = () => { s.mode = md.value as sel.Combine; this.drawMacro(false); };
        row.append(md);
      } else row.append(el('span'));
      const ps = el('div', { class: 'ps' });
      ps.append(el('b', {}, k.label));
      for (const f of k.fields) if (fieldShown(f, s.p)) ps.append(this.macroField(f, () => s.p[f.key], v => { s.p[f.key] = v; }, { vars: true }));
      row.append(ps);
      const mv = el('div', { class: 'mv' });
      const btn = (t: string, title: string, f: () => void, off = false) => {
        const b = el('button', { class: 'small', title }, t) as HTMLButtonElement;
        b.disabled = off;
        b.onclick = () => { f(); this.macroBad = -1; this.tuneLive.clear(); this.drawMacro(); };
        mv.append(b);
      };
      const swap = (j: number) => { [list[i], list[j]] = [list[j], list[i]]; };
      btn('↑', 'move up', () => swap(i - 1), i === 0);
      btn('↓', 'move down', () => swap(i + 1), i === list.length - 1);
      btn('✕', 'remove this step', () => list.splice(i, 1));
      row.append(mv);
      if (s.t === 'tune' && top) row.append(this.tuneRow(s, i));
      if (s.t === 'macro') { const a = this.argsRow(s); if (a) row.append(a); }
      if (s.t === 'blobs') row.append(this.blobsRow(s));
      box.append(row);
    });
  }

  /** A For each step's own steps (run once a time round), and a picker to add one (any but Auto tune). */
  private blobsRow(s: MacroStep): HTMLElement {
    const steps = (s.steps ??= []), d = el('div', { class: 'emsub' }), each = s.p.over === 'count' ? 'each time round' : `each ${s.p.over === 'frames' ? 'frame' : s.p.over === 'scans' ? 'scan' : 'blob'}`;
    if (steps.length) this.drawSteps(steps, d, false);
    else d.append(el('div', { class: 'help' }, `no steps inside: it selects what it goes over (${s.p.over === 'blobs' ? 'every blob that size' : s.p.over === 'count' ? 'the selection as it is' : `every ${s.p.over === 'frames' ? 'frame' : 'scan'}'s points`}); steps added run ${each}${s.p.over === 'count' ? '' : ' (its points as the selection)'}, <${s.p.var}> in their inputs its ${s.p.over === 'frames' ? 'frame number' : 'number, from 1'}`));
    const add = el('select', { title: `a step to run ${each}` }) as HTMLSelectElement;
    for (const [t, k] of Object.entries(KINDS)) if (t !== 'tune' && !(k as Kind).hidden) add.append(el('option', { value: t, title: k.title }, k.label));
    const go = el('button', { class: 'small' }, 'Add step inside') as HTMLButtonElement;
    go.onclick = () => {
      const t = add.value as StepKind;
      steps.push(newStep(t, this.toolbarParams(t), steps.length ? 'inter' : 'replace'));
      this.macroBad = -1;
      this.drawMacro();
    };
    const bar = el('div', { class: 'emsubadd' });
    bar.append(add, go);
    d.append(bar);
    return d;
  }

  /**
   * The dialog's variables: a row for each variable the macro's steps use (<name>, in a step's input or passed to a
   * Macro step's macro), with its default value, the input as the first input it goes to has it; marked when the
   * default doesn't fit every input it goes to. Defaults of variables no longer used go.
   */
  private drawMacroVars() {
    const m = this.macro, uses = varsOf(m, this.macros), box = this.$('emacvars'), vars: Params = {};
    for (const [n, fs] of uses) { const v = m.vars?.[n] ?? fs[0]?.dflt; if (v !== undefined) vars[n] = v; }
    if (Object.keys(vars).length) m.vars = vars; else delete m.vars;
    const dl = this.$('emacvarnames');
    dl.textContent = '';
    for (const n of new Set([...uses.keys(), ...allSteps(m.steps).filter(s => s.t === 'blobs').map(s => String(s.p.var))])) dl.append(el('option', { value: `<${n}>` }));   // For each's too
    box.textContent = '';
    box.hidden = !uses.size;
    if (!uses.size) return;
    box.append(el('div', { class: 'help' }, 'variables: their values when it runs (a Macro step running this macro can pass others)'));
    for (const [n, fs] of uses) {
      const f = fs[0] ?? anyField(n), bad = fs.filter(g => fieldValue(g, vars[n]) === undefined);
      const row = el('div', { class: 'emvar' + (bad.length ? ' bad' : ''), title: bad.length ? `${n} is ${JSON.stringify(vars[n])}: ${bad.map(g => `${g.label || g.key} ${takes(g)}`).join('; ')}` : `<${n}>: ${fs.length} input${fs.length === 1 ? '' : 's'}` });
      row.append(el('b', {}, `<${n}>`), this.macroField({ ...f, label: '' }, () => vars[n], v => { (m.vars ??= {})[n] = v; }));
      box.append(row);
    }
  }

  /**
   * A Macro step's values for its macro's variables: each an input (a value, or <name>: this macro's variable passed
   * on); one not set shows that macro's default, ↺ puts one set back to it. Values for variables it no longer has go.
   */
  private argsRow(s: MacroStep): HTMLElement | null {
    const name = String(s.p.name), d = el('div', { class: 'emargs' });
    if (varOf(name)) { d.append(el('span', { class: 'help' }, `the macro is ${name}'s: its variables keep their defaults`)); return d; }
    const sub = this.macros.find(x => x.name === name);
    if (!sub) return null;
    const uses = varsOf(sub, this.macros);
    if (s.args) { for (const n of Object.keys(s.args)) if (!uses.has(n)) delete s.args[n]; if (!Object.keys(s.args).length) delete s.args; }
    if (!uses.size) return null;
    d.append(el('span', { class: 'help' }, 'with'));
    for (const [n, fs] of uses) {
      const f = fs[0] ?? anyField(n), set = !!s.args && Object.hasOwn(s.args, n);
      const a = el('span', { class: 'emarg' + (set ? '' : ' dflt'), title: set ? `${n}: passed to ${name}` : `${n}: ${name}'s default` });
      a.append(this.macroField({ ...f, key: n, label: n }, () => s.args?.[n] ?? sub.vars?.[n] ?? f.dflt, v => { (s.args ??= {})[n] = v; }, { vars: true }));
      if (set) {
        const b = el('button', { class: 'small', title: `back to ${name}'s default` }, '↺');
        b.onclick = () => { delete s.args![n]; if (!Object.keys(s.args!).length) delete s.args; this.drawMacro(); };
        a.append(b);
      }
      d.append(a);
    }
    return d;
  }

  /**
   * An input for a step's parameter (or a variable's default, a value passed to a Macro step's macro): get reads it,
   * put writes it back when it is a value the input takes. vars: it may be a variable, <name> (‹› makes it one, ✕ a
   * value again).
   */
  private macroField(f: Field, get: () => unknown, put: (v: number | string | boolean) => void, o: { vars?: boolean; self?: string; redraw?: (steps: boolean) => void; say?: (msg: string, warn?: boolean) => void } = {}): HTMLElement {
    const cur = get(), what = f.label || f.key, redraw = o.redraw ?? ((steps: boolean) => this.drawMacro(steps)), say = o.say ?? ((m: string, w?: boolean) => this.macroSay(m, w));
    const set = (v: unknown) => {
      const x = o.vars ? paramValue(f, v) : fieldValue(f, v);
      if (x !== undefined) put(x);
      // its args, the variables; a data step's source: the fields for it
      if (f.k === 'macro' || f.k === 'var' || f.k === 'text' || (f.k === 'choice' && (f.key === 'src' || f.key === 'over')) || (x !== undefined && (!!exprOf(x) || !!exprOf(cur)))) redraw(true); else redraw(false);
      return x !== undefined;
    };
    const wrap = (inp: HTMLElement) => {
      const unit = f.k === 'num' ? f.unit : undefined;
      if (!f.label && !unit) return inp;
      const l = el('label', {}, f.label ? f.label + ' ' : '');
      l.append(inp);
      if (unit) l.append(unit === '°' ? unit : ' ' + unit);
      return l;
    };
    const toggle = (inp: HTMLElement, t: string, title: string, f2: () => void) => {
      const b = el('button', { class: 'small emvt', title }, t);
      b.onclick = () => { f2(); redraw(true); };
      const w = el('span', { class: 'emf' });
      w.append(inp, b);
      return w;
    };
    if (o.vars && exprOf(cur)) {   // a variable or math over them: its text, ✕ a value again
      const i = el('input', { type: 'text', class: 'emvin', style: 'width:8em', list: 'emacvarnames', title: `${what}: a variable, <name>, or math over them and numbers, <i+1>, <w*h> (+ - * / %, brackets)` }) as HTMLInputElement;
      i.value = String(cur);
      i.onchange = () => { const v = i.value.trim(); if (paramValue(f, v) === undefined || !exprOf(v)) { i.value = String(cur); say(`a variable, <name>, or math where a number goes, <i+1>, <w*h> (✕ to give ${what} a value)`, true); return; } set(v); };
      return toggle(wrap(i), '✕', `give ${what} a value, not a variable`, () => put(f.dflt));
    }
    const lit = (inp: HTMLElement, w = wrap) => {   // w: a checkbox has its label already
      if (!o.vars) return w(inp);
      const n = f.key.replace(/\W/g, '_').replace(/^(\d)/, '_$1');
      return toggle(w(inp), '‹›', `make ${what} a variable: <${n}> (a value for it in the variables at the top, or passed in by a Macro step)`, () => put(`<${n}>`));
    };
    if (f.k === 'bool') {
      const l = el('label', {}), c = el('input', { type: 'checkbox' }) as HTMLInputElement;
      c.checked = !!cur;
      c.onchange = () => set(c.checked);
      l.append(c, f.label ? ' ' + f.label : '');
      return lit(l, x => x);
    }
    if (f.k === 'var') {   // a For each's variable: its name (never itself a variable)
      const i = el('input', { type: 'text', class: 'emvin', style: 'width:5em', title: `${what}: the name its steps' inputs use, <name>` }) as HTMLInputElement;
      i.value = `<${cur}>`;
      i.onchange = () => { const v = i.value.trim().replace(/^<(.*)>$/, '$1'); if (!set(v)) { i.value = `<${cur}>`; say(`${what} ${takes(f)}`, true); } };
      return wrap(i);
    }
    if (f.k === 'num') {
      const i = el('input', { type: 'number', style: 'width:4.4em' }) as HTMLInputElement;
      if (f.min !== undefined) i.min = String(f.min);
      if (f.max !== undefined) i.max = String(f.max);
      i.step = String(f.step ?? 1);
      const show = (v: unknown) => (f.blank !== undefined && v === 0 ? '' : String(v));   // left empty: 0
      if (f.blank !== undefined) i.placeholder = f.blank;
      i.value = show(cur);
      i.onchange = () => { const ok = set(i.value === '' ? (f.blank !== undefined ? 0 : NaN) : +i.value); i.value = show(get()); if (!ok) say(`${what} ${takes(f)}${f.blank !== undefined ? ` (empty: ${f.blank})` : ''}`, true); };
      return lit(i);
    }
    if (f.k === 'text') {   // Data eval's expression: wide, checked when it runs
      const i = el('input', { type: 'text', class: 'emexpr', spellcheck: 'false', placeholder: f.placeholder ?? '', title: KINDS.dataEval.title }) as HTMLInputElement;
      i.value = String(cur);
      i.onchange = () => { if (!set(i.value.trim())) { i.value = String(get()); say(`${what} ${takes(f)}`, true); } };
      return wrap(i);
    }
    if (f.k === 'group' || f.k === 'data' || isAny(f)) {   // a name: any group, made when the step edits it and it isn't there (or <name>); any text
      const data = f.k === 'data', i = el('input', { type: 'text', style: 'width:8em', list: data ? 'emacdata' : 'emacgroups', placeholder: f.k === 'group' || data ? f.blank ?? (data ? 'data group' : 'group') : '' }) as HTMLInputElement;
      i.value = String(cur);
      i.onchange = () => { const ok = set(i.value.trim()); i.value = String(get()); if (!ok) say(`${what} ${takes(f)}${o.vars ? ', or a variable <name>' : ''}`, true); };
      const dl = this.$(data ? 'emacdata' : 'emacgroups');
      dl.textContent = '';
      for (const g of data ? this.dataNames() : this.groupNames()) dl.append(el('option', { value: g }));
      return lit(i);
    }
    const sl = el('select') as HTMLSelectElement;
    const opts: [string, string][] = f.k === 'choice' ? [...f.options]
      : f.k === 'macro' ? [['', f.graphs ? '(pick a graph)' : '(pick a macro)'], ...this.macros.filter(x => x.name !== (o.self ?? this.macro.name.trim()) && (!f.graphs || (x.graph && hasIo(x.graph)))).map(x => [x.name, x.name] as [string, string])]
      : f.k === 'vol' ? [['*', 'all enabled boxes'], ...this.v.s.volumes.map(v => [v.id, `${v.kind} · ${v.name || v.id}`] as [string, string])]
      : [...(f.key === 'from' ? [['sel', 'the selection (as it is at this step)']] as [string, string][] : []), ...[...(this.$('esrc') as HTMLSelectElement).options].map(o => [o.value, o.textContent ?? o.value] as [string, string])];
    if (!opts.some(([v]) => v === cur)) opts.push([String(cur), `${cur} (not here)`]);
    for (const [v, t] of opts) sl.append(el('option', { value: v }, t));
    sl.value = String(cur);
    sl.onchange = () => set(sl.value);
    return lit(sl);
  }

  private async saveMacro() {
    const name = this.macro.name.trim();
    if (!name) { this.macroSay('name it first', true); this.inp('emacname').focus(); return; }
    if (!this.macro.steps.length) { this.macroSay('add a step first', true); return; }
    try {
      this.macros = (await this.api.saveMacro(name, this.macro.steps, this.macro.vars)).macros;
      this.macro.name = name;
      this.macroSay(`saved ${name}`);
    } catch (e) { this.macroSay(`save: ${(e as Error).message}`, true); }
    this.drawMacro();
    this.drawMacroBar();
    this.nodes?.refresh();
  }

  private async dropMacro() {
    const name = this.macro.name.trim();
    if (!this.macros.some(x => x.name === name) || !confirm(`Delete the macro ${name}? (from this scene)`)) return;
    try {
      this.macros = (await this.api.dropMacro(name)).macros;
      this.macroSay(`deleted ${name}: its steps are still here until you pick another`);
    } catch (e) { this.macroSay(`delete: ${(e as Error).message}`, true); }
    this.drawMacro();
    this.drawMacroBar();
    this.nodes?.refresh();
  }

  // ── Auto tune: its lists (the selection put in or taken out), its row in the dialog, the toolbar's ──
  /** An Auto tune step's list in a scan as a vertex mask (null: none there). */
  private tuneMask(s: MacroStep, sc: Scan, l: 'req' | 'deny'): Uint8Array | null {
    const set = s.tune?.sets[sc.s.scan]?.[l];
    if (!set?.n) return null;
    let pids: Uint32Array;
    try { pids = decodeSet(set, sc.s.npid); } catch (e) { console.warn('tune list', e); return null; }
    const m = new Uint8Array(sc.s.nv), V = sc.s.vOfPid;
    for (const p of pids) { const v = V[p]; if (v >= 0) m[v] = 1; }
    return m;
  }

  /** The points of masks (per scan id; null: the selection) put in an Auto tune step's list l (and out of the other) or taken out of it: how many points that moved. */
  private tuneEdit(s: MacroStep, l: 'req' | 'deny', act: 'add' | 'remove', masks?: Map<string, Uint8Array>): number {
    const t = (s.tune ??= { sets: {} });
    let n = 0;
    for (const [id, sc] of this.scans) {
      const m = masks ? masks.get(id) : this.held(sc);
      if (!m || !sel.count(m)) continue;
      n += tuneListEdit(t, sc.s.scan, sc.s.npid, sc.s.pidOfV, m, l, act);
    }
    return n;
  }

  /** An Auto tune step's list as the selection (to see what is in it). */
  private tuneSelect(s: MacroStep, l: 'req' | 'deny') {
    const ms = new Map<string, Uint8Array>();
    for (const [id, sc] of this.scans) { const m = this.tuneMask(s, sc, l); if (m) ms.set(id, m); }
    this.setSel(ms, { t: 'tune list', list: l });
  }

  /**
   * Step s's lists: require and deny, each its count and + − select; after: redraw; say: where to say what happened.
   * arm: + and − arm the lasso (its strokes go in or out of the list, see tuneArm) instead of taking the selection.
   */
  private tuneLists(s: MacroStep, after: () => void, say: (msg: string, warn?: boolean) => void, arm = false): HTMLElement {
    const box = el('span', { class: 'etlists' }), c = tuneCounts(s);
    for (const [l, name, tip] of [['req', 'require', 'the points it must select'], ['deny', 'deny', 'the points it must not select']] as const) {
      const g = el('span', { class: 'etlist', title: `${name}: ${tip}${arm ? '' : ' (saved with the macro)'}` });
      g.append(`${name} `, el('b', {}, (l === 'req' ? c.req : c.deny).toLocaleString()), ' ');
      const b = (t: string, title: string, f: () => void, act?: 'add' | 'remove') => {
        const x = el('button', { class: 'small', title }, t);
        x.onclick = f;
        if (act) { x.dataset.l = l; x.dataset.act = act; }
        g.append(x);
      };
      if (arm) {
        b('+', `lasso points into the ${name} list (and out of the other); the tune runs again after each stroke (Esc: back to selecting)`, () => this.armTune({ l, act: 'add' }, true), 'add');
        b('−', `lasso points out of the ${name} list; the tune runs again after each stroke (Esc: back to selecting)`, () => this.armTune({ l, act: 'remove' }, true), 'remove');
      } else {
        b('+', `put the selection in the ${name} list (and out of the other)`, () => {
          const n = this.tuneEdit(s, l, 'add');
          say(n ? `${n.toLocaleString()} points put in ${name}` : 'nothing to put in: select some points first (or they are in it already)', !n);
          after();
        });
        b('−', `take the selection out of the ${name} list`, () => {
          const n = this.tuneEdit(s, l, 'remove');
          say(n ? `${n.toLocaleString()} points taken out of ${name}` : `none of the selection is in ${name}`, !n);
          after();
        });
      }
      b('select', `select the ${name} list's points (to see them)`, () => this.tuneSelect(s, l));
      box.append(g);
    }
    return box;
  }

  /** How step s went (or, with st, is going): what it found, as steps. */
  private tuneText(s: MacroStep, st?: TuneStat) {
    const steps = (b: MacroStep[]) => b.map(describe).join('  ') || 'every point let through';
    if (st) return `${sayStat(st)}\n${steps(st.best)}`;
    if (s.tune?.best) return `last best: ${steps(s.tune.best)}`;
    return s === this.soloTune ? 'not run yet: + then lasso the points it must select, − or deny + those it must not; it runs after each stroke' : 'not run yet: put points in its lists (select them, then +), then Run';
  }

  /** Step s's best as the steps it is: the first with its mode, the rest ∩ (All shown when it lets everything through). */
  private tuneSteps(s: MacroStep): MacroStep[] {
    // the rules ∩ one another, the first with its mode (All shown first when the first pass let everything through);
    // an erode / dilate after a pass is a plain step, on the selection as it is by then
    const best = structuredClone(s.tune?.best ?? []), mode = s.mode ?? 'replace';
    if (!best.length || !best[0].mode) best.unshift(newStep('all', {}, mode));
    return best.map((b, k) => (b.mode ? { ...b, mode: k ? 'inter' : mode } : b));
  }

  /** An Auto tune step's row in the dialog, under its parameters: its lists, how it went, → steps, Finish. */
  private tuneRow(s: MacroStep, i: number): HTMLElement {
    const d = el('div', { class: 'emtune' });
    d.append(this.tuneLists(s, () => this.drawMacro(), (m, w) => this.macroSay(m, w)));
    const flat = s.mode === 'replace' || s.mode === 'inter';
    const go = el('button', { class: 'small', title: flat ? 'put the rules it found in its place, as steps (to run without tuning, or change by hand)' : 'its mode (+ or −) can\'t be written out as steps ∩ one another: set it to New or ∩ first' }, '→ steps') as HTMLButtonElement;
    go.disabled = !s.tune?.best || !flat;
    go.onclick = () => { this.macro.steps.splice(i, 1, ...this.tuneSteps(s)); this.macroBad = -1; this.tuneLive.clear(); this.drawMacro(); };
    const fin = el('button', { class: 'small emtfin', title: 'end this Auto tune with the best it has found (the macro goes on)' }, 'Finish') as HTMLButtonElement;
    fin.hidden = this.macroAt !== i || !this.macroFinish;
    fin.onclick = () => this.macroFinish?.();
    const st = el('div', { class: 'emtstat mono' });
    st.textContent = this.tuneText(s, this.tuneLive.get(i));
    d.append(go, fin, st);
    d.dataset.i = String(i);
    return d;
  }

  /** How dialog step i (an Auto tune of the dialog's macro) is going: its row says so. */
  private tuneShow(i: number, stat: TuneStat) {
    this.tuneLive.set(i, stat);
    const s = this.macro.steps[i];
    if (!s) return;
    const row = this.$('emacsteps').querySelector<HTMLElement>(`.emtune[data-i="${i}"]`);
    if (row) {
      row.querySelector('.emtstat')!.textContent = this.tuneText(s, stat);
      row.querySelector<HTMLElement>('.emtfin')!.hidden = !this.macroFinish;
    }
  }

  /** How the Tools tab's Auto tune is going (done: it ran to its end, and keeps what it found as its best). */
  private soloShow(stat: TuneStat, done = false) {
    this.soloLive = stat;
    if (done) (this.soloTune.tune ??= { sets: {} }).best = stat.best;
    this.$('etunestat').textContent = this.tuneText(this.soloTune, stat);
    (this.$('etunefin') as HTMLButtonElement).disabled = !this.macroFinish;
  }

  /** An Auto tune step ran to its end: the dialog's macro keeps what it found (Save stores it). */
  private tuneDone(r: RunStep, mine: boolean, stat: TuneStat) {
    if (!mine || r.at.length !== 1) return;
    const s = this.macro.steps[r.at[0]];
    if (s?.t !== 'tune') return;
    (s.tune ??= { sets: {} }).best = stat.best;
    this.tuneShow(r.at[0], stat);
    this.drawMacro(false);
  }

  /** The Tools tab's Auto tune (soloTune, no macro's): its lists, settings, Run, Finish, Clear, → macro step, how it went; and the View's. */
  private drawTuneTools() {
    const s = this.soloTune, box = this.$('etunelists'), ps = this.$('etuneparams');
    box.textContent = '';
    box.append(this.tuneLists(s, () => this.drawTuneTools(), (m, w) => this.say(m, w), true));
    ps.textContent = '';
    for (const f of KINDS.tune.fields) if (fieldShown(f, s.p)) ps.append(this.macroField(f, () => s.p[f.key], v => { s.p[f.key] = v; }, { redraw: all => { if (all) this.drawTuneTools(); }, say: (m, w) => this.say(m, w) }));
    const c = tuneCounts(s), any = c.req + c.deny > 0;
    this.$('etunestat').textContent = this.tuneText(s, this.soloLive);
    (this.$('etunefin') as HTMLButtonElement).disabled = !(this.soloRunning && this.macroFinish);
    (this.$('etunerun') as HTMLButtonElement).disabled = !c.req;
    (this.$('etuneclr') as HTMLButtonElement).disabled = !any && !s.tune?.best;
    (this.$('etunemac') as HTMLButtonElement).disabled = !any;
    this.$('vtunen').textContent = `require ${c.req.toLocaleString()} · deny ${c.deny.toLocaleString()}`;
    this.syncTuneArm();
  }

  /** The View's Lasso / Auto tune group: Auto tune shows its list buttons and Clear; Lasso hides them (and selects again). */
  private viewTune(on: boolean) {
    for (const b of this.$('vtune').querySelectorAll<HTMLElement>('button')) b.classList.toggle('on', (b.dataset.v === 'tune') === on);
    this.$('vtunebtns').hidden = !on;
    if (!on) this.armTune(null);
  }

  /** Arm the lasso on a list (a stroke goes in or out of it), or disarm it (null; toggle: the armed one again disarms it). */
  private armTune(a: { l: 'req' | 'deny'; act: 'add' | 'remove' } | null, toggle = false) {
    if (a && toggle && this.tuneArm?.l === a.l && this.tuneArm.act === a.act) a = null;
    if (a && (this.entry === 'brush' || this.entry === 'seed')) this.pick(null);   // the lasso, not Brush or Grow
    this.tuneArm = a;
    if (a) this.viewTune(true);
    this.syncTuneArm();
    if (a) this.say(`Auto tune: lasso the points ${a.act === 'add' ? 'into' : 'out of'} the ${a.l === 'req' ? 'require' : 'deny'} list (a mouse: drag; Esc: back to selecting)`);
  }

  /** The armed list's + / − lit, in the Tools tab and the View. */
  private syncTuneArm() {
    const a = this.tuneArm;
    for (const b of document.querySelectorAll<HTMLElement>('#etunelists button[data-act], #vtunebtns button[data-act]')) b.classList.toggle('on', !!a && b.dataset.l === a.l && b.dataset.act === a.act);
  }

  /** A lasso stroke into or out of an armed list: the lists change and the tune runs again (the selection is its result). */
  private tuneStroke(a: { l: 'req' | 'deny'; act: 'add' | 'remove' }, masks: Map<string, Uint8Array>) {
    const n = this.tuneEdit(this.soloTune, a.l, a.act, masks), name = a.l === 'req' ? 'require' : 'deny';
    this.drawTuneTools();
    if (!n) { this.say(a.act === 'add' ? `nothing new put in ${name}` : `none of those points are in ${name}`, true); return; }
    this.say(`${n.toLocaleString()} points ${a.act === 'add' ? 'put in' : 'taken out of'} ${name}`);
    this.runSoloTune();
  }

  /** Run the Tools tab's Auto tune (as a one-step macro); one running already: stop it, and run again when it has. */
  private runSoloTune() {
    if (!tuneCounts(this.soloTune).req) { this.say('Auto tune: nothing in its require list yet (+, then lasso the points it must select)', true); return; }
    if (this.soloRunning) { this.tuneAgain = true; this.macroStop?.(); return; }
    if (this.busy || this.stroke || this.growing) { this.say('Auto tune: busy (another action is running); it runs after the next stroke or Run', true); return; }
    this.soloLive = undefined;
    this.$('etunestat').textContent = 'running… (Esc cancels it, Finish ends it with the best so far)';
    void this.runMacro({ name: 'Auto tune', steps: [this.soloTune] });
  }

  /** The Tools tab's Auto tune as a step of the Macros dialog's macro (to save there, or run with other steps). */
  private tuneToMacro() {
    const st = structuredClone(this.soloTune);
    st.mode = this.macro.steps.length ? 'inter' : 'replace';
    this.macro.steps.push(st);
    this.macroBad = -1;
    this.drawMacro();
    this.say(`Auto tune put in ${this.macro.name.trim() || 'the unsaved macro'} as step ${this.macro.steps.length} (the Macros dialog: Save keeps it)`);
  }

  /**
   * Run the macro's steps in order on the scans being edited (a Macro step: that saved macro's steps in its place):
   * one undo step for the lot, group edits included (their commits share one stamp, so one Undo takes them all
   * back). A step that can't run (no such volume, no plane to fit…) stops it: before any edit the selection goes
   * back as it was; after one the edits stand (the selection as it was at that step) and Undo takes back the lot.
   */
  async runMacro(m: Macro = this.macro, env: Env = {}, tap?: Tap) {
    // the Tools tab's Auto tune, run as a one-step macro: it tells the status bar and its own row, not the Macros dialog
    const solo = m.steps.length === 1 && m.steps[0] === this.soloTune;
    const macroSay = (msg: string, warn = false) => (solo ? this.say(msg, warn) : this.macroSay(msg, warn));
    if (this.busy || this.stroke || this.growing) { macroSay('busy: wait for the running action', true); return false; }
    if (!this.scans.size) { macroSay('no scans being edited', true); return false; }
    let steps: RunStep[];
    try { steps = flatten(m, this.macros, undefined, env); } catch (e) {
      const why = (e as Error).message, at = /^step (\d+)/.exec(why);
      if (!solo) { this.macroBad = at ? +at[1] - 1 : -1; this.drawMacro(); }
      macroSay(`can't run it: ${why}`, true);
      return false;
    }
    if (steps.some(r => editsOf(r.s)) && this.job) { macroSay(`${this.job.title} is running: a macro that edits waits until it is done (or Cancel it)`, true); return false; }
    const name = m.name.trim(), before = this.snapSel(), stamp = stampNow(`macro ${name || '(unsaved)'}`), done: Action = [];
    const scans = [...this.scans.values()], ss = steps.map(r => r.s), mine = m === this.macro;
    const wBefore: WeightSnap = new Map();   // per scan, its weights before the first weight step changed them
    // the steps run in the macro worker; each selection shows as it lands, the running step marked in the dialog
    const mark = (i: number) => { this.macroAt = mine ? i : -1; this.markMacroStep(); };
    let end: End;
    this.busy = this.batching = true;
    this.soloRunning = solo;
    let again = false;   // the Tools tab's Auto tune stopped for its lists' new points, to run again
    try {
      const run = runInWorker(`macro ${name || '(unsaved)'}`, scans.map(sc => this.macroSource(sc)), this.ruleCtx(ss, scans),
        scans.map(sc => this.held(sc) ?? new Uint8Array(sc.s.nv)), steps, {
          step: (i, out) => {
            this.landed(out, scans); this.macroAt = mine ? steps[i].at[0] : -1;
            tap?.(steps[i], out);
            if (out.tune && solo) this.soloShow(out.tune, true);
            else if (out.tune) this.tuneDone(steps[i], mine, out.tune);
          },
          show: () => { this.refresh(); this.markMacroStep(); },
          commit: async i => {
            mark(steps[i].at[0]);
            const st = steps[i].s;
            if (st.t === 'groupSave') await this.groupSaveStep(st.p, stamp, done);
            else if (st.t === 'plane') await this.planeStep(st.p, stamp, done);
            else if (WEIGHTS.has(st.t)) await this.weightStep(st, scans, wBefore);
            else this.groupEditStep(st.p);
            this.refresh();
            const now = this.ruleCtx(ss, scans);   // the groups (and so layers, sources) as the step left them
            return { at: now.at, src: now.src, ...(now.weights ? { weights: now.weights } : {}) };
          },
          src: keys => Object.fromEntries(keys.map(k => [k, this.srcOf(k, scans)])),
          tune: (i, stat) => { if (solo) this.soloShow(stat); else if (mine && steps[i].at.length === 1) this.tuneShow(steps[i].at[0], stat); },
          photo: (i, stop, progress) => { mark(steps[i].at[0]); return this.photoStep(steps[i].s, scans, stop, progress, stamp, done); },
          measure: async (_i, r, stop) => {   // on the worker pool, as the toolbar's Density does
            const p = this.densityOf(r);
            stop.addEventListener('abort', () => this.densRun?.stop());
            return this.evalDens(await p, scans);
          },
        });
      this.macroStop = run.cancel;
      this.macroFinish = run.finish;
      if (solo) (this.$('etunefin') as HTMLButtonElement).disabled = false;
      end = await run.done;
    } finally {
      this.busy = this.batching = false; this.soloRunning = false; this.macroStop = this.macroFinish = null; mark(-1); this.drawTuneTools();
      again = solo && this.tuneAgain;
      if (again) { this.tuneAgain = false; setTimeout(() => this.runSoloTune()); }
    }
    const w = wBefore.size ? wBefore : undefined;   // the weights it changed stand, whatever happened after (one Undo)
    if (w) this.weightsChanged([...w.keys()]);
    if (done.length) {   // edits: one step back to before the macro, whatever happened after
      this.step({ k: 'edit', snap: before, w });
      this.stepStamp = stamp.t;
      this.future = [];
    } else if (w && end.k !== 'done') { this.step({ k: 'sel', snap: before, w }); this.future = []; }
    const bad = end.k === 'fail' ? steps[end.i] ?? null : null;
    if (!solo) this.macroBad = bad ? bad.at[0] : -1;
    if (bad && end.k === 'fail') tap?.(bad, null, end.why);
    if (end.k !== 'done') {
      if (!done.length && !w) this.restoreSel(before);
      this.refresh();
      const what = end.k === 'cancelled' ? `cancelled${steps[end.i] ? ` before ${whereIs(steps[end.i])}` : ''}` : `stopped at ${bad ? whereIs(bad) : 'the end'}${roundsText(end.rounds)}: ${end.why}`;
      if (again && end.k === 'cancelled') return false;
      macroSay(`${solo ? 'Auto tune: ' : ''}${what}; ${done.length || w ? 'its edits before that stand (one Undo takes back the lot)' : 'the selection and memory groups are as they were'}`, true);
      if (end.k === 'cancelled' && !solo) this.say(`${name || 'macro'}: ${what}`, true);
      this.drawMacro();
      return false;
    }
    if (!done.length) { this.step({ k: 'sel', snap: before, w }); if (w) this.future = []; }
    this.refresh();
    const n = [...this.scans.values()].reduce((a, sc) => a + (sc.mask ? sel.count(sc.mask) : 0), 0);
    const ran = steps.length, saves = done.length ? steps.filter(r => commits(r.s)).length : 0, edits = steps.filter(r => r.s.t === 'groupEdit').length;   // saves: when any were committed
    const wts = steps.filter(r => WEIGHTS.has(r.s.t)).length;
    const what = [edits && `${edits} memory group edit${edits === 1 ? '' : 's'}`, wts && `${wts} weight step${wts === 1 ? '' : 's'}`, saves && `${saves} edit${saves === 1 ? '' : 's'} saved`].filter(Boolean).join(', ');
    this.say(`${m.name.trim() || 'macro'}: ${ran} step${ran === 1 ? '' : 's'}${what ? ` (${what})` : ''}, ${n.toLocaleString()} points selected`);
    return true;
  }

  /**
   * A Group edit step: change the memory groups (memgroups.ts) with each scan's selection; nothing is committed. A
   * saved group's name is refused: memory groups are apart from those (Group save writes one to the scan).
   */
  private groupEditStep(p: Params) {
    const act = p.act as 'add' | 'remove' | 'clear', name = String(p.name);
    if ([...this.scans.values()].some(sc => sc.s.state.groups.has(name))) throw new Error(`"${name}" is a saved group: Group edit changes memory groups (Group save writes one to the scan)`);
    for (const sc of this.scans.values()) {
      const g = memEdit(this.memOf(sc.s.scan), sc.mask ? sc.mask.slice() : new Uint8Array(sc.s.nv), act, name);
      if (g) this.mem.set(sc.s.scan, g);
    }
  }

  /**
   * A Set weight or Blur weights step (weights.ts): change the weights of each shown scan's selected points (Blur:
   * of every shown point, with none selected), keeping each scan's weights as they were before the run's first
   * change in before. They are saved when the run ends.
   */
  private async weightStep(st: MacroStep, scans: Scan[], before: WeightSnap) {
    const shown = scans.filter(sc => !this.off(sc)), masks = new Map(shown.map(sc => {
      const live = this.live(sc), m = sc.mask, out = new Uint8Array(sc.s.nv);
      if (m) for (let i = 0; i < out.length; i++) out[i] = m[i] & live[i];
      return [sc, out] as const;
    }));
    const any = [...masks.values()].some(m => m.some(x => x));
    const keep = (sc: Scan) => { if (!before.has(sc.s.scan)) before.set(sc.s.scan, sc.s.weight.slice()); };
    let n = 0;
    if (st.t === 'weight') {
      if (!any) throw new Error('select some points first');
      const of = String(st.p.of ?? '');
      if (of && !scans.some(sc => this.dataOf(sc.s.scan).has(of))) throw new Error(`no data group "${of}"`);
      for (const [sc, m] of masks) {
        const g = of ? this.dataOf(sc.s.scan).get(of) : undefined;
        if (of && !g) continue;   // a scan without that data group: left as it is
        keep(sc);
        // from a data group: each point its value (the default where it isn't in it)
        n += applyOp(sc.s.weight, m, st.p.op as WeightOp, g ? (i: number) => (g.v[i] === g.v[i] ? g.v[i] : g.dflt) : +st.p.x);
      }
      this.macroSay(`${n.toLocaleString()} weights changed`);
      return;
    }
    const r = +st.p.r, jobs: { sc: Scan; f: number; idx: number[]; change: Uint8Array }[] = [];
    for (const [sc, m] of masks) {
      const s = sc.s, ci = s.images;
      if (!ci || !s.frame) continue;
      const live = this.live(sc), change = any ? m : live;
      for (const [f, idx] of byFrame(s.frame, i => !!live[i])) if (ci.cams.has(f) && idx.some(i => change[i])) jobs.push({ sc, f, idx, change });
    }
    if (!jobs.length) throw new Error(shown.some(sc => sc.s.images) ? 'no frames with depth images under those points' : 'these scans have no camera images (prep/images.py --scene, then Publish)');
    const head = proc(`blurring weights: 0 of ${jobs.length} frames`, { cantStop: 'it is quick: a frame at a time' });
    try {
      let k = 0;
      for (const j of jobs) {
        const fr = this.depthFrame(j.sc, j.f, await loadDepth(j.sc.s.images!, j.f));
        keep(j.sc);
        n += blurFrame(j.sc.s.weight, j.idx, pixelsOf(j.sc.s.P, j.idx, fr), fr, r, i => !!j.change[i]);
        head.set(`blurring weights: ${++k} of ${jobs.length} frames`, k / jobs.length);
      }
    } finally { head.end(); }
    this.macroSay(`${n.toLocaleString()} weights blurred over ${jobs.length} frame${jobs.length === 1 ? '' : 's'}`);
  }

  /** Frame f's depth image placed as its points are (s.P: the scan's T, the tree's E(f)). */
  private depthFrame(sc: Scan, f: number, depth: Float32Array): DepthFrame {
    const s = sc.s, ci = s.images!, cam = ci.cams.get(f)!;
    return { depth, dw: ci.dw, dh: ci.dh, K: cam.K, place: compose(s.T, compose(frameTransform(s.state.tree, f), cam.pose)) };
  }

  /**
   * A Group save step (or a memory group's Save): write the memory group `name` as the saved group `as` (its points
   * out of the other saved groups) in every scan that has it, with the stamp (done collects the commits); the memory
   * group goes from those scans. Scans whose source changed are read-only: skipped (they keep the memory group).
   */
  private async groupSaveStep(p: Params, stamp: { t: string; msg: string }, done: Action) {
    const name = String(p.name), as = String(p.as ?? '') || name;
    const has = [...this.scans.values()].filter(sc => this.memOf(sc.s.scan).has(name));
    if (!has.length) throw new Error(`no memory group "${name}"`);
    if (as !== name && [...this.scans.values()].some(sc => this.memOf(sc.s.scan).has(as))) throw new Error(`"${as}" is a memory group: save it as itself, or pick another name`);
    const saving = has.filter(sc => !sc.s.graph.stale);
    const jobs = saving.map(sc => {
      const m = this.memOf(sc.s.scan).get(name)!;
      return { s: sc.s, bodies: [...this.outOfOtherGroups(sc, m, as), { op: 'group.save', name: as, set: sc.s.toSet(m) } as OpBody] };
    });
    if (!jobs.length) throw new Error(`the scans with memory group "${name}" are read-only`);
    const head = proc(`saving: group "${as}"`, { delay: 400, cantStop: 'a commit is all or nothing: stopping it midway could leave it half made' });
    try { await commitMany(jobs, describe({ t: 'groupSave', p }), stamp, done); } finally { head.end(); }
    for (const sc of saving) { const g = new Map(this.memOf(sc.s.scan)); g.delete(name); this.mem.set(sc.s.scan, g); }
  }


  /** A scan as the photo steps see it: its photos, frames placed as drawn now, and the selection. */
  private photoScan(sc: Scan): PhotoScan {
    const c = this.cloudOf(sc);
    return {
      id: sc.s.scan, name: sc.s.meta.name, images: sc.s.images, place: f => this.framePlace(sc, f), P: c.P, frame: c.frame, locked: sc.s.state.tree.locked, links: sc.s.links, features: sc.s.features,
      sel: sc.mask ?? new Uint8Array(sc.s.nv), live: this.live(sc),
    };
  }

  /** A photo step of a macro (macros.ts PHOTO), on the photo pool or the photogrammetry worker; shown photos are drawn again with what it found. */
  private async photoStep(s: MacroStep, scans: Scan[], stop: AbortSignal, progress: (t: string, f: number) => void,
    stamp: { t: string; msg: string }, done: Action): Promise<PhotoDone> {
    if (this.viewing) return { fail: 'photo steps run in the scene view (leave the instance view first)' };
    const shown = scans.filter(sc => !this.off(sc) && !sc.s.graph.stale), ps = shown.map(sc => this.photoScan(sc));
    switch (s.t) {
      case 'features': {
        const pool = makePool<PhotoTask, PhotoResult>(photoWorkerSrc, { key: 'task' }), quit = () => pool.stop('cancelled');
        stop.addEventListener('abort', quit);
        try {
          const r = await this.photo.features(ps, s.p, location.href, t => pool.exec(t), stop, progress);
          this.featVer++; this.shading();
          this.redrawPhotos();
          return r;
        } finally { stop.removeEventListener('abort', quit); pool.stop('done'); }
      }
      case 'calibrate': return this.photo.calibrate(ps, s.p);
      case 'alignCameras': return this.alignCamerasStep(s, shown, ps, stop, progress, stamp, done);
      case 'matchEdges': {
        const r = this.photo.matchEdges(ps, s.p);
        this.redrawPhotos();
        if (r.fail) return r;
        // per cloud of the run (scans in order; unshown ones: none)
        const matched = scans.map(sc => { const k = shown.indexOf(sc); return k < 0 ? null : r.matched![k]; });
        return { say: r.say, warn: r.warn, ctx: { photo: this.photoCtx({ matched }) } };
      }
      case 'mesh': return this.meshStep(s, ps, stop, progress);
      case 'edges3d': {
        const r = await this.photo.edges3d(ps, s.p, (this.pg ??= pgHost()), stop, progress);
        this.drawPhotoEdges();
        return r.fail ? r : { say: r.say, ctx: { photo: this.photoCtx({ edges: r.edges }) } };
      }
      default: return { fail: `${s.t}: not a photo step` };
    }
  }

  /**
   * Align cameras (a macro step): the frames shown moving each round (their points, cameras and photos), with Finish
   * now (keep where they have got to) and Cancel; then frames without photo features are carried along by the
   * springs and the lot is committed with the macro's stamp (one Undo). The clouds go back to the macro worker moved.
   */
  private async alignCamerasStep(s: MacroStep, shown: Scan[], ps: PhotoScan[], stop: AbortSignal, progress: (t: string, f: number) => void, stamp: { t: string; msg: string }, done: Action): Promise<PhotoDone> {
    const finish = new AbortController(), row = this.$('ewidenf').parentElement!, wrap = el('div', { class: 'ejob' }), btns = el('div', { class: 'row' });
    const info = el('div', { class: 'mono' }, 'Aligning cameras: matching…');
    const fin = el('button', { class: 'small', title: 'stop here, keeping where the cameras have got to (one edit)' }, 'Finish now') as HTMLButtonElement;
    const no = el('button', { class: 'small', title: 'stop, leaving every frame where it was (the macro stops too)' }, 'Cancel') as HTMLButtonElement;
    fin.onclick = () => { finish.abort(); fin.disabled = true; info.textContent += ' (finishing)'; };
    no.onclick = () => this.macroStop?.();
    btns.append(fin, no);
    wrap.append(info, btns);
    row.parentElement!.insertBefore(wrap, row.nextElementSibling);
    // shown at most every 200 ms (straight away: a frame of a big scene can take longer than the whole alignment)
    let last = 0, raf = 0, pending: Map<string, Map<number, Rigid>> | null = null;
    const show = () => { raf = 0; last = performance.now(); if (pending && !stop.aborted) this.setFramePreview(pending); };
    try {
      const r = await this.photo.align(ps, s.p, (this.pg ??= pgHost()), stop, finish.signal, (t, f) => { progress(t, f); if (!pending) info.textContent = `Aligning cameras: ${t}`; },
        (moves, it) => {
          pending = moves;
          info.textContent = `Aligning cameras: pass ${it.pass}, round ${it.it}: ${it.rmsPx.toFixed(2)} px over ${it.n.toLocaleString()} residuals`;
          if (performance.now() - last > 200) { if (raf) cancelAnimationFrame(raf); show(); } else raf ||= requestAnimationFrame(show);
        });
      if (r.fail || !r.moves) return r;
      // frames as they are again (the preview moves them: the checks below are of where they are against the moves)
      cancelAnimationFrame(raf); raf = 0; pending = null;
      this.clearFramePreview();
      // frames with no photo features go along with their aligned neighbours (springs), then one tree.transform per scan
      const fsOf = new Map<string, FrameScan>(shown.map(sc => [sc.s.scan, this.placed(sc)]));
      const anchors = new Map<FrameScan, Map<number, Rigid>>();
      for (const [id, m] of r.moves) { const f = fsOf.get(id); if (f) anchors.set(f, m); }
      const sp = springAll([...fsOf.values()], anchors, +s.p.reach);
      let carried = 0;
      for (const [f, m] of sp.moves) for (const [k, W] of m) { if (!anchors.get(f)?.has(k)) { (anchors.get(f) ?? anchors.set(f, new Map()).get(f)!).set(k, W); carried++; } }
      const jobs = [...anchors].map(([f, m]) => ({ s: this.scans.get(f.scan)!.s, bodies: [{ op: 'tree.transform', D: Object.fromEntries([...m].map(([k, W]) => [`f:${k}`, frameD(f, k, W)])) } as OpBody] }));
      if (stop.aborted) throw new Error('cancelled');
      // checked by what it wasn't fitted to: RTAB-Map's own matches (prep/features.py) when the scans have them; kept
      // only if better, unless told to keep it anyway
      const byId = new Map([...anchors].map(([f, m]) => [f.scan, m])), r0 = this.photo.rtabCheck(ps), r1 = r0 && this.photo.rtabCheck(ps, byId);
      let rtabSay = '';
      if (r0 && r1) {
        rtabSay = `; RTAB-Map's own ${r0.n.toLocaleString()} matches ${r0.px.toFixed(2)} → ${r1.px.toFixed(2)} px apart in the photos, ${r0.mm.toFixed(1)} → ${r1.mm.toFixed(1)} mm in depth`;
        if ((!(r1.px < r0.px) || r1.mm > r0.mm * 1.02) && s.p.keep !== 'always') {
          this.clearFramePreview();
          return { say: `not kept: RTAB-Map's own matches don't agree better (${r.say}${rtabSay}); the frames are as they were ("keep it" keeps it anyway)`, warn: true };
        }
      }
      const head = proc('saving: camera alignment', { delay: 400, cantStop: 'a commit is all or nothing: stopping it midway could leave it half made' });
      try { await commitMany(jobs, 'align cameras', stamp, done); } finally { head.end(); }
      this.clearFramePreview();
      this.refresh();
      return { say: r.say + (carried ? `; ${carried} frames without photo features carried along` : '') + rtabSay, srcs: shown.map(sc => this.macroSource(sc)) };
    } finally { cancelAnimationFrame(raf); wrap.remove(); this.clearFramePreview(); }
  }

  /**
   * Mesh export (a macro step): the shown scans' frames as placed now, only the shown points' depth pixels (deleted
   * and hidden points left out), the step's region; built and coloured in a worker, then downloaded (with the parts
   * kept before, which own their regions) or kept for the next Mesh export.
   */
  private async meshStep(s: MacroStep, ps: PhotoScan[], stop: AbortSignal, progress: (t: string, f: number) => void): Promise<PhotoDone> {
    const p = s.p, frames: MeshFrame[] = [], dirs: Record<string, string> = {}, masks: FrameMasks[] = [];
    for (const x of ps) {
      if (!x.images || !x.frame) continue;
      dirs[x.id] = new URL(x.images.url, location.href).href;
      const mine: MeshFrame[] = [];
      for (const f of x.images.cams.keys()) { const cam = this.photo.camOf(x, f); if (cam) mine.push({ key: cam.key, scan: x.id, f, cam }); }
      frames.push(...mine);
      masks.push(frameMasks(mine, x.id, x.P, x.frame, i => x.live[i] === 1));
    }
    if (!frames.length) return { fail: 'no shown scan has photos and depth (prep/images.py) to mesh from' };
    let region: RegionSpec = { kind: 'all' };
    if (p.region === 'sel') {
      const P: number[] = [];
      for (const x of ps) for (let i = 0; i < x.sel.length; i++) if (x.sel[i] && x.live[i]) P.push(x.P[3 * i], x.P[3 * i + 1], x.P[3 * i + 2]);
      if (!P.length) return { fail: 'nothing selected to mesh near' };
      region = { kind: 'near', P: Float32Array.from(P), cell: 0.05 };
    } else if (p.region === 'vol') {
      const id = String(p.vol), vols = this.v.s.volumes.filter(v => (id === '*' ? v.on : v.id === id));
      if (!vols.length) return { fail: id === '*' ? 'no volume: draw a box in Volumes first' : `no volume ${id}` };
      region = { kind: 'boxes', vols };
    }
    const engine = String(p.engine), fields = ENGINE_FIELDS[engine]?.fields;
    if (!fields) return { fail: `no mesh engine ${engine}` };
    const params = Object.fromEntries(fields.map(f => [f.key, p[f.key] as number | string | boolean]));
    const download_ = p.then !== 'keep', kept = this.meshKept;
    const job: MeshJob = {
      frames, dirs, part: { name: `${engine} ${kept.length + 1}`, engine, region, params, masks: anyMasks(...masks) },
      taken: kept.map(k => k.region), kept: download_ ? kept.map(k => k.part) : [],
      colour: p.colour === 'none' ? null : { mode: p.colour === 'best' ? 'best' : 'blend', gains: !!p.gains },
      maxBlocks: 120_000, format: download_ ? String(p.format) : null,
    };
    const url = URL.createObjectURL(new Blob([meshWorkerSrc], { type: 'text/javascript' })), w = new Worker(url);
    const quit = () => w.terminate();
    stop.addEventListener('abort', quit);
    try {
      const done = await new Promise<MeshDone>((ok, fail) => {
        w.onmessage = (e: MessageEvent<{ progress?: [string, number]; result?: MeshDone; error?: string }>) => {
          if (e.data.progress) progress(...e.data.progress);
          else if (e.data.error) fail(new Error(e.data.error));
          else if (e.data.result) ok(e.data.result);
        };
        w.onerror = e => fail(new Error(`the mesh worker failed: ${e.message}`));
        stop.addEventListener('abort', () => fail(new Error('cancelled')));
        w.postMessage(job);
      });
      const tris = (m: MeshPart) => m.mesh.indices.length / 3, n = tris(done.part).toLocaleString();
      const size = `${n} triangles (${done.blocks.toLocaleString()} voxel blocks, ${done.voxelMB.toFixed(0)} MB)`;
      if (!download_) {
        kept.push({ part: done.part, region });
        return { say: `${size} kept: the next Mesh export's file has it (${kept.length} part${kept.length > 1 ? 's' : ''} kept)` };
      }
      const f = done.file!, name = `${this.base.split('/').filter(Boolean).pop() ?? 'scene'}-${engine}-${Math.round(+params.voxel * 1000) / 10}cm.${f.ext}`;
      download(name, f.chunks as BlobPart[], f.mime);
      const mb = f.chunks.reduce((a, c) => a + c.byteLength, 0) / 2 ** 20;
      this.meshKept = [];
      return { say: `${name}: ${size}${kept.length ? ` and ${kept.length} kept part${kept.length > 1 ? 's' : ''}` : ''}, ${mb.toFixed(1)} MB` };
    } finally { stop.removeEventListener('abort', quit); w.terminate(); URL.revokeObjectURL(url); }
  }

  /** What the run's later steps get of the photo steps' results (ctx.photo is replaced whole on a resume). */
  private photoCtx(more: { edges?: Float32Array; matched?: (Uint8Array | null)[] }) {
    const E = this.photo.edges, edges = new Float32Array(6 * E.length);
    E.forEach((e, k) => { edges.set(e.A, 6 * k); edges.set(e.B, 6 * k + 3); });
    return { edges: E.length ? edges : undefined, ...more };
  }

  /** The photo steps' 3D edges in the view (line set 'ephoto:edges'). */
  private drawPhotoEdges() {
    const E = this.photo.edges, xyz = new Float32Array(6 * E.length);
    E.forEach((e, k) => { xyz.set(e.A, 6 * k); xyz.set(e.B, 6 * k + 3); });
    this.v.setLines('ephoto:edges', xyz, [1, 0.55, 0.1, 1], true);
  }

  /** Shown photos drawn again (with or without the photo steps' marks). */
  private redrawPhotos() {
    for (const k of [...this.shownImgs]) {
      const i = k.lastIndexOf(':'), sc = this.scans.get(k.slice(0, i));
      if (!sc) continue;
      this.hideImages(k);
      void this.showImage(sc, +k.slice(i + 1));
    }
  }

  // ── actions ───────────────────────────────────────────────────────────
  /**
   * Commit one op per scan: make(scan, its selected points) for every scan with
   * selected points (and every scan `also` names), all at one time so history
   * and undo see one action.
   */
  private act(label: string, make: (sc: Scan, m: Uint8Array, n: number) => OpBody | OpBody[] | null,
    o: { keepDeleted?: boolean; also?: (sc: Scan) => boolean; keepSel?: boolean; stamp?: { t: string; msg: string } } = {}) {
    if (this.job && !o.stamp) { this.say(`${this.job.title} is running: edits wait until it is done (or Cancel it)`, true); return; }
    const jobs: { sc: Scan; bodies: OpBody[]; n: number }[] = [];
    let total = 0, stale = 0;
    for (const sc of this.scans.values()) {
      const m = sc.mask ? sc.mask.slice() : new Uint8Array(sc.s.nv);
      if (!o.keepDeleted) { const live = this.live(sc); for (let i = 0; i < m.length; i++) m[i] &= live[i]; }
      const n = sel.count(m);
      if (!n && !o.also?.(sc)) continue;
      if (sc.s.graph.stale) { stale++; continue; }
      const made = make(sc, m, n);
      if (!made) continue;
      const bodies = (Array.isArray(made) ? made : [made]).map(b =>
        ({ ...b, ...(SET_OPS.has(b.op) && !('set' in b) ? { set: sc.s.toSet(m) } : {}), rule: this.rule ?? undefined }) as OpBody);
      jobs.push({ sc, bodies, n });
      total += n;
    }
    if (!jobs.length) { this.say(stale ? 'those scans are read-only (their source changed)' : 'select some points first'); return; }
    // a shared stamp makes several commits one entry in history (undo takes back every op with its time and message)
    const t = o.stamp?.t ?? new Date().toISOString(), msg = o.stamp?.msg ?? `${label}${total ? ` (${total.toLocaleString()})` : ''}`;
    const before = this.snapSel();
    this.run(label, async () => {
      const done: Action = [];
      const head = proc(`saving: ${label}`, { delay: 400, cantStop: 'a commit is all or nothing: stopping it midway could leave it half made' });
      try {
        await commitMany(jobs.map(j => ({ s: j.sc.s, bodies: j.bodies })), label, { t, msg }, done);
      } finally {
        head.end();
        if (done.length) {
          // commits sharing a stamp are one entry: one undo step for all of them
          if (!o.stamp || this.stepStamp !== o.stamp.t) this.step({ k: 'edit', snap: before });
          this.stepStamp = o.stamp?.t ?? null;
          this.future = [];
        }
      }
      if (!o.keepSel) { for (const sc of this.scans.values()) sc.mask = sc.cand = sc.parked = null; this.rule = null; }
      if (stale) this.say(`${stale} read-only scan(s) skipped`, true);
    });
  }

  /** Floor / solid take points out of any movable object (a point is one or the other); other classes and unclassify leave objects alone,
   * except in the instance viewer, where any class means "not part of this object" and takes them out too.
   * A user class (by name) is made in a scan that doesn't have it yet. */
  private classify(name: string) {
    this.act(name === 'none' ? 'unclassify' : `classify ${name}`, (sc, m) => {
      let next = nextClassId(sc.s.state);
      return this.classifyOps(sc, name, m, () => next++);
    }, { keepSel: true });
  }

  /** The ops classifying the points m of a scan as name, making the user class there first if it doesn't have it
   * (with id fresh(), so several new ones in one commit differ); null if there's no room for it. */
  private classifyOps(sc: Scan, name: string, m: Uint8Array, fresh: () => number): OpBody[] | null {
    const st = sc.s.state, ops: OpBody[] = [];
    let cls: Cls | number = name as Cls;
    if (!(CLS as readonly string[]).includes(name)) {
      let id = [...st.classes].find(([, c]) => c.name === name)?.[0];
      if (id === undefined) {
        id = fresh();
        if (id > MAX_CLASS) { this.say(`${sc.s.scan}: no room for another class`, true); return null; }
        ops.push({ op: 'class.new', cls: id, name, color: this.userClasses().get(name) ?? this.freeColor() } as OpBody);
      }
      cls = id;
    }
    ops.push({ op: 'classify', cls, set: sc.s.toSet(m) } as OpBody);
    if (name === 'floor' || name === 'solid' || this.viewing) {
      const inObj = m.slice();
      let k = 0;
      for (let v = 0; v < inObj.length; v++) if (inObj[v] && !st.obj[sc.s.pidOfV[v]]) inObj[v] = 0; else k += inObj[v];
      if (k) ops.push({ op: 'object.release', set: sc.s.toSet(inObj) } as OpBody);
    }
    return ops;
  }

  /** Each selected point takes the class of the nearest unselected point of another class within reach, in any loaded
   * scan, shown or hidden (but not deleted), as Floor / Solid / … would (the same rules for objects); points with none
   * there keep theirs. One Undo. */
  private dissolve() {
    const r = Math.max(0.005, +this.inp('edisr').value || 0.1), { Pof, gridOf } = this.growSpace(r);
    const names: string[] = [], code = new Map<string, number>();
    const codeOf = (n: string) => code.get(n) ?? (code.set(n, names.length), names.push(n) - 1);
    const use = [...this.scans.values()];
    const clouds = use.map(x => {
      const st = x.s.state, tab = new Int32Array(MAX_CLASS + 1).fill(codeOf('none'));
      CLS.forEach((c, k) => { tab[k] = codeOf(c); });
      for (const [id, c] of st.classes) tab[id] = codeOf(c.name);
      const nv = x.s.nv, live = this.live(x), cls = new Int32Array(nv), pick = new Uint8Array(nv), target = new Uint8Array(nv);
      for (let v = 0; v < nv; v++) {
        const f = st.flags[x.s.pidOfV[v]];
        cls[v] = tab[f >> 1];
        const m = x.mask?.[v] ?? 0;
        pick[v] = live[v] & m;   // what act() commits for
        target[v] = (pick[v] ^ 1) & (f & DELETED ? 0 : 1);   // hidden layers give their class too (selected-but-hidden points keep theirs, so they may)
      }
      return { P: Pof(x), grid: gridOf(x), cls, sel: pick, target };
    });
    if (!clouds.some(c => sel.count(c.sel))) { this.say('select some points first'); return; }
    const out = dissolvePts(clouds, r);
    let moved = 0, left = 0;
    clouds.forEach((c, k) => { for (let v = 0; v < c.sel.length; v++) if (c.sel[v]) { if (out[k][v] >= 0) moved++; else left++; } });
    if (!moved) { this.say(`nothing of another class within ${r} m of the selection`); return; }
    const of = new Map(use.map((x, k) => [x, out[k]]));
    this.act('dissolve', (sc, m) => {
      const o = of.get(sc)!, by = new Map<number, Uint8Array>();
      for (let v = 0; v < o.length; v++) if (m[v] && o[v] >= 0) (by.get(o[v]) ?? by.set(o[v], new Uint8Array(o.length)).get(o[v])!)[v] = 1;
      let next = nextClassId(sc.s.state);
      const ops = [...by].flatMap(([c, mm]) => this.classifyOps(sc, names[c], mm, () => next++) ?? []);
      return ops.length ? ops : null;
    }, { keepSel: true });
    if (left) this.say(`${left.toLocaleString()} selected points have nothing of another class within ${r} m: they keep theirs`);
  }

  private newObject() {
    const name = prompt('Name of the movable object (the same name in several scans is one object)', `object ${this.objectNames().length + 1}`);
    if (!name) return;
    if (this.objectNames().includes(name)) { this.addToObject(name); return; }   // an existing object: add to it
    this.act(`new object "${name}"`, sc => {
      const id = nextId(sc.s.state.objects);
      return id >= MAX_ID ? null : ({ op: 'object.new', obj: id, name: name.slice(0, 80) }) as OpBody;
    });
  }

  /** Add the selection to an object (by name), creating it in scans that don't have it yet. */
  private addToObject(name: string) {
    if (!name) return;
    const info = this.objectInfo().get(name);
    this.act(`add to "${name}"`, sc => {
      const id = idsNamed(sc.s.state.objects, name)[0];
      if (id) return { op: 'object.assign', obj: id } as OpBody;
      const nid = nextId(sc.s.state.objects);
      if (nid >= MAX_ID) return null;
      // an instance keeps its type and fit in the scans it spreads to
      return [{ op: 'object.new', obj: nid, name } as OpBody, ...(info?.of ? [{ op: 'object.of', obj: nid, of: info.of, ...(info.F ? { F: info.F } : {}) } as OpBody] : [])];
    });
  }

  // ── instances of a type ───────────────────────────────────────────────
  /**
   * Objects by name across scans, with their type and fit. A fit is written to the scans loaded at the
   * time, so scans can disagree: the one whose object.of is newest wins.
   */
  private objectInfo() { return ovl.objectInfo([...this.scans.values()].map(sc => sc.s)); }

  /** Every loaded scan, its world copy in step with its alignment. */
  private placedAll() { return [...this.scans.values()].map(sc => this.placed(sc)); }

  /**
   * Every object name in use in any editable scan, loaded or not (from the others' edit graphs), so a
   * new name can't be one an unloaded scan already gives to something else.
   */
  private async allObjectNames() {
    const names = new Set(this.objectNames());
    await Promise.all([...this.editable].filter(id => !this.scans.has(id)).map(async id => {
      try { for (const o of (await this.api.graph(id)).ops) if ((o.op === 'object.new' || o.op === 'object.meta') && o.name) names.add(o.name); } catch { /* unreadable: skip */ }
    }));
    return names;
  }

  /** A type's instances by name, in natural order (the first is the reference others are fitted onto). */
  private instancesOf(type: string) { return ovl.instancesOf(this.objectInfo(), type); }

  private typeNames() { return ovl.typeNames(this.objectInfo()); }

  /** The selection becomes a new instance "<type> <n>" of a type (asked for), fitted at once so its box shows. */
  private async newInstance() {
    const types = this.typeNames();
    const t = prompt(`New instance of which type?${types.length ? ` (so far: ${types.join(', ')})` : ''}`, this.lastType || types[0] || '')?.trim();
    if (!t) return;
    if (!isTypeName(t)) { this.say('bad type name (1-60 characters)'); return; }
    this.lastType = t;
    const name = nextInstanceName(t, await this.allObjectNames());   // unique in every scan, not just the loaded ones
    this.act(`new instance "${name}"`, sc => {
      const id = nextId(sc.s.state.objects);
      return id >= MAX_ID ? null : [{ op: 'object.new', obj: id, name } as OpBody, { op: 'object.of', obj: id, of: t } as OpBody];
    });
    await this.idle();
    // the first of a type with no master is what the others are fitted onto: nothing to fit it onto
    if (this.objectInfo().has(name) && (this.hasMaster(t) || this.instancesOf(t)[0] !== name)) this.fitInstances(t, [name], `fit "${name}"`);
  }

  /** Make an object an instance of a type, or (empty) of none. */
  private setType(name: string) {
    const cur = this.objectInfo().get(name)?.of ?? '';
    const t = prompt(`"${name}" is an instance of which type? (empty: none)`, cur || this.lastType)?.trim();
    if (t === undefined || t === cur) return;
    if (t && !isTypeName(t)) { this.say('bad type name (1-60 characters)'); return; }
    if (t) this.lastType = t;
    const has = (sc: Scan) => idsNamed(sc.s.state.objects, name).length > 0;
    this.byName(t ? `"${name}" is a ${t}` : `"${name}" has no type`, has, sc => ({ op: 'object.of', obj: idsNamed(sc.s.state.objects, name)[0], of: t || null }) as OpBody);
  }

  /**
   * Where an object's vertex goes: through its frame's correction W (the object's own, see log.ts Obj),
   * then, if placed, the object's fit F into the overlay. null: nowhere else (identity).
   */
  private placer(sc: Scan, placed: boolean, info = placed ? this.objectInfo() : null) { return ovl.placer(sc.s, placed ? info : null); }

  /** World points of objects (by name) in every loaded scan, deleted points left out, each through its frame corrections and (if placed) its fit F. */
  private objectPts(names: string[], placed = true): Map<string, Pts> {
    return ovl.objectPts(this.placedAll(), names, placed ? this.objectInfo() : null);
  }

  /**
   * Each instance's part in each loaded scan, with its world centroid (not fitted). Parts of one instance
   * should be one physical object: parts far apart are different objects that ended up with one name.
   */
  private instanceParts(type: string) {
    const parts = ovl.instanceParts(this.placedAll(), type, this.instancesOf(type));
    return new Map([...parts].map(([n, ps]) => [n, ps.map(p => ({ sc: this.scans.get(p.s.scan)!, id: p.id, n: p.n, c: p.c }))]));
  }

  /** Parts of an instance grouped by place (within `near` m of a group's biggest part), biggest group first. */
  private clusters<P extends { n: number; c: V3 }>(parts: P[], near = 0.6) { return ovl.clusters(parts, near); }

  /**
   * Instances whose parts in different scans are in different places (one name given to different
   * objects): the biggest group keeps the name, each other group becomes a new instance (a name unique
   * in every scan). Then every instance is fitted again: the old fits were made on the mixed-up parts.
   */
  private async untangle(type: string) {
    if (this.job) { this.say(`${this.job.title} is running: wait for it (or Cancel it)`, true); return; }
    if (this.busy) return;
    const r = instops.untangle(this.placedAll(), type, this.instancesOf(type), await this.allObjectNames());
    if (!r.made.length) { this.say(r.note); return; }
    this.commitOut(`untangle ${r.made.length} ${type} instance(s)`, r.bodies);
    while (this.busy) await nextFrame();
    this.fitInstances(type, this.instancesOf(type), `fit the ${type} instances again after untangling`);
  }

  /** Commit new fits for some instances of a type, as one action. */
  private commitFits(label: string, type: string, fits: Map<string, Rigid>, stamp?: { t: string; msg: string }) {
    const has = (sc: Scan) => [...fits.keys()].some(n => idsNamed(sc.s.state.objects, n).length > 0);
    this.byName(label, has, sc => [...fits].flatMap(([n, F]) => {
      const id = idsNamed(sc.s.state.objects, n)[0];
      return id ? [{ op: 'object.of', obj: id, of: type, F } as OpBody] : [];
    }), stamp);
  }

  /**
   * Fit instances onto the master (instops.ts fitInstances), on the worker pool with progress in the
   * viewer, then commit the fits as one action.
   */
  private fitInstances(type: string, names: string[], label: string) {
    if (this.busy || this.job) { if (this.job) this.say('a fit is running: wait for it (or Cancel it)', true); return; }
    const n = this.hasMaster(type) ? names.length : names.filter(x => x !== this.instancesOf(type)[0]).length;
    const job = this.startJob(`fitting ${n} instance${n === 1 ? '' : 's'} onto ${this.masterLabel(type)}`);
    this.runOut(job, label, instops.fitInstances(this.instCtx(type, job), names));
  }

  /** The context instops.ts operations run in: the loaded scans and a job's worker pool. */
  private instCtx(type: string, job: { exec: instops.Exec; K: number; set: (text: string, frac: number) => void }): instops.InstCtx {
    return { scans: this.placedAll(), type, info: this.objectInfo(), exec: job.exec, K: job.K, progress: job.set };
  }

  /** Commit an operation's ops (per scan id) as one action. */
  private commitOut(label: string, bodies: Map<string, OpBody[]>, stamp?: { t: string; msg: string }) {
    this.act(label, sc => bodies.get(sc.s.scan) ?? null, { also: sc => bodies.has(sc.s.scan), keepSel: true, keepDeleted: true, stamp });
  }

  /** When a job's operation is done: end the job, commit what it made (one action), and show its note. */
  private runOut(job: ReturnType<EditUI['startJob']>, label: string, p: Promise<instops.OpOut>, stamp?: { t: string; msg: string }) {
    p.then(async r => {
      job.end();
      if (r.bodies.size) { this.commitOut(label, r.bodies, stamp); await this.idle(); } else this.say(r.note);
      this.viewNote = r.note;
      this.refresh();
    }, (e: Error) => job.fail(e));
  }

  // ── the master: a type's composite template ────────────────────────
  /** The point group holding a type's master. */
  private masterName = ovl.masterName;

  /** Per vertex of a scan, the index (in names) of the instance of type it is in; -1: none, or deleted. */
  private instanceIx(sc: Scan, type: string, names: string[]) { return ovl.instanceIx(sc.s, type, names); }

  /** Does the type have a master set (a non-empty master group in some scan)? */
  private hasMaster(type: string) { return ovl.hasMaster([...this.scans.values()].map(sc => sc.s), type); }

  /** Per vertex: in the type's master? Without a master set, the master is its first instance. */
  private masterMask(sc: Scan, type: string, kv: Int32Array, explicit = this.hasMaster(type)) { return ovl.masterMask(sc.s, type, kv, explicit); }

  /**
   * The master's points, placed in the overlay (each through its frame correction and its instance's fit),
   * with the instance each came from (index in names).
   */
  private masterOf(type: string, names = this.instancesOf(type)) { return ovl.masterOf(this.placedAll(), type, names, this.objectInfo()); }

  /** The master's points not from instance `without`, optionally moved by `place` (packed, for target()). */
  private masterPart(m: ovl.Master, without: number, move?: Rigid): Pts { return ovl.masterPart(m, without, move); }

  /** What the instances are fitted onto, for messages. */
  private masterLabel(type: string) { return this.hasMaster(type) ? `the ${type} master` : `"${this.instancesOf(type)[0]}"`; }

  /** Add the selection to (or take it out of) a type's master. */
  private setMaster(type: string, inside: boolean) {
    const name = this.masterName(type);
    if (!GROUP_NAME.test(name)) { this.say(`"${type}" can't have a master: its name needs to be letters, digits, space . : + -`); return; }
    this.act(`${inside ? 'add to' : 'take out of'} the ${type} master`, (sc, m) => {
      const g = sc.s.group(name);
      let k = 0;
      for (let i = 0; i < g.length; i++) if (m[i] && g[i] !== (inside ? 1 : 0)) { g[i] = inside ? 1 : 0; k++; }
      return k ? ({ op: 'group.save', name, set: sc.s.toSet(g) } as OpBody) : null;
    }, { keepSel: true });
  }

  /**
   * Fit each instance's points onto the master frame by frame (instops.ts fitInstanceFrames), on the
   * worker pool (one task per instance) with progress in the viewer; one action at the end.
   */
  private fitInstanceFrames(type: string, names: string[]) {
    if (this.busy || this.job) { if (this.job) this.say('a fit is running: wait for it (or Cancel it)', true); return; }
    const job = this.startJob(`fitting the frames of ${names.length === 1 ? `"${names[0]}"` : `the ${type} instances`} onto ${this.masterLabel(type)}`);
    this.runOut(job, `fit the frames of ${names.length === 1 ? `"${names[0]}"` : `the ${type} instances`}`, instops.fitInstanceFrames(this.instCtx(type, job), names));
  }

  /**
   * Fit one instance and its frames (instops.ts fitInstanceAll): the whole onto the master, then each frame
   * onto the master and its other frames, on the worker pool; one action at the end.
   */
  private fitInstanceAll(type: string, name: string) {
    if (this.busy || this.job) { if (this.job) this.say('a fit is running: wait for it (or Cancel it)', true); return; }
    const job = this.startJob(`fitting "${name}" and its frames onto ${this.masterLabel(type)}`);
    this.runOut(job, `fit "${name}" and its frames`, instops.fitInstanceAll(this.instCtx(type, job), name));
  }

  /** For the Align tool's Auto-Align: a loaded scan's shown points as the editor draws them, less its T (thinned). */
  alignPoints(id: string): { xyz: Float32Array; nrm: Float32Array } | null {
    const sc = this.scans.get(id);
    if (!sc) return null;
    const s = this.placed(sc), live = this.live(sc), Ti = inverse(s.T), R = Ti.R, t = Ti.t;
    let n = 0;
    for (let i = 0; i < s.nv; i++) if (live[i] && s.N[3 * i] === s.N[3 * i]) n++;
    const step = Math.max(1, Math.ceil(n / 120000)), m = Math.ceil(n / step), xyz = new Float32Array(3 * m), nrm = new Float32Array(3 * m);
    let j = 0, k = 0;
    for (let i = 0; i < s.nv && j < m; i++) {
      if (!live[i] || s.N[3 * i] !== s.N[3 * i]) continue;
      if (k++ % step) continue;
      const a = s.P[3 * i], b = s.P[3 * i + 1], c = s.P[3 * i + 2], d = s.N[3 * i], e = s.N[3 * i + 1], f = s.N[3 * i + 2], o = 3 * j++;
      xyz[o] = R[0] * a + R[1] * b + R[2] * c + t[0]; xyz[o + 1] = R[3] * a + R[4] * b + R[5] * c + t[1]; xyz[o + 2] = R[6] * a + R[7] * b + R[8] * c + t[2];
      nrm[o] = R[0] * d + R[1] * e + R[2] * f; nrm[o + 1] = R[3] * d + R[4] * e + R[5] * f; nrm[o + 2] = R[6] * d + R[7] * e + R[8] * f;
    }
    return { xyz: xyz.subarray(0, 3 * j), nrm: nrm.subarray(0, 3 * j) };
  }

  /** Save the Align tool's corrections T (by scan id) into the scans' edits, as one action (see realign.ts). Returns the scans saved. */
  async saveAlignment(moved: Map<string, Rigid>): Promise<string[]> {
    if (this.busy || this.job) { this.say('wait for the running action first', true); return []; }
    const by = new Map<Scan, OpBody[]>(), done: string[] = [];
    for (const [id, T] of moved) {
      const sc = this.scans.get(id);
      if (!sc || sc.s.graph.stale) continue;
      const ops = realignOps(sc.s.state, T) as OpBody[];
      by.set(sc, ops);
      done.push(id);
    }
    if (!by.size) { this.say('nothing saved: the moved scans are not loaded for editing (tick them in Scans, under Classes)', true); return []; }
    this.act(`save the alignment of ${done.length} scan${done.length === 1 ? '' : 's'}`, sc => by.get(sc) ?? null,
      { also: sc => by.has(sc), keepSel: true, keepDeleted: true });
    await this.idle();
    return done;
  }

  /** Resolves once no action is running (an act() commits in the background). */
  private async idle() { await nextFrame(); while (this.busy) await nextFrame(); }

  // ── density: fitting onto where the instances agree (in a worker, density.ts) ──
  /**
   * Fit every instance, then its frames, onto where the instances agree, in a worker so the page stays
   * responsive; progress (with Cancel) shows in the viewer. Other edits wait until it is done, since its
   * result is computed from the points as they were when it started. All of it is one Undo.
   */
  private densityFit(type: string, rounds = 3) {
    if (this.busy || this.job) return;
    if (this.instancesOf(type).length < 2) { this.say(`density fit needs at least two ${type} instances with points`); return; }
    const job = this.startJob('density fit'), msg = `density fit of the ${type} instances`;
    this.runOut(job, msg, instops.densityFit(this.instCtx(type, job), rounds), { t: new Date().toISOString(), msg });
  }

  // ── background jobs: a pool of workers, and progress in the viewer ────
  /** A pool of workers (densityworker.ts) for runTask tasks; transfer hands a task's arrays over instead of copying them. */
  private makePool() {
    const K = Math.max(2, Math.min(8, (navigator.hardwareConcurrency || 4) - 1));
    const url = URL.createObjectURL(new Blob([densityWorkerSrc], { type: 'text/javascript' }));
    const pool = Array.from({ length: K }, () => new Worker(url)), free = [...pool], queue: ((w: Worker) => void)[] = [];
    const pending = new Map<number, { ok: (r: TaskResult) => void; fail: (e: Error) => void }>();
    let nextId = 0, stopped = false;
    const stop = (why: string) => {
      if (stopped) return;
      stopped = true;
      for (const w of pool) w.terminate();
      URL.revokeObjectURL(url);
      for (const p of pending.values()) p.fail(new Error(why));
      pending.clear();
    };
    for (const w of pool) {
      w.onmessage = (e: MessageEvent<{ id: number; result?: TaskResult; error?: string }>) => {
        const p = pending.get(e.data.id);
        if (!p) return;
        pending.delete(e.data.id);
        const q = queue.shift();
        if (q) q(w); else free.push(w);
        if (e.data.error) p.fail(new Error(e.data.error)); else p.ok(e.data.result ?? null);
      };
      w.onerror = e => stop(`a worker failed: ${e.message}`);
    }
    const exec = (task: Task, transfer = false) => new Promise<TaskResult>((ok, fail) => {
      if (stopped) { fail(new Error('cancelled')); return; }
      const go = (w: Worker) => {
        const id = nextId++;
        pending.set(id, { ok, fail });
        const bufs = !transfer ? [] : task.kind === 'fit' ? [task.xyz.buffer, task.nrm.buffer, ...task.list.map(l => l.xyz.buffer)]
          : task.kind === 'onto' ? [task.xyz.buffer, task.nrm.buffer, task.src.buffer]
          : task.kind === 'shape' ? [task.xyz.buffer, task.nrm.buffer]
          : task.kind === 'pdensity' ? [task.xyz.buffer] : [];   // (its sample goes to every worker)
        w.postMessage({ id, task }, [...new Set(bufs)]);
      };
      const w = free.pop();
      if (w) go(w); else queue.push(go);
    });
    return { K, exec, stop };
  }

  /** The background job running in the viewer (a fit or the density fit), if any. */
  private job: { title: string } | null = null;

  /**
   * Start a background job: a worker pool, and a progress bar with Cancel at the top of the viewer panel.
   * While it runs the panel isn't redrawn (so its progress stays put) and other edits wait, since its
   * result comes from the points as they were when it started.
   */
  private startJob(title: string, box = this.$('eview'), before: Element | null = box.children[1] ?? null) {
    const pool = this.makePool(), wrap = el('div', { class: 'ejob' }), row = el('div', { class: 'row' });
    const bar = el('progress', { max: '1', value: '0' }) as HTMLProgressElement, txt = el('div', { class: 'mono' }, `${title} (${pool.K} workers): starting…`);
    const cancel = el('button', { class: 'small', title: 'stop it (nothing is changed)' }, 'Cancel');
    bar.style.flex = '1';
    row.append(bar, cancel);
    wrap.append(row, txt);
    box.insertBefore(wrap, before);   // under the viewer's heading (or where the caller asks), where it is seen
    wrap.scrollIntoView({ block: 'nearest' });
    const t0 = performance.now();
    this.job = { title };
    const head = proc(title, { cancel: () => pool.stop('cancelled') });   // in the status bar too, with the others
    const end = () => { pool.stop('done'); wrap.remove(); head.end(); this.job = null; };
    cancel.onclick = () => pool.stop('cancelled');
    return {
      K: pool.K, exec: pool.exec, end,
      set: (text: string, frac: number) => {
        bar.value = frac;
        const s = (performance.now() - t0) / 1000, left = frac > 0.03 && frac < 1 ? s / frac - s : NaN;
        const eta = Number.isFinite(left) ? `about ${left < 90 ? `${Math.ceil(left)} s` : `${Math.ceil(left / 60)} min`} left` : '';
        txt.textContent = `${title} (${pool.K} workers): ${text} · ${Math.round(100 * frac)}%${eta ? `, ${eta}` : ''}`;
        head.set(`${text}${eta ? ` (${eta})` : ''}`, frac);
      },
      fail: (e: Error) => {
        end();
        this.viewNote = e.message === 'cancelled' ? `${title} cancelled: nothing was changed` : `${title} failed: ${e.message}`;
        if (e.message !== 'cancelled') this.say(this.viewNote, true);
        this.refresh();
      },
    };
  }


  private supCache: { key: string; sup: Uint16Array } | null = null;
  private supRun: { key: string; p: Promise<Uint16Array> } | null = null;
  /** The points whose support run was cancelled (supportFor doesn't start it again for them). */
  private supStopped: string | null = null;
  /** In the viewer, per scan: the shown points below the density threshold (what Release hidden takes out). */
  private lowMasks: Map<Scan, Uint8Array> | null = null;

  /**
   * Support of the overlay's points (see instances.ts support), from the worker pool, kept until the points
   * change. null while it is being worked out; the viewer redraws when it is in.
   */
  private supportFor(xyz: Float32Array, inst: Int32Array): Uint16Array | null {
    const key = this.supKey(xyz, inst);
    if (this.supCache?.key === key) return this.supCache.sup;
    if (this.supStopped === key) return null;   // cancelled: not started again by every redraw
    if (this.supRun?.key !== key) this.supportOf(xyz, inst).then(() => this.refresh(), () => {});
    return null;
  }

  private supKey(xyz: Float32Array, inst: Int32Array) {
    let sum = inst.length;
    for (let i = 0; i < xyz.length; i += 301) sum += xyz[i];   // cheap fingerprint of the points
    return `${inst.length}|${sum}`;
  }

  /** The same, to wait for (one run per set of points). */
  private supportOf(xyz: Float32Array, inst: Int32Array): Promise<Uint16Array> {
    const key = this.supKey(xyz, inst);
    if (this.supCache?.key === key) return Promise.resolve(this.supCache.sup);
    if (this.supRun?.key === key) return this.supRun.p;
    const pool = this.makePool();
    this.say('measuring where the instances agree…');
    this.supStopped = null;
    const head = proc('measuring where the instances agree', { cancel: () => { this.supStopped = key; pool.stop('cancelled'); } });
    const p = instops.supportOf(pool.exec, pool.K, xyz, inst).then(sup => {
      if (this.supRun?.key === key) { this.supCache = { key, sup }; this.supRun = null; }
      return sup;
    }, e => {
      if (this.supRun?.key === key) this.supRun = null;
      const m = (e as Error).message;
      this.say(m === 'cancelled' ? 'density cancelled (tick density colours again to measure)' : `density: ${m}`, m !== 'cancelled');
      throw e;
    }).finally(() => { pool.stop('done'); head.end(); });
    this.supRun = { key, p };
    return p;
  }

  // ── point density (shading mode 8, and the Density rule): each point's neighbours within r, on the worker pool ──
  /** The latest measurement: per ticked scan, each vertex's density (0: deleted), for the points and radius of key. */
  private densCache: Dens | null = null;
  /** The points (and radius) the shading shows, with the edit clouds they were set on. */
  private densShown: { key: string; clouds: unknown[] } | null = null;
  private densRun: { key: string; p: Promise<Dens>; stop: () => void } | null = null;
  /** The points whose density run was cancelled (the shading doesn't start it again by every refresh). */
  private densStopped: string | null = null;

  /** What a measurement at radius r is of: every point of the ticked scans, deleted ones aside (they overlap: counted against each other). */
  private densInput(r: number) {
    const pts = [...this.scans.values()].filter(sc => !this.off(sc)).map(sc => {
      const s = this.placed(sc), keep = new Uint8Array(s.nv), fl = s.state.flags;
      let n = 0, sum = 0;
      for (let v = 0; v < s.nv; v++) if (!(fl[s.pidOfV[v]] & DELETED)) { keep[v] = 1; n++; }
      for (let i = 0; i < s.P.length; i += 301) sum += s.P[i];   // cheap fingerprint of where they are
      return { s, keep, n, id: `${s.scan}:${n}:${sum}` };
    });
    const total = pts.reduce((a, p) => a + p.n, 0), xyz = () => {
      const out = new Float32Array(3 * total);
      let o = 0;
      for (const { s, keep } of pts) for (let v = 0; v < s.nv; v++) if (keep[v]) { out[o++] = s.P[3 * v]; out[o++] = s.P[3 * v + 1]; out[o++] = s.P[3 * v + 2]; }
      return out;
    };
    // neighbours counted among at most ~1.5M of them (every step-th), scaled up: the counts hardly change
    const step = Math.max(1, Math.ceil(total / 1.5e6));
    const sample = (P: Float32Array) => {
      const out = new Float32Array(3 * Math.ceil(total / step));
      for (let i = 0, j = 0; i < total; i += step, j++) out.set(P.subarray(3 * i, 3 * i + 3), 3 * j);
      return out;
    };
    return { key: `${r}|${pts.map(p => p.id).join('|')}`, r, pts, total, step, xyz, sample };
  }

  /** A measurement from every kept point's density (in densInput order): per scan, and the 2nd to 98th percentile. */
  private densResult(inp: ReturnType<EditUI['densInput']>, all: Uint16Array): Dens {
    const { total } = inp, k = Math.max(1, Math.floor(total / 200000)), q = Uint16Array.from({ length: Math.ceil(total / k) }, (_, i) => all[i * k]).sort();
    const lo = q[Math.floor(0.02 * (q.length - 1))] ?? 1, hi = q[Math.floor(0.98 * (q.length - 1))] ?? 1;
    const d = new Map<string, Uint16Array>();
    let a = 0;
    for (const { s, keep } of inp.pts) {
      const x = new Uint16Array(s.nv);
      for (let v = 0; v < s.nv; v++) if (keep[v]) x[v] = all[a++];
      d.set(s.scan, x);
    }
    return { key: inp.key, r: inp.r, step: inp.step, d, range: [Math.max(1, lo), Math.max(lo + 1, hi)] };
  }

  /** Densities at radius r: the latest measurement if the points are as they were, else measured on the worker pool (one run at a time). */
  private densityOf(r: number): Promise<Dens> {
    const inp = this.densInput(r), key = inp.key;
    if (this.densCache?.key === key) return Promise.resolve(this.densCache);
    if (this.densRun?.key === key) return this.densRun.p;
    this.densRun?.stop();
    if (!inp.total) return Promise.reject(new Error('no points to measure'));
    const xyz = inp.xyz(), sample = inp.sample(xyz), { total, step } = inp;
    const pool = this.makePool(), parts = pool.K * 2, cuts = Array.from({ length: parts + 1 }, (_, i) => Math.round((i * total) / parts));
    const head = proc(`measuring point density (${total.toLocaleString()} points)`, { cancel: () => { this.densStopped = key; pool.stop('cancelled'); } });
    let done = 0;
    const p = Promise.all(cuts.slice(0, -1).map((from, i) => pool.exec({ kind: 'pdensity', sample, step, xyz: xyz.slice(3 * from, 3 * cuts[i + 1]), r }, true)
      .then(d => { head.set(`${++done} of ${parts}`, done / parts); return d as Uint16Array; })))
      .then(ds => {
        const all = new Uint16Array(total);
        ds.forEach((d, i) => all.set(d, cuts[i]));
        const res = this.densResult(inp, all);
        if (this.densRun?.key === key) this.densCache = res;
        return res;
      })
      .finally(() => { pool.stop('done'); head.end(); if (this.densRun?.key === key) this.densRun = null; });
    this.densRun = { key, p, stop: () => pool.stop('replaced') };
    return p;
  }

  /**
   * Point density shading: when it is chosen, show each point's density (densityOf), measured again whenever
   * the points move, go or come back.
   */
  shading() {
    if (!this.on) return;
    if (this.v.s.mode === 9) this.featShading(); else { this.featRun?.stop('replaced'); this.featStopped = null; }
    if (this.v.s.mode === 10) this.weightShading(); else this.dropWeightImages();
    this.dataShown();
    if (this.v.s.mode !== 8) { this.densStopped = null; return; }
    const r = this.v.s.densR, key = this.densInput(r).key, clouds = [...this.scans.values()].filter(sc => !this.off(sc)).map(sc => this.v.edits.get(sc.s.scan));
    if (this.densShown?.key === key && this.densShown.clouds.every((c, i) => c === clouds[i])) return;
    if (this.densStopped === key) return;
    if (this.densRun?.key !== key && this.densCache?.key !== key) this.densLegend('measuring…');
    this.densityOf(r).then(res => {
      if (res.key !== key || this.v.s.mode !== 8 || this.v.s.densR !== r) return;
      this.v.densRange = res.range;
      for (const [id, d] of res.d) this.v.setEditDensity(id, d);
      this.densShown = { key, clouds };
      this.onDensity();
      this.densLegend(`blue ${res.range[0].toLocaleString()} or fewer, red (Red at, at the top; in the middle ${res.range[1].toLocaleString()}) or more points within ${Math.round(r * 100)} cm${res.step > 1 ? ` (counted among 1 in ${res.step} of them)` : ''} · grey: not measured (load the scan for editing)`);
    }, e => {
      const m = (e as Error).message;
      if (m === 'replaced') return;
      this.densLegend(m === 'cancelled' ? 'cancelled: pick another shading, then Point density again, to measure' : `failed: ${m}`);
      if (m !== 'cancelled') this.say(`point density: ${m}`, true);
    });
  }

  // ── image features (shading mode 9): each point marked with what its frame's photo and depth have where it came from ──
  /** Features of whole photos the shading found itself (slimmed), per '<scan>:<frame>': the photo steps' own come first. */
  private featCache = new Map<string, FrameFeats>();
  /** Bumped when the photo steps' features change (a Photo features step, Clear photo results). */
  private featVer = 0;
  /** What the shading shows: its key, and the edit clouds it was set on. */
  private featShown: { key: string; clouds: unknown[] } | null = null;
  private featRun: { key: string; stop: (why: string) => void } | null = null;
  private featStopped: string | null = null;

  /**
   * Image features shading: when it is chosen, every shown scan's points marked from its frames' features: the photo
   * steps' (what a macro's Photo features found), else found here on the whole photo (the photo pool, once a frame;
   * the scans' points go up as each scan is done).
   */
  private featShading() {
    const scans = [...this.scans.values()].filter(sc => !this.off(sc)), clouds = scans.map(sc => this.v.edits.get(sc.s.scan));
    const key = `${this.featVer}|${scans.map(sc => sc.s.scan).join(',')}`;
    if (this.featShown?.key === key && this.featShown.clouds.every((c, i) => c === clouds[i])) return;
    if (this.featRun?.key === key || this.featStopped === key) return;
    this.featRun?.stop('replaced');
    const ps = scans.map(sc => this.photoScan(sc)), withPhotos = ps.filter(p => p.images);
    if (!withPhotos.length) { this.featLegend('these scans have no camera images (prep/images.py --scene, then Publish)'); this.featShown = { key, clouds }; return; }
    const ffOf = (k: string) => this.photo.feats.get(k) ?? this.featCache.get(k), mine = this.photo.feats.size;
    const legend = (pending: string) => this.featLegend(`${pending}photo lines red, corners green, depth jumps cyan, creases blue (Show: which), on the scans' colours dimmed · grey: no photo${mine ? ' · frames a Photo features step looked at show what it found (kept to its mask)' : ''}`);
    const show = (p: PhotoScan) => {
      const m = new Map<number, { cam: NonNullable<ReturnType<PhotoRun['camOf']>>; ff: FrameFeats }>();
      for (const f of this.photo.framesOf(p, 'all')) { const ff = ffOf(`${p.id}:${f}`), cam = this.photo.camOf(p, f); if (ff && cam) m.set(f, { cam, ff }); }
      if (p.frame) this.v.setEditFeatures(p.id, featCodes(p.P, p.frame, m));
    };
    for (const p of ps) if (!p.images) this.v.setEditFeatures(p.id, null);
    const o = { what: 'both' as const, n: 400, len: 20, bendLen: 1e9 }, skey = (p: PhotoScan, f: number) => featKey(p.images!.src ?? p.images!.url, f, o);
    let alive = true, pool: ReturnType<typeof makePool<PhotoTask, PhotoResult>> | null = null, head: ReturnType<typeof proc> | null = null;
    const stop = (why: string) => {
      if (!alive) return;
      alive = false; pool?.stop(why); head?.end();
      if (this.featRun?.key === key) this.featRun = null;
      if (why === 'cancelled') legend('cancelled (pick another shading, then Image features again, to go on) · ');
    };
    this.featRun = { key, stop };
    void (async () => {
      // the frames not looked at yet: from this browser's store first (photos looked at before), the rest on the photo pool
      const todo: { p: PhotoScan; fs: number[] }[] = [];
      for (const p of withPhotos) {
        const fs = this.photo.framesOf(p, 'all').filter(f => !ffOf(`${p.id}:${f}`)), got = await getFeats(fs.map(f => skey(p, f)));
        if (!alive) return;
        fs.forEach((f, i) => { const k = got[i]; if (k) this.featCache.set(`${p.id}:${f}`, unkeep(`${p.id}:${f}`, k)); });
        todo.push({ p, fs: fs.filter((_, i) => !got[i]) });
      }
      const total = todo.reduce((a, t) => a + t.fs.length, 0);
      if (!total) { for (const p of withPhotos) show(p); this.featShown = { key, clouds }; stop('done'); legend(''); this.redrawPhotos(); return; }
      pool = makePool<PhotoTask, PhotoResult>(photoWorkerSrc, { key: 'task' });
      head = proc(`finding image features (${total.toLocaleString()} photos)`, { cancel: () => { this.featStopped = key; stop('cancelled'); } });
      let done = 0;
      legend(`finding features: 0 of ${total} photos · `);
      await Promise.all(todo.map(async ({ p, fs }) => {
        // kept in this browser a batch at a time (a tab closed midway keeps what it found)
        const url = new URL(p.images!.url, location.href).href, writes: Promise<void>[] = [];
        let kept: [string, ReturnType<typeof keepFeats>][] = [];
        const flush = () => { if (kept.length) writes.push(putFeats(kept)); kept = []; };
        await Promise.all(fs.map(async f => {
          const cam = this.photo.camOf(p, f)!, ff = await pool!.exec({ kind: 'detect', jpg: `${url}${f}.jpg`, d16: `${url}${f}.d16`, cam, mask: null, o });
          if (!alive) return;
          this.featCache.set(cam.key, slim(ff));
          kept.push([skey(p, f), keepFeats(ff)]);
          if (kept.length >= 16) flush();
          head!.set(`${++done} of ${total}`, done / total);
          legend(`finding features: ${done} of ${total} photos · `);
        }));
        flush();
        await Promise.all(writes);
        if (!alive) return;
        show(p);
      }));
      if (!alive) return;
      for (const p of withPhotos) show(p);
      this.featShown = { key, clouds };
      stop('done');
      legend('');
      this.redrawPhotos();
    })().catch(e => {
      const m = (e as Error).message;
      if (!alive || m === 'replaced' || m === 'cancelled') return;
      stop('failed');
      this.featLegend(`failed: ${m}`);
      this.say(`image features: ${m}`, true);
    });
  }

  // ── image weights (shading mode 10): every frame's photo, blended by its points' weights (weights.ts) ──
  /** Bumped when weights change (a weight step, Undo / Redo). */
  private wVer = 0;
  /**
   * Per scan whose photos are drawn (Viewer images 'wimg:<scan>:<f>'): its frames, the weights they show (wVer), each
   * frame's depth and its points with their pixels (a frame's photo and points move together: they hold), and the
   * selection they show (its frames' selection grids are made again where it changes), and each frame's photo's
   * colour grid (Match exposure).
   */
  private wShown = new Map<string, { sc: Scan; frames: number[]; ver: number; depth: Map<number, Float32Array>; at: Map<number, { idx: number[]; pix: Int32Array }>; sel: Uint8Array | null; grid: Map<number, Uint8Array> }>();
  private wRun: { stop: () => void } | null = null;
  /** The photo pool decoding and processing them (photoworker.ts 'show'), what the drawn photos are (op) and whether their exposures are matched. */
  private wPool: ReturnType<typeof makePool<ShowTask, ShownPhoto>> | null = null;
  private wOp: PhotoOp = 'rgb';
  private wExpo: boolean | null = false;   // null: not worked out for the photos as drawn
  private photoPool() { return this.wPool ??= makePool<ShowTask, ShownPhoto>(photoWorkerSrc, { key: 'task' }); }
  /** (the worker runs from a blob: URL, so the photo's URL is absolute) */
  private showTask(ci: CamImages, f: number, op: PhotoOp): ShowTask { return { kind: 'show', jpg: new URL(ci.url + `${f}.jpg`, location.href).href, maxW: 640, op, gw: ci.dw >> 1, gh: ci.dh >> 1 }; }

  /** Image weights shading: when it is chosen, every shown scan's photos (loaded once), each with its points' weights. */
  private weightShading() {
    const scans = [...this.scans.values()].filter(sc => !this.off(sc));
    for (const [id, x] of this.wShown) if (!scans.includes(x.sc)) { this.v.removeImages(`wimg:${id}:`); this.wShown.delete(id); }
    if (!this.wRun) for (const x of this.wShown.values()) { if (x.ver !== this.wVer) this.weightGrids(x.sc); this.selGrids(x.sc); }   // (a load under way does them when done)   // a frame's photo and points move together: its grids hold
    const todo = scans.filter(sc => sc.s.images && sc.s.frame && !this.wShown.has(sc.s.scan)), op = this.v.s.imgOp;
    const legend = (doing = '') => this.$('wlegend').textContent = !scans.some(sc => sc.s.images) ? 'these scans have no camera images (prep/images.py --scene, then Publish)'
      : `every frame's photo on its depth, each pixel the photos there averaged by their points' weights (Set weight, Blur weights; 1 unless set); weight 0: see-through (the points aren't drawn)${doing}`;
    if (this.wRun) return;
    // loaded already: processed again (another Photos choice), or their exposures matched (or not) again
    const redo = !todo.length && this.wOp !== op;
    if (!todo.length && !redo) {
      if (this.wExpo !== this.expoWanted()) this.weightGains();
      legend(this.wExpo ? ' · exposures matched' : '');
      return;
    }
    let alive = true;
    const head = proc(redo ? 'image weights: processing photos' : 'image weights: loading photos', { cancel: () => stop() });
    const stop = () => {
      if (!alive) return;
      alive = false; head.end(); this.wRun = null; legend();
      this.wPool?.stop('cancelled'); this.wPool = null;   // what is still queued goes (a new pool when wanted)
    };
    this.wRun = { stop };
    legend(redo ? ' · processing photos…' : ' · loading photos…');
    void (async () => {
      const pool = this.photoPool();
      let jobs: { sc: Scan; f: number }[], k = 0, failed = 0, one: (j: { sc: Scan; f: number }) => Promise<void>;
      if (redo) {
        jobs = [...this.wShown.values()].flatMap(x => x.frames.map(f => ({ sc: x.sc, f })));
        one = async ({ sc, f }) => {
          let r: ShownPhoto;
          try { r = await pool.exec(this.showTask(sc.s.images!, f, op)); } catch { failed++; return; }
          const x = this.wShown.get(sc.s.scan);
          if (!alive || x?.sc !== sc) { r.img.close(); return; }
          x.grid.set(f, r.grid);
          this.v.setImagePhoto(`wimg:${sc.s.scan}:${f}`, r.img);
          head.set(`image weights: ${++k} of ${jobs.length} photos processed`, k / jobs.length);
        };
      } else {
        const of = new Map(todo.map(sc => [sc, byFrame(sc.s.frame!)]));
        jobs = todo.flatMap(sc => [...of.get(sc)!.keys()].filter(f => sc.s.images!.cams.has(f)).sort((a, b) => a - b).map(f => ({ sc, f })));
        for (const sc of todo) this.wShown.set(sc.s.scan, { sc, frames: [], ver: -1, depth: new Map(), at: new Map(), sel: null, grid: new Map() });
        one = async ({ sc, f }) => {
          const s = sc.s, ci = s.images!, cam = ci.cams.get(f)!;
          let r: ShownPhoto, depth: Float32Array;
          try { [r, depth] = await Promise.all([pool.exec(this.showTask(ci, f, op)), loadDepth(ci, f)]); } catch { failed++; return; }
          const x = this.wShown.get(s.scan);
          if (!alive || x?.sc !== sc) { r.img.close(); return; }
          const idx = of.get(sc)!.get(f) ?? [];
          x.frames.push(f); x.depth.set(f, depth); x.grid.set(f, r.grid); x.at.set(f, { idx, pix: pixelsOf(s.P, idx, this.depthFrame(sc, f, depth)) });
          this.v.setImage(`wimg:${s.scan}:${f}`, { depth, dw: ci.dw, dh: ci.dh, K: cam.K, img: r.img, model: mat4(compose(this.framePlace(sc, f), cam.pose)), of: s.scan, weight: new Float32Array(ci.dw * ci.dh) });
          head.set(`image weights: ${++k} of ${jobs.length} photos`, k / jobs.length);
        };
      }
      const B = 2 * pool.K;   // the pool's workers kept busy, the page not flooded
      for (let i = 0; i < jobs.length && alive; i += B) await Promise.all(jobs.slice(i, i + B).map(one));
      if (!alive) return;
      this.wOp = op;
      if (!redo) for (const sc of todo) if (this.wShown.get(sc.s.scan)?.sc === sc) { this.weightGrids(sc); this.selGrids(sc); }
      this.wExpo = null;   // new photos and grids: their gains made again below
      if (failed) this.say(`image weights: ${failed} photo${failed === 1 ? '' : 's'} couldn't be ${redo ? 'processed' : 'loaded'}`, true);
      stop();
      if (this.v.s.mode === 10) this.weightShading(); else this.dropWeightImages();
    })();
  }

  /** Match exposure is on and means something for the photos as drawn (not for their edges). */
  private expoWanted() { return this.v.s.imgExpo && keepsExposure(this.wOp); }

  /**
   * Each shown frame's photo's colour gain (exposure.ts: neighbouring frames compared where they see the same
   * surface, as the frames are placed now), or none (Match exposure off). Grey photos: one gain, the channels' mean.
   */
  private weightGains() {
    const on = this.expoWanted();
    for (const x of this.wShown.values()) {
      const s = x.sc.s, ci = s.images!, fs = x.frames.slice().sort((a, b) => a - b), id = (f: number) => `wimg:${s.scan}:${f}`;
      if (!on) { for (const f of fs) this.v.setImagePhoto(id(f), null, null); continue; }
      const ef = fs.map(f => ({ fr: this.depthFrame(x.sc, f, x.depth.get(f)!), grid: x.grid.get(f)!, gw: ci.dw >> 1, gh: ci.dh >> 1, ...x.at.get(f)! }));
      const pairs = neighbourPairs(fs.length).flatMap(([a, b]) => { const r = pairRatio(s.P, ef[a], ef[b]); return r ? [{ a, b, ...r }] : []; });
      const g = solveGains(fs.length, pairs);
      fs.forEach((f, i) => {
        const c: [number, number, number] = [g[3 * i], g[3 * i + 1], g[3 * i + 2]];
        if (this.wOp === 'grey') c.fill(Math.cbrt(c[0] * c[1] * c[2]));
        this.v.setImagePhoto(id(f), null, c);
      });
    }
    this.wExpo = on;
  }

  /** A shown scan's photos' weights, from its points' weights as they are now. */
  private weightGrids(sc: Scan) {
    const x = this.wShown.get(sc.s.scan), s = sc.s;
    if (!x) return;
    const live = this.live(sc);
    for (const f of x.frames) {
      const { idx, pix } = x.at.get(f)!;
      this.v.setImageWeights(`wimg:${s.scan}:${f}`, frameWeightGrid(s.weight, idx, pix, this.depthFrame(sc, f, x.depth.get(f)!), i => !!live[i]));
    }
    x.ver = this.wVer;
  }

  /** A shown scan's photos' selection grids (tinted, or alone in Selection only), again for the frames whose selection changed. */
  private selGrids(sc: Scan) {
    const x = this.wShown.get(sc.s.scan), s = sc.s, m = sc.mask;
    if (!x || !s.frame || !x.frames.length) return;
    const was = x.sel, changed = new Set<number>();
    if (!m && !was) return;
    for (let i = 0; i < s.nv; i++) if ((m ? m[i] : 0) !== (was ? was[i] : 0)) changed.add(s.frame[i]);
    for (const f of x.frames) {
      if (!changed.has(f)) continue;
      const { idx, pix } = x.at.get(f)!;
      this.v.setImageWeights(`wimg:${s.scan}:${f}`, null, m ? frameSelGrid(m, idx, pix, this.depthFrame(sc, f, x.depth.get(f)!)) : null);
    }
    x.sel = m ? m.slice() : null;
  }

  /** Out of Image weights shading: its photos go (loaded again when it is chosen again). */
  private dropWeightImages() {
    this.wRun?.stop();
    this.wPool?.stop('done'); this.wPool = null;
    if (!this.wShown.size) return;
    this.v.removeImages('wimg:');
    this.wShown.clear();
    this.wOp = 'rgb'; this.wExpo = false;
  }

  private featLegend(text: string) {
    const l = this.$('featlegend');
    l.textContent = text;
  }

  private densLegend(text: string) {
    const l = this.$('denslegend');
    l.textContent = text;
  }

  /**
   * Absorb: points of a class (asked for) that are in no object join an instance where they belong. Each
   * goes with the shown instance whose points are nearest it in its scan (within ABSORB_REACH), is placed in
   * the overlay as that instance is (its frame correction and fit), and is taken if it lands within 3 cm of
   * the dense part: overlay points at least `need` of the other instances agree with (the density threshold
   * if set, else a quarter of them, as the density fit's target). One action.
   */
  private async absorb(type: string) {
    const vw = this.viewing, ov = this.ov;
    if (!vw || !ov || this.busy) return;
    if (this.job) { this.say(`${this.job.title} is running: wait for it (or Cancel it)`, true); return; }
    const names = this.instancesOf(type), others = names.length - 1;
    if (others < 1) { this.say(`absorb needs at least two ${type} instances (it goes by where they agree)`); return; }
    const classes = ['unclassified', ...CLS.slice(1), ...this.userClasses().keys()];
    let cls = prompt(`Absorb points of which class? (${classes.join(', ')})`, this.lastAbsorb)?.trim();
    if (!cls) return;
    if (cls === 'unclassified') cls = 'none';
    if (!(CLS as readonly string[]).includes(cls) && !this.userClasses().has(cls)) { this.say(`no class "${cls}"`, true); return; }
    this.lastAbsorb = cls === 'none' ? 'unclassified' : cls;
    const need = vw.thresh || Math.max(1, Math.ceil(0.25 * others));
    const sup = await this.supportOf(ov.xyz, ov.ik);
    if (this.viewing !== vw) return;   // closed meanwhile
    // the dense part of the overlay
    let nd = 0;
    for (const x of sup) if (x >= need) nd++;
    const dense = new Float32Array(3 * nd);
    for (let i = 0, j = 0; i < sup.length; i++) if (sup[i] >= need) dense.set(ov.xyz.subarray(3 * i, 3 * i + 3), 3 * j++);
    if (!nd) { this.say(`no points where ${need} of the other ${others} instances agree`); return; }
    const into = new Set(names.filter(n => !vw.off.has(n)));
    const r = instops.absorb({ scans: this.placedAll(), type, info: this.objectInfo() }, cls, into, dense, s => s.state.classes);
    const shown = cls === 'none' ? 'unclassified' : cls;
    this.viewNote = `absorb: ${r.total ? `${r.total.toLocaleString()} ${shown} ${r.note.replace(/^[\d,.]+ /, '')}` : `no ${shown} points outside objects land`} where at least ${need} of the other ${others} instances agree`;
    if (!r.total) { this.say(this.viewNote); return; }
    this.commitOut(`absorb ${r.total.toLocaleString()} ${shown} points into the ${type} instances`, r.bodies);
  }
  private lastAbsorb = 'unclassified';

  /** Take the shown points below the density threshold out of their instances (one action). */
  private releaseLow(type: string) {
    const low = this.lowMasks;
    let total = 0;
    for (const m of low?.values() ?? []) for (const x of m) total += x;
    if (!low || !total) { this.say('no points below the threshold'); return; }
    this.act(`take ${total.toLocaleString()} low-density points out of the ${type} instances`, sc => {
      const m = low.get(sc);
      if (!m || !m.some(x => x)) return null;
      return { op: 'object.release', set: sc.s.toSet(m) } as OpBody;
    }, { also: sc => !!low.get(sc)?.some(x => x), keepSel: true, keepDeleted: true });
  }
  private stepStamp: string | null = null;

  /** Clear the frame corrections of a type's instances. */
  private resetFrames(type: string) {
    const bodies = instops.resetFrames(this.placedAll(), this.instancesOf(type));
    this.viewNote = '';
    if (!bodies.size) { this.say(`the ${type} instances have no frame corrections`); return; }
    this.commitOut(`reset the frame corrections of the ${type} instances`, bodies);
  }

  /** Turn an instance (in the overlay) by a yaw about its centre, and shift it. */
  private turnInstance(type: string, name: string, deg: number, d: V3 = [0, 0, 0]) {
    const fits = instops.turnFits(this.placedAll(), this.objectInfo(), [name], deg, d);
    if (fits.size) this.commitFits(`turn "${name}" ${deg}°${d.some(x => x) ? ` and shift ${d.join(' ')} m` : ''}`, type, fits);
  }

  /**
   * Move an instance in the overlay with the gizmo: it is hidden there while a ghost of it moves, and
   * Apply makes its fit F' = W · F (one action).
   */
  private moveInstance(type: string, name: string) {
    const vw = this.viewing;
    if (!vw) return;
    this.gizmo?.cancel();
    const F = this.objectInfo().get(name)?.F ?? identity(), p = this.objectPts([name]).get(name)!;
    if (!p.xyz.length) { this.say(`no points of "${name}"`); return; }
    const wasOff = vw.off.has(name), k = this.instancesOf(type).indexOf(name);
    vw.off.add(name);
    this.refresh();
    this.ghost(p.xyz, p.nrm, p.rgb, instId(Math.max(0, k)));
    const done = () => { this.gizmo = null; this.clearGhost(); if (!wasOff) vw.off.delete(name); this.refresh(); };
    this.gizmo = new Gizmo(this.v, centroid(p.xyz), `Move "${name}" in the overlay`, {
      change: W => this.moveGhost(W),
      apply: (W, made) => {
        done();
        if (isIdentity(W, 1e-9)) return;
        this.commitFits(`move "${name}" (moved ${made.shift.map(x => (x * 100).toFixed(1)).join(' ')} cm, turned ${made.yawDeg.toFixed(2)}°)`, type, new Map([[name, compose(W, F)]]));
      },
      cancel: done,
    });
  }

  /** Show a type's instances overlaid (only them), fitting any that were never fitted. */
  private openView(type: string) {
    const c = this.v.cam;
    this.viewing = { type, off: new Set(), cam: this.viewing?.cam ?? { eye: [...c.eye], yaw: c.yaw, pitch: c.pitch, pc: [...c.pc], ps: c.ps } };
    this.pick(null);
    this.frameView();
    const info = this.objectInfo(), names = this.instancesOf(type);
    const unfitted = (this.hasMaster(type) ? names : names.slice(1)).filter(n => isIdentity(info.get(n)?.F ?? identity()));
    this.refresh();
    if (unfitted.length) this.fitInstances(type, unfitted, `fit ${unfitted.length} new instance(s) of ${type}`);
  }

  private closeView() {
    const vw = this.viewing;
    if (!vw) return;
    this.viewing = null;
    this.viewNote = '';
    this.ov = null;
    for (const sc of this.scans.values()) sc.view = null;
    this.v.solo = null;
    this.v.clearEditCloud(OVERLAY);
    this.$('eview').hidden = true;
    if (vw.cam) { const c = this.v.cam; c.eye = vw.cam.eye; c.yaw = vw.cam.yaw; c.pitch = vw.cam.pitch; c.pc = vw.cam.pc; c.ps = vw.cam.ps; this.ctl.pivot = null; }
    this.v.dirty = true;
    this.refresh();
  }

  /** Point the camera at the overlay. */
  private frameView() {
    const vw = this.viewing;
    if (!vw) return;
    const names = this.instancesOf(vw.type), pts = this.objectPts(names);
    const all = new Float32Array(names.reduce((a, n) => a + pts.get(n)!.xyz.length, 0));
    let o = 0;
    for (const n of names) { all.set(pts.get(n)!.xyz, o); o += pts.get(n)!.xyz.length; }
    if (!all.length) return;
    const c = centroid(all), sm = thin(all, 20000), d: number[] = [];
    for (let i = 0; i < sm.length; i += 3) d.push(Math.hypot(sm[i] - c[0], sm[i + 1] - c[1], sm[i + 2] - c[2]));
    const R = Math.max(0.3, d.sort((a, b) => a - b)[Math.floor(0.9 * (d.length - 1))]);   // stray points don't push the camera back
    const cam = this.v.cam, e: V3 = [c[0] + R * 1.3, c[1] - R * 2.0, c[2] + R * 1.5];
    cam.eye = e; cam.lookAt(c); cam.pc = [c[0], c[1]]; cam.ps = (2.6 * R) / Math.max(300, Math.min(this.v.canvas.clientWidth, this.v.canvas.clientHeight));
    this.ctl.pivot = c; this.ctl.depth = dist(e, c);
    this.v.dirty = true;
  }

  /** The overlay's per-point words: its instance tint, plus the selection and the brush's candidates. */
  private viewWords() {
    const ov = this.ov;
    if (!ov) return;
    const w = new Uint32Array(ov.base.length);
    for (let i = 0; i < w.length; i++) {
      const sc = ov.scans[ov.si[i]], v = ov.v[i];
      // selected / brushed points lose their instance tint, so the selection colour shows on their own colour
      w[i] = sc.mask?.[v] ? (ov.base[i] & ~32) | 2 : sc.cand?.[v] ? (ov.base[i] & ~32) | 4 : ov.base[i];
    }
    this.v.setEditState(OVERLAY, w);
  }

  /** Redraw the overlay and its panel from the current state. */
  private drawView() {
    const vw = this.viewing, box = this.$('eview');
    if (!vw) { box.hidden = true; return; }
    if (this.job) return;   // redrawn once it is done (and its progress stays put meanwhile)
    const names = this.instancesOf(vw.type);
    if (!names.length || !this.scans.size) { this.closeView(); return; }
    const pts = this.objectPts(names), explicit = this.hasMaster(vw.type), scans = [...this.scans.values()];
    // which instance each vertex of each scan is (-1: none, or deleted), and whether it is in the master
    let n = 0;
    const per = scans.map(sc => { this.placed(sc); return this.instanceIx(sc, vw.type, names); });
    const inM = scans.map((sc, j) => this.masterMask(sc, vw.type, per[j], explicit));
    for (const kv of per) for (const k of kv) if (k >= 0) n++;
    const xyz = new Float32Array(3 * n), nrm = new Float32Array(3 * n), rgb = new Uint8Array(3 * n);
    const si = new Uint16Array(n), vv = new Uint32Array(n), base = new Uint32Array(n), ik = new Int32Array(n);
    let o = 0;
    scans.forEach((sc, j) => {
      const s = sc.s, kv = per[j], mm = inM[j], pl = this.placer(sc, true);
      if (!sc.view || sc.view.in.length !== s.nv) sc.view = { P: new Float32Array(3 * s.nv), N: new Float32Array(3 * s.nv), in: new Uint8Array(s.nv) };
      const V = sc.view;
      V.P.fill(NaN); V.N.fill(NaN); V.in.fill(0); V.grid = undefined;
      for (let v = 0; v < s.nv; v++) {
        const k = kv[v];
        if (k < 0) continue;
        const i = 3 * v;
        place(pl(s.state.obj[s.pidOfV[v]], v), s.P, s.N, v, V.P, V.N, v);
        const off = vw.off.has(names[k]) || (!!vw.masterOnly && !mm[v]);
        V.in[v] = off ? 0 : 1;
        xyz.set(V.P.subarray(i, i + 3), 3 * o); nrm.set(V.N.subarray(i, i + 3), 3 * o); rgb.set(s.rgb.subarray(i, i + 3), 3 * o);
        si[o] = j; vv[o] = v; ik[o] = k;
        base[o] = 32 | (instId(k) << 8) | (off ? 128 : 0);   // tinted by instance, like objects
        o++;
      }
    });
    // density: colours, and the threshold (hide points fewer than `thresh` other instances agree with, or with
    // "only below", everything else). Support comes from the worker pool; until it is in, draw without it.
    const thresh = vw.thresh ?? 0, wantSup = (vw.density || thresh > 0) && n > 0;
    const sup = wantSup ? this.supportFor(xyz, ik) : null;
    this.lowMasks = null;
    if (sup && vw.density) {
      const top = Math.max(1, 0.6 * (names.length - 1));
      for (let i = 0; i < n; i++) { rgb.set(heat(sup[i] / top), 3 * i); base[i] &= ~32; }
    }
    if (sup && thresh > 0) {
      const low = scans.map(sc => new Uint8Array(sc.s.nv));
      for (let i = 0; i < n; i++) {
        if (base[i] & 128) continue;   // its instance is switched off (or not in the master, with master only)
        const isLow = sup[i] < thresh, V = scans[si[i]].view!;
        if (isLow) low[si[i]][vv[i]] = 1;
        if (isLow !== !!vw.below) { base[i] |= 128; V.in[vv[i]] = 0; }   // hidden, so it can't be selected either
      }
      this.lowMasks = new Map(scans.map((sc, j) => [sc, low[j]]));
    }
    this.ov = { scans, si, v: vv, base, xyz, ik };
    const { idx, rec } = asCloud(xyz, nrm, rgb), meta = { ...this.v.clouds[0].meta, id: OVERLAY, name: `instances of ${vw.type}`, color: '#ffffff' } as CloudMeta;
    this.v.setEditCloud(meta, idx, [rec], identity(), new Uint16Array(n), new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0]));
    this.v.solo = OVERLAY;
    this.viewWords();

    // the panel: the master, then one row per instance with how well it matches the master (less its own points)
    const m = this.masterOf(vw.type, names), mg = m.inst.length ? new Grid(m.xyz, 0.06) : null;
    const share = (k: number) => {
      const p = thin(pts.get(names[k])!.xyz, 3000), q = p.length / 3;
      if (!mg || !q || !m.inst.some(j => j !== k)) return null;   // nothing in the master but itself
      let hit = 0;
      for (let i = 0; i < q; i++) {
        let ok = false;
        mg.within(p[3 * i], p[3 * i + 1], p[3 * i + 2], 0.03, j => { if (m.inst[j] !== k) ok = true; });
        if (ok) hit++;
      }
      return hit / q;
    };
    const fromN = new Set(m.inst).size, parts = this.instanceParts(vw.type);
    const apart = new Map([...parts].map(([nm, ps]) => {
      const g = this.clusters(ps);
      return [nm, g.length > 1 ? Math.max(...g.slice(1).map(x => Math.hypot(x[0].c[0] - g[0][0].c[0], x[0].c[1] - g[0][0].c[1])) ) : 0] as const;
    }).filter(([, d]) => d > 0));
    box.hidden = false;
    box.textContent = '';
    const hd = el('div', { class: 'layer hd' });
    const b = (lbl: string, tip: string, f: () => void) => { const x = el('button', { class: 'small', title: tip }, lbl); x.onclick = f; return x; };
    hd.append(el('b', {}, `Instances of ${vw.type}`), ' ',
      b('Fit all', 'fit every instance onto the master again', () => this.fitInstances(vw.type, names, `fit the instances of ${vw.type}`)),
      b('Fit frames', "fit each instance's points from each frame onto the master: a better copy of the object (the scans don't move)", () => this.fitInstanceFrames(vw.type, names)),
      b('Reset frames', "clear the instances' frame corrections", () => this.resetFrames(vw.type)),
      b('Density fit', 'fit every instance, and its frames, onto where the instances agree most (the densest points) instead of the master; 3 rounds in the background, one Undo', () => this.densityFit(vw.type)),
      b('Absorb', `add the points of a class (asked for) that are in no object and, placed like the instance nearest them, land where the instances agree (the density threshold, or a quarter of the others), to that instance (shown instances only, within ${ABSORB_REACH} m); one Undo`, () => { this.absorb(vw.type).catch(e => this.say(`absorb: ${(e as Error).message}`, true)); }),
      ...(apart.size ? [b(`Untangle ${apart.size}`, 'some instances have parts in different places in different scans (one name given to different objects): split them into separate instances and fit them', () => this.untangle(vw.type))] : []),
      b('Frame', 'point the camera at them', () => this.frameView()),
      b('Close', 'back to the scans', () => this.closeView()));
    const mr = el('div', { class: 'row' }), only = el('input', { type: 'checkbox' }) as HTMLInputElement;
    only.checked = !!vw.masterOnly;
    only.onchange = () => { vw.masterOnly = only.checked; this.refresh(); };
    const lab = el('label', { title: 'show (and select) only the master points' });
    lab.append(only, ' master only');
    const dn = el('input', { type: 'checkbox' }) as HTMLInputElement, dl = el('label', { title: 'colour each point by how many other instances agree with it (blue: none, red: most)' });
    dn.checked = !!vw.density;
    dn.onchange = () => { vw.density = dn.checked; this.supStopped = null; this.refresh(); };
    dl.append(dn, ' density colours');
    mr.append(el('span', { class: 'mono' }, explicit ? `Master: ${(m.inst.length).toLocaleString()} points from ${fromN} instance${fromN === 1 ? '' : 's'}` : `Master: ${names[0]} (none set)`),
      b('In master', 'add the selected points to the master (from any instance)', () => this.setMaster(vw.type, true)),
      b('Out', 'take the selected points out of the master', () => this.setMaster(vw.type, false)), lab, dl);
    // density threshold: hide points fewer than K other instances agree with (or, only below, everything else)
    const tr = el('div', { class: 'row' }), others = Math.max(1, names.length - 1);
    const sl = el('input', { type: 'range', min: '0', max: String(others), step: '1', value: String(vw.thresh ?? 0), title: 'hide points that fewer than this many of the other instances have a point within 3 cm of' }) as HTMLInputElement;
    const val = el('span', { class: 'mono' }), cur = this.supCache?.sup, lowN = [...(this.lowMasks?.values() ?? [])].reduce((a, m) => a + m.reduce((x, y) => x + y, 0), 0);
    const label = (k: number) => {
      let c = '';
      if (k > 0 && cur && cur.length === n) { let q = 0; for (let i = 0; i < n; i++) if (cur[i] < k) q++; c = `, ${q.toLocaleString()} below`; }
      val.textContent = k ? `agree ≥ ${k} of ${others}${c}` : 'density threshold: off';
    };
    label(vw.thresh ?? 0);
    sl.oninput = () => label(+sl.value);                         // while dragging: just the count
    sl.onchange = () => { vw.thresh = +sl.value; this.refresh(); };  // let go: redraw
    sl.style.flex = '1';
    const bl = el('input', { type: 'checkbox' }) as HTMLInputElement, bll = el('label', { title: 'show only the points below the threshold (and hide the rest)' });
    bl.checked = !!vw.below;
    bl.onchange = () => { vw.below = bl.checked; this.refresh(); };
    bll.append(bl, ' only below');
    const rel = b(`Release ${lowN ? lowN.toLocaleString() + ' ' : ''}below`, 'take the points below the threshold (of the instances shown) out of their instances; one Undo', () => this.releaseLow(vw.type));
    if (!lowN) (rel as HTMLButtonElement).disabled = true;
    tr.append(sl, val, bll, rel);
    box.append(hd, mr, tr, el('div', { class: 'help' }, `The master is the ${vw.type} template the instances are fitted onto${explicit ? '' : `; until points are put In master it is ${names[0]}`}. ` +
      `% = points within 3 cm of the master (less their own points). ${explicit ? '' : `${names[0]} is the master for now and never moves. `} Every tool selects what is shown here; Release, or a class button (Floor, Solid, …), takes them out of their instance. Colours: "tint objects/parts".`));
    if (this.viewNote) box.append(el('div', { class: 'help' }, `Frames: ${this.viewNote}`));
    names.forEach((nm, k) => {
      const off = vw.off.has(nm), r = el('div', { class: 'layer' + (off ? ' off' : '') }), p = pts.get(nm)!;
      const eye = el('button', { class: 'eye', title: off ? 'show' : 'hide' }, off ? '◌' : '●');
      eye.style.color = hueCss(instId(k));
      eye.onclick = () => { if (off) vw.off.delete(nm); else vw.off.add(nm); this.refresh(); };
      const sw = el('span', { class: 'sw' });
      sw.style.background = hueCss(instId(k));
      const sh = k === 0 && !explicit ? null : share(k), fitTxt = (k === 0 && !explicit ? 'master' : '') + (sh === null ? '' : `${Math.round(100 * sh)}%`) || '–';
      const btns = el('span', { class: 'row' });
      // S: show only this instance; again (when it is the only one shown): show them all
      const solo = !off && names.every(x => x === nm || vw.off.has(x));
      const sb = b('S', solo ? 'show all the instances again' : 'show only this instance', () => {
        vw.off = solo ? new Set() : new Set(names.filter(x => x !== nm));
        this.refresh();
      });
      if (solo) sb.classList.add('on');
      btns.append(sb);
      if (k === 0 && explicit) btns.append(
        b('Auto', 'fit it onto the rest of the master', () => this.fitInstances(vw.type, [nm], `fit "${nm}"`)),
        b('Frames', "fit its points from each frame onto the rest of the master (only this instance's points move)", () => this.fitInstanceFrames(vw.type, [nm])));
      if (k > 0) btns.append(
        b('Auto', 'fit it onto the master again', () => this.fitInstances(vw.type, [nm], `fit "${nm}"`)),
        b('Frames', "fit its points from each frame onto the master (only this instance's points move, in the overlay)", () => this.fitInstanceFrames(vw.type, [nm])),
        b('↻90', 'turn it 90° about its centre', () => this.turnInstance(vw.type, nm, 90)),
        b('180', 'turn it 180° about its centre', () => this.turnInstance(vw.type, nm, 180)),
        b('Move', 'move / turn it in the overlay with the gizmo, then Apply', () => this.moveInstance(vw.type, nm)));
      const far = apart.get(nm), label = far ? `${nm} ⚠` : nm;
      r.append(eye, sw, el('span', { class: 'name', title: far ? `${nm}: its parts in different scans are ${far.toFixed(1)} m apart, so they are different objects (Untangle splits them)` : nm }, label), el('span', { class: 'n' }, `${(p.xyz.length / 3).toLocaleString()} · ${fitTxt}`), btns);
      box.append(r);
    });
  }

  private split() {
    const by = ruleBy(this.rule);
    const name = prompt(`Name of the split-off scan (by ${by}); one per scan`, `part ${this.partNames().length + 1}`);
    if (!name) return;
    this.act(`split off "${name}"`, sc => ({ op: 'part.new', part: nextId(sc.s.state.parts), name: name.slice(0, 80), by }) as OpBody);
  }

  private saveGroup() {
    const name = prompt('Group name (letters, digits, space . : + -)', 'floor');
    if (!name) return;
    if (!/^[\w .:+-]{1,60}$/.test(name)) { this.say('bad group name'); return; }
    // every scan that had the group gets the new version too (maybe empty); its points leave any other group
    this.act(`save group "${name}"`, (sc, m) => [...this.outOfOtherGroups(sc, m, name), { op: 'group.save', name, set: sc.s.toSet(m) } as OpBody],
      { keepDeleted: true, keepSel: true, also: sc => sc.s.state.groups.has(name) });
  }

  /** Tidy the loaded scans' groups (groups.ts tidyGroups), as one action. */
  private tidyAllGroups() {
    const by = new Map<Scan, OpBody[]>();
    for (const sc of this.scans.values()) { const ops = tidyGroups(sc.s.state) as OpBody[]; if (ops.length) by.set(sc, ops); }
    if (!by.size) { this.say('the groups are tidy: each point is in one group'); return; }
    if (!confirm(`Tidy the groups of ${by.size} scan${by.size === 1 ? '' : 's'}? Groups that hold others become their parents, and each point stays only in its most specific group. One Undo takes it back.`)) return;
    this.act('tidy groups (one group per point)', sc => by.get(sc) ?? null, { also: sc => by.has(sc), keepSel: true, keepDeleted: true });
  }

  /** group.save ops taking the points m out of every group but keep: a point is in one group at a time. */
  private outOfOtherGroups(sc: Scan, m: Uint8Array, keep: string): OpBody[] {
    const ops: OpBody[] = [];
    for (const g of sc.s.state.groups.keys()) {
      if (g === keep) continue;
      const gm = sc.s.group(g);
      let k = 0;
      for (let i = 0; i < gm.length; i++) if (gm[i] && m[i]) { gm[i] = 0; k++; }
      if (k) ops.push({ op: 'group.save', name: g, set: sc.s.toSet(gm) } as OpBody);
    }
    return ops;
  }

  private checkpoint() {
    const heads = [...this.scans.values()].filter(sc => sc.s.head);
    if (!heads.length) { this.say('nothing to checkpoint yet'); return; }
    const name = prompt('Checkpoint name (for every edited scan)', new Date().toISOString().slice(0, 16).replace('T', ' '));
    if (!name) return;
    this.run('checkpoint', async () => { for (const sc of heads) { await sc.s.api.ref(sc.s.scan, name.trim(), sc.s.head); await sc.s.reload(); } });
  }

  /** Undo the newest action: every scan whose HEAD op has the newest time (and message) steps back. Returns what it undid. */
  private undo(): Promise<Action | null> { return undoNewest([...this.scans.values()].map(sc => sc.s)); }

  private redo(act: Action) { return redoAction(id => this.scans.get(id)?.s, act); }

  // ── previews of a move: a "ghost" copy of some points, moved by the gizmo ──
  /** Show points (packed) as a ghost, tinted with a hue id; the gizmo then moves it (moveGhost). */
  private ghost(xyz: Float32Array, nrm: Float32Array, rgb: Uint8Array, hueId: number) {
    const n = xyz.length / 3, { idx, rec } = asCloud(xyz, nrm, rgb);
    const meta = { ...this.v.clouds[0].meta, id: GHOST, name: 'preview', color: '#ffffff' } as CloudMeta;
    this.v.setEditCloud(meta, idx, [rec], identity(), new Uint16Array(n), new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0]));
    this.v.setEditState(GHOST, new Uint32Array(n).fill(32 | ((hueId & 255) << 8)));
  }
  private moveGhost(W: Rigid) {
    const e = this.v.edits.get(GHOST);
    if (e) { e.T = W; e.M = mat4(W); this.v.dirty = true; }
  }
  private clearGhost() { if (this.v.edits.has(GHOST)) this.v.clearEditCloud(GHOST); }

  /** Per object name, where its publish move puts it in the world: T · D · T⁻¹ (the scans agree; the first one with it). Unmoved ones left out. */
  private pubMoves(): Map<string, Rigid> {
    const out = new Map<string, Rigid>();
    for (const sc of this.scans.values()) {
      const s = this.placed(sc);
      for (const o of s.state.objects.values()) if (!out.has(o.name) && !isIdentity(o.D, 1e-9)) out.set(o.name, compose(s.T, compose(o.D, inverse(s.T))));
    }
    return out;
  }

  /** The object a move is under way for: its points hide (a voxel model or a ghost moves in their place). */
  private movingObj: string | null = null;

  /** Redo the hide masks and per-point words of these scans only (not a whole refresh). */
  private rewords(scs: Iterable<Scan>) {
    for (const sc of scs) { sc.hide = null; this.v.setEditState(sc.s.scan, sc.s.words(sc.mask, sc.cand, this.hiddenOf(sc), this.clsIx(sc))); }
  }

  /**
   * Move a movable object (by name, in every scan) with the gizmo. Its points hide while it moves, and in their
   * place, where it will be published (each scan's T · D · T⁻¹): an instance drawn as voxels moves its voxel
   * model, an instance of a type with a master moves the master (placed on it), anything else a ghost of its own
   * points. Apply commits per scan D' = T⁻¹ · W · T · D, so the move is the same in the world whatever each scan's alignment.
   */
  private moveObject(name: string) {
    this.gizmo?.cancel();
    const has = [...this.scans.values()].filter(sc => idsNamed(sc.s.state.objects, name).length > 0);
    if (!has.length) { this.say(`no points of "${name}"`); return; }
    const Pd = this.pubMoves().get(name) ?? identity(), x = this.voxed.get(name), e = x && this.v.edits.get(VOX + name);
    let c: V3, change: (W: Rigid) => void, end: () => void;
    if (x && e) {
      // its voxel model (already drawn in its place): moved as it is
      const base = e.T, m = ovl.typeVoxels(has.map(sc => sc.s)).get(x.type)?.vox;
      c = apply(base, m ? centroid(decodeVoxels(m).xyz) : [0, 0, 0]);
      const put = (T: Rigid) => { e.T = T; e.M = mat4(T); this.v.dirty = true; };
      change = W => put(compose(W, base));
      end = () => put(base);
    } else {
      const info = this.objectInfo(), I = info.get(name), type = I?.of;
      let P: Float32Array<ArrayBuffer>, N: Float32Array<ArrayBuffer>, rgb: Uint8Array, hue = idsNamed(has[0].s.state.objects, name)[0];
      if (type && ovl.isFitted(info, name) && this.hasMaster(type)) {
        // the type's master, from the overlay onto this instance (F⁻¹), then as published
        const mo = this.masterOf(type), M = compose(Pd, inverse(I!.F ?? identity()));
        P = transformPoints(M, mo.xyz); N = transformPoints(M, mo.nrm, undefined, false); rgb = new Uint8Array(P.length).fill(200);
      } else {
        // its own points (deleted ones aside), as published
        let n = 0;
        const per = has.map(sc => {
          const s = this.placed(sc), st = s.state, ids = new Set(idsNamed(st.objects, name)), v: number[] = [];
          for (let i = 0; i < s.nv; i++) { const q = s.pidOfV[i]; if (ids.has(st.obj[q]) && !(st.flags[q] & DELETED)) v.push(i); }
          n += v.length;
          return { s, v };
        });
        P = new Float32Array(3 * n); N = new Float32Array(3 * n); rgb = new Uint8Array(3 * n);
        let o = 0;
        for (const { s, v } of per) for (const i of v) {
          P.set(s.P.subarray(3 * i, 3 * i + 3), o); N.set(s.N.subarray(3 * i, 3 * i + 3), o); rgb.set(s.rgb.subarray(3 * i, 3 * i + 3), o); o += 3;
        }
        transformPoints(Pd, P, P); transformPoints(Pd, N, N, false);
      }
      if (!P.length) { this.say(`no points of "${name}"`); return; }
      if (!hue) hue = 1;
      this.ghost(P, N, rgb, hue);
      c = centroid(P);
      change = W => this.moveGhost(W);
      end = () => this.clearGhost();
    }
    this.movingObj = name;
    this.rewords(has);
    const done = () => { this.gizmo = null; this.movingObj = null; end(); this.rewords(has); };
    this.gizmo = new Gizmo(this.v, c, `Move "${name}" (as it will be published)`, {
      change,
      apply: (W, made) => {
        done();
        if (isIdentity(W, 1e-9)) return;
        this.act(`move "${name}" (moved ${made.shift.map(x => (x * 100).toFixed(1)).join(' ')} cm, turned ${made.yawDeg.toFixed(2)}°)`, sc => {
          const id = idsNamed(sc.s.state.objects, name)[0];
          if (!id) return null;
          const T = sc.s.T;
          return { op: 'object.transform', obj: id, D: compose(inverse(T), compose(W, compose(T, sc.s.state.objects.get(id)!.D))) } as OpBody;
        }, { also: sc => idsNamed(sc.s.state.objects, name).length > 0, keepSel: true, keepDeleted: true });
      },
      cancel: done,
    });
  }

  /** Commit a per-scan op for every scan that has a thing of this name. */
  private byName(label: string, has: (sc: Scan) => boolean, make: (sc: Scan) => OpBody | OpBody[] | null, stamp?: { t: string; msg: string }) {
    this.act(label, sc => (has(sc) ? make(sc) : null), { also: has, keepSel: true, stamp });
  }

  private objectNames() { return [...new Set([...this.scans.values()].flatMap(sc => [...sc.s.state.objects.values()].map(o => o.name)))]; }
  private partNames() { return [...new Set([...this.scans.values()].flatMap(sc => [...sc.s.state.parts.values()].map(p => p.name)))]; }

  // ── panel ─────────────────────────────────────────────────────────────
  /** Inputs that depend on the loaded scans but not on their edit state. */
  private fillStatic() {
    const vs = this.$('evol') as HTMLSelectElement, keep = vs.value;
    vs.textContent = '';
    vs.append(el('option', { value: '*' }, 'all enabled boxes'));
    for (const v of this.v.s.volumes) vs.append(el('option', { value: v.id }, `${v.kind} · ${v.name || v.id}`));
    if ([...vs.options].some(o => o.value === keep)) vs.value = keep;
    let lo = Infinity, hi = 0, nf = 0;
    for (const sc of this.scans.values()) {
      if (!sc.s.frame) continue;
      const h = sel.frameHistogram(sc.s.frame);
      if (h.length) { lo = Math.min(lo, h[0].frame); hi = Math.max(hi, h[h.length - 1].frame); nf++; }
    }
    if (nf && !this.inp('efa').value) { this.inp('efa').value = String(lo); this.inp('efb').value = String(hi); }
    this.$('efhist').textContent = nf ? `frames ${lo}–${hi} in ${nf} scan${nf > 1 ? 's' : ''} (each scan numbers its own; ∩ with Points → scan to pick one)` : 'no frame ids';
  }

  /** The Display tab's show toggles as their own tabs' are (code ticks those without a change event). */
  private syncDisplay() {
    for (const [d, e] of DISPLAY_MIRRORS) this.inp(d).checked = this.inp(e).checked;
    this.inp('dpath').checked = this.showPath;
    this.$('dpathl').hidden = ![...this.scans.values()].some(sc => sc.s.links?.links.length);
  }

  /** Redraw everything from the scans' state. */
  refresh() {
    if (!this.on) return;
    this.syncDisplay();
    let nv = 0, del = 0, n = 0, stale = 0, inObj = 0;
    const clsN = new Map<string, number>();
    const objN = new Map<string, number>(), partN = new Map<string, number>(), groupN = new Map<string, number>(), memN = new Map<string, number>();
    const fr = [Infinity, 0];
    let nf = 0;   // selected frames, over the scans (each numbers its own)
    this.findVoxed();   // before hiddenOf: the instances it replaces hide
    for (const [id, sc] of this.scans) {
      sc.hide = null;      // layers or state may have changed
      const s = sc.s, st = s.state;
      if (sc.tv !== s.treeVersion) { sc.tv = s.treeVersion; this.v.setEditFrames(id, s.frameMats); sc.grid = null; }
      sc.camFrames = this.inp('eshowcams').checked && !this.off(sc) ? this.selectedFrames(sc) : undefined;
      this.drawPath(sc);
      this.v.setEditState(id, s.words(sc.mask, sc.cand, this.hiddenOf(sc), this.clsIx(sc)));
      nv += s.nv;
      const cname: string[] = [...CLS];
      for (const [k, c] of st.classes) cname[k] = c.name;
      if (s.graph.stale) stale++;
      const frs = new Set<number>();
      let lastF = 0;   // a frame's points mostly run together: skip the set for repeats
      for (let v = 0; v < s.nv; v++) {
        const p = s.pidOfV[v], f = st.flags[p];
        if (sc.mask?.[v]) {
          n++;
          if (s.frame?.[v]) { if (s.frame[v] !== lastF) frs.add(lastF = s.frame[v]); fr[0] = Math.min(fr[0], s.frame[v]); fr[1] = Math.max(fr[1], s.frame[v]); }
        }
        if (f & DELETED) { del++; continue; }
        if (st.obj[p]) { const nm = st.objects.get(st.obj[p])!.name; objN.set(nm, (objN.get(nm) ?? 0) + 1); inObj++; }
        else {
          const nm = cname[f >> 1] ?? '?';
          clsN.set(nm, (clsN.get(nm) ?? 0) + 1);
          if (st.part[p]) { const nm = st.parts.get(st.part[p])!.name; partN.set(nm, (partN.get(nm) ?? 0) + 1); }
        }
      }
      nf += frs.size;
      for (const [k, g] of st.groups) groupN.set(k, (groupN.get(k) ?? 0) + g.n);
      for (const [k, g] of this.memOf(id)) memN.set(k, (memN.get(k) ?? 0) + memCount(g));
    }
    const loading = this.loading ? `\nloading ${this.v.clouds.find(c => c.meta.id === this.loading)?.meta.name}…` : '';
    if (!this.busy && !this.sayWarn) this.say(`${this.scans.size} scan${this.scans.size === 1 ? '' : 's'}, ${nv.toLocaleString()} points: ${del.toLocaleString()} deleted` +
      [...clsN].filter(([k]) => k !== 'none').map(([k, c]) => `, ${c.toLocaleString()} ${k}`).join('') +
      (stale ? `\n${stale} scan(s) read-only: their source ply changed since their edits` : '') + loading);
    this.sayWarn = false;
    const off = [...this.scans.keys()].filter(id => this.v.edits.get(id)?.visible === false).length;
    this.$('ename').textContent = this.scans.size ? `Editing ${this.scans.size} scan${this.scans.size === 1 ? '' : 's'}${off ? ` (${off} hidden)` : ''}` : (this.loading ? 'Loading…' : 'Tick scans (under Classes) to edit');
    this.$('ecount').textContent = n ? `${n.toLocaleString()} selected in ${[...this.scans.values()].filter(sc => sc.mask && sel.count(sc.mask)).length} scan(s)` +
      (fr[1] ? ` · Frames ${nf.toLocaleString()} (${fr[0]}–${fr[1]})` : '') : 'nothing selected';

    // sources + object picker
    const src = this.$('esrc') as HTMLSelectElement, keep = src.value;
    src.textContent = '';
    const opt = (v: string, t: string) => src.append(el('option', { value: v }, t));
    opt('all', 'all'); opt('floor', 'floor'); opt('solid', 'solid'); opt('movable', 'movable');
    const users = this.userClasses();
    for (const nm of users.keys()) opt(`class:${nm}`, `class: ${nm}`);
    opt('unclassified', 'unclassified');
    for (const c of this.v.clouds) if (this.scans.has(c.meta.id)) opt(`scan:${c.meta.id}`, `scan: ${c.meta.name}`);
    opt('objects', 'in any object');
    for (const t of this.typeNames()) opt(`type:${t}`, `instances of: ${t}`);
    for (const nm of objN.keys()) opt(`object:${nm}`, `object: ${nm}`);
    opt('parts', 'in any split part');
    for (const nm of partN.keys()) opt(`part:${nm}`, `part: ${nm}`);
    for (const k of groupN.keys()) opt(`group:${k}`, `group: ${k}`);
    for (const k of [...memN.keys()].sort()) opt(`group:${k}`, `memory group: ${k}`);
    for (const k of this.dataNames()) opt(`data:${k}`, `data group: ${k}`);
    opt('deleted', 'deleted');
    if ([...src.options].some(o => o.value === keep)) src.value = keep;
    const os = this.$('eobjsel') as HTMLSelectElement, okeep = os.value;
    os.textContent = '';
    for (const nm of this.objectNames()) os.append(el('option', { value: nm }, nm));
    if ([...os.options].some(o => o.value === okeep)) os.value = okeep;

    this.classList(clsN, users);
    const ix = this.classIndex();
    this.v.setClassColors(this.classColors(ix, users));
    this.counts = { objN, groupN, memN, partN, del };
    this.refreshLists();
    this.drawShapes();
    this.shapeList();
    this.drawPoints();
    this.drawBoxes();
    this.drawVoxels();
    this.pointList();
    this.history();
    this.drawView();
    this.shading();
  }

  /** The point counts the last refresh took, for redrawing the lists alone. */
  private counts: { objN: Map<string, number>; groupN: Map<string, number>; memN: Map<string, number>; partN: Map<string, number>; del: number } | null = null;

  /** Redraw the object, group and layer lists from the last refresh's counts (a fold opened or closed: nothing else changed). */
  private refreshLists() {
    const c = this.counts;
    if (!c) { this.refresh(); return; }
    this.objectList(c.objN);
    this.groupList(c.groupN, c.memN);
    this.dataList();
    this.layers(c.partN, c.del);
  }

  /** A row of the Layers list (or the Classes window): its eye shows / hides it, then swatch, name, point count and buttons. */
  private layerRow(key: string, color: string, name: string, count: number, buttons: LayerButton[] = [], depth = 0, twisty?: { open: boolean; toggle: () => void }, icon?: [string, string]) {
    const off = this.hidden.has(key), r = el('div', { class: 'layer' + (off ? ' off' : '') });
    if (depth) r.style.paddingLeft = `${depth * 12}px`;
    const eye = el('button', { class: 'eye', title: off ? 'show' : 'hide' }, off ? '◌' : '●');
    eye.style.color = color;
    eye.onclick = () => this.toggleLayer(key);
    const sw = el('span', { class: 'sw' });
    sw.style.background = color;
    const b = el('span', { class: 'row' });
    for (const [label, tip, f, on] of buttons) { const x = el('button', { class: 'small', title: tip }, label); x.onclick = f; if (on) x.classList.add('on'); b.append(x); }
    const nm = el('span', { class: 'name', title: name }, twisty ? (twisty.open ? '▾ ' : '▸ ') : '');
    if (icon) nm.append(el('span', { class: 'ico', title: icon[1] }, icon[0]));
    nm.append(name);
    if (twisty) { nm.style.cursor = 'pointer'; nm.onclick = twisty.toggle; }
    r.append(eye, sw, nm, el('span', { class: 'n' }, count.toLocaleString()), b);
    return r;
  }

  /** A button's action: select the points of a source (see applySourceKind), combined with the selection as mode says. */
  private pickSrc = (k: string, mode: sel.Combine = 'replace') => () => { this.applySourceKind(k, mode); };

  /** The Classes window (top right): a row per class, its + classifying the selection as it. */
  private classList(clsN: Map<string, number>, users: Map<string, string>) {
    const box = this.$('eclasses'), pickSrc = this.pickSrc;
    box.textContent = '';
    const add = (name: string): LayerButton => name === 'unclassified'
      ? ['+', 'clear the class of the selection', () => this.classify('none')]
      : ['+', `classify the selection as ${name}${name === 'movable' ? ' (things that can be moved: chairs, tables…)' : ''}`, () => this.classify(name)];
    // its name does what its + does
    const row = (key: string, color: string, name: string, count: number, buttons: LayerButton[]) => {
      const r = this.layerRow(key, color, name, count, buttons), nm = r.querySelector<HTMLElement>('.name')!, [, tip, go] = add(name);
      nm.title = tip; nm.style.cursor = 'pointer'; nm.onclick = go;
      box.append(r);
    };
    // S: show only this class; again: show every class again
    const solo = (name: string): LayerButton => {
      const on = this.soloClass === name;
      return ['S', on ? 'show every class again' : `show only the ${name} points`, () => { this.soloClass = on ? null : name; this.refresh(); }, on];
    };
    const recolour = (name: string, color: string, make: (sc: Scan, c: string) => OpBody | null, has: (sc: Scan) => boolean = () => true): LayerButton => ['Col', 'change its colour (in every scan)', () => {
      const c = prompt(`Colour for "${name}" (#rrggbb)`, color);
      if (!c || c.toLowerCase() === color) return;
      if (!/^#[0-9a-fA-F]{6}$/.test(c)) { this.say('want a colour like #44aa88'); return; }
      this.byName(`recolour class "${name}"`, has, sc => make(sc, c.toLowerCase()));
    }];
    const builtin = this.builtinColors();
    for (const [k, name, def] of CLS_LAYER) {
      const id = +k.slice(4), color = builtin[id];
      row(k, color, name, clsN.get(CLS[id]) ?? 0, [add(name), solo(name), ['Sel', `select the ${name} points`, pickSrc(name)],
        // scans already that colour are left alone
        recolour(name, color, (sc, c) => ((sc.s.state.clsColors.get(id) ?? def) === c ? null : ({ op: 'class.color', cls: id, color: c }) as OpBody))]);
    }
    for (const [name, color] of users) {
      const has = (sc: Scan) => [...sc.s.state.classes.values()].some(c => c.name === name);
      const idIn = (sc: Scan) => [...sc.s.state.classes].find(([, c]) => c.name === name)![0];
      row(`clsn:${name}`, color, name, clsN.get(name) ?? 0, [
        add(name),
        solo(name),
        ['Sel', `select the ${name} points`, pickSrc(`class:${name}`)],
        recolour(name, color, (sc, c) => ({ op: 'class.meta', cls: idIn(sc), name, color: c }) as OpBody, has),
        ['Ren', 'rename (in every scan)', () => {
          const nm = prompt('New name', name);
          if (!nm || nm === name) return;
          if (!isClassName(nm) || users.has(nm)) { this.say('bad or taken class name'); return; }
          if (this.soloClass === name) this.soloClass = nm;
          this.byName(`rename class "${name}" to "${nm}"`, has, sc => ({ op: 'class.meta', cls: idIn(sc), name: nm, color }) as OpBody);
        }],
        ['×', 'delete the class (its points become unclassified)', () => {
          if (!confirm(`Delete class "${name}"? Its points become unclassified.`)) return;
          if (this.soloClass === name) this.soloClass = null;
          this.byName(`delete class "${name}"`, has, sc => ({ op: 'class.delete', cls: idIn(sc) }) as OpBody);
        }],
      ]);
    }
  }

  /** Types opened in the Classes window's objects (their instances listed); closed by default. */
  private openTypes = new Set<string>();

  /** Types whose instances are drawn as their own points, not the type's voxel model (unticked voxels on its row); per scene, in this browser. */
  private rawSet: Set<string> | null = null;
  private get rawTypes() {
    if (!this.rawSet) {
      this.rawSet = new Set();
      try { const a = JSON.parse(localStorage.getItem(this.rawKey()) || '[]'); if (Array.isArray(a)) for (const x of a) if (typeof x === 'string') this.rawSet.add(x); } catch { /* storage blocked */ }
    }
    return this.rawSet;
  }
  private rawKey() { return `cv.rawTypes:${this.scene ?? ''}`; }
  private setRaw(t: string, raw: boolean) {
    if (raw) this.rawTypes.add(t); else this.rawTypes.delete(t);
    try { localStorage.setItem(this.rawKey(), JSON.stringify([...this.rawTypes])); } catch { /* storage blocked */ }
    this.refresh();
  }

  /** The Objects window: a row per type (its instances listed only when opened), then each object of no type. */
  private objectList(objN: Map<string, number>) {
    const box = this.$('eobjects');
    box.textContent = '';
    if (!objN.size) { box.append(el('div', { class: 'help' }, 'No objects yet: select points and make one (New object in Tools).')); return; }
    const row = (key: string, color: string, name: string, count: number, buttons: LayerButton[] = [], depth = 0, twisty?: { open: boolean; toggle: () => void }) =>
      box.append(this.layerRow(key, color, name, count, buttons, depth, twisty));
    const scansWith = (f: (sc: Scan) => boolean) => [...this.scans.values()].filter(f).length;
    const pickSrc = this.pickSrc;
    const objRow = (name: string, count: number, depth: number) => {
      const has = (sc: Scan) => idsNamed(sc.s.state.objects, name).length > 0, k = scansWith(has);
      const first = [...this.scans.values()].map(sc => [sc, idsNamed(sc.s.state.objects, name)[0]] as const).find(([, id]) => id)!;
      const D = first[0].s.state.objects.get(first[1])!.D;
      row(`obj:${name}`, hueCss(first[1]), `${name}${k > 1 ? ` · ${k} scans` : ''}${isIdentity(D) ? '' : ` (moved, ${yawDeg(D).toFixed(0)}°)`}`, count, [
        ['Sel', 'select its points', pickSrc(`object:${name}`)],
        ['Move', 'move / turn it with the gizmo (shown as a ghost; applies when published)', () => this.moveObject(name)],
        ['Of', 'make it an instance of a type (pew, chair…), or of none', () => this.setType(name)],
        ['Ren', 'rename (in every scan)', () => {
          const nm = prompt('Name', name);
          if (nm && nm !== name) this.byName(`rename "${name}" to "${nm}"`, has, sc => ({ op: 'object.meta', obj: idsNamed(sc.s.state.objects, name)[0], name: nm.slice(0, 80) }) as OpBody);
        }],
        ['×', 'release all its points (no longer an object)', () => this.byName(`release "${name}"`, has, sc => {
          const ids = new Set(idsNamed(sc.s.state.objects, name));
          return { op: 'object.release', set: sc.s.toSet(sc.s.where((st, p) => ids.has(st.obj[p]))) } as OpBody;
        })],
      ], depth);
    };
    // instances under their type, then objects with no type
    const info = this.objectInfo(), byType = new Map<string, string[]>(), models = ovl.typeVoxels([...this.scans.values()].map(sc => sc.s));
    for (const name of objN.keys()) { const t = info.get(name)?.of; if (t) (byType.get(t) ?? byType.set(t, []).get(t)!).push(name); }
    for (const [t, names] of [...byType].sort((a, b) => natural(a[0], b[0]))) {
      const total = names.reduce((a, n) => a + objN.get(n)!, 0), viewing = this.viewing?.type === t, open = this.openTypes.has(t);
      row(`type:${t}`, '#dddddd', `${t} · ${names.length} instance${names.length === 1 ? '' : 's'}${viewing ? ' (viewing)' : ''}`, total, [
        ['View', 'overlay all its instances, fitted onto the first', () => (viewing ? this.closeView() : this.openView(t))],
        ['Edit', 'the object editor on it (also in the top bar): its instances only, with part labels and operations on many instances at once', () => this.openObjects(t)],
        ['Sel', 'select every instance', pickSrc(`type:${t}`)],
      ], 0, { open, toggle: () => { if (open) this.openTypes.delete(t); else this.openTypes.add(t); this.refreshLists(); } });
      if (models.has(t)) {
        const all = this.inp('evoxshow').checked, cb = el('input', { type: 'checkbox' }) as HTMLInputElement;
        cb.checked = !this.rawTypes.has(t); cb.disabled = !all;
        cb.onchange = () => this.setRaw(t, !cb.checked);
        const lb = el('label', { class: 'vox', title: all ? 'draw its instances as the type\'s voxel model (unticked: their own points)' : 'voxels are off for every type (the voxels tick in the editor)' });
        lb.append(cb, 'vox');
        box.lastElementChild!.querySelector('.row')!.prepend(lb);   // before its buttons
      }
      if (open) for (const n of names.sort(natural)) objRow(n, objN.get(n)!, 1);
    }
    for (const [name, count] of objN) if (!info.get(name)?.of) objRow(name, count, 0);
  }

  /**
   * The Groups window: the saved groups as a tree by name across scans, then the
   * memory groups (memgroups.ts). Each row's icon says which: ◆ saved in the scan, ◇ only in this page.
   */
  private groupList(groupN: Map<string, number>, memN: Map<string, number>) {
    const box = this.$('egroups');
    box.textContent = '';
    const head = (t: string) => box.append(el('div', { class: 'layer hd' }, t));
    const scansWith = (f: (sc: Scan) => boolean) => [...this.scans.values()].filter(f).length;
    const pickSrc = this.pickSrc;
    if (groupN.size) {
      head('Groups');
      const t = el('button', { class: 'small', title: 'one group per point: nest groups that hold others, and keep each point only in its most specific group (what each group holds stays the same); one Undo' }, 'Tidy');
      t.onclick = () => this.tidyAllGroups();
      box.lastElementChild!.append(' ', t);
    }
    // a tree by name across scans: a group's parent is the one the first scan that has it gives
    const gparent = new Map<string, string | null>();
    for (const sc of this.scans.values()) for (const k of sc.s.state.groups.keys()) if (!gparent.has(k)) gparent.set(k, sc.s.state.groupParent.get(k) ?? null);
    const kids = (p: string | null) => [...groupN.keys()].filter(k => (gparent.get(k) ?? null) === p || (p === null && !groupN.has(gparent.get(k) ?? ''))).filter(k => k !== p).sort();
    // shown false: inside a collapsed group, so only marked seen (else the cycle catch-all below draws it at top level)
    const groupRow = (name: string, depth: number, seen: Set<string>, shown = true) => {
      if (seen.has(name)) return;
      seen.add(name);
      const sub = kids(name).filter(k => gparent.get(k) === name), open = !this.closedGroups.has(name);
      if (!shown) { for (const k of sub) groupRow(k, depth + 1, seen, false); return; }
      const has = (sc: Scan) => sc.s.state.groups.has(name);
      const solo = this.soloGroup === name;
      box.append(this.layerRow(`group:${name}`, '#8ab4f8', `${name}${scansWith(has) > 1 ? ` · ${scansWith(has)} scans` : ''}`, groupN.get(name) ?? 0, [
        // S: show only this group (and the groups inside it); again: show everything again
        ['S', solo ? 'show all the points again' : 'show only this group (and the groups inside it)', () => {
          this.soloGroup = solo ? null : name;
          if (!solo) this.hidden.delete(`group:${name}`);
          this.refresh();
        }, solo],
        ['Sel', 'select the group (and the groups inside it)', pickSrc(`group:${name}`)],
        ['+', 'move the selected points into this group (out of any other group they are in)', () => this.addToGroup(name)],
        ['−', 'take the selected points out of the group', () => this.removeFromGroup(name)],
        ['New', 'new group inside this one, from the selected points', () => this.newSubGroup(name)],
        ['↑', 'put this group inside another (empty: top level)', () => this.moveGroup(name)],
        ['×', 'delete the group (its points stay; groups inside it move up)', () => this.byName(`delete group "${name}"`, has, () => ({ op: 'group.delete', name }) as OpBody)],
      ], depth, sub.length ? { open, toggle: () => { if (open) this.closedGroups.add(name); else this.closedGroups.delete(name); this.refreshLists(); } } : undefined, SAVED_ICON));
      for (const k of sub) groupRow(k, depth + 1, seen, open);
    };
    const seen = new Set<string>();
    for (const k of kids(null)) groupRow(k, 0, seen);
    for (const k of groupN.keys()) groupRow(k, 0, seen);   // anything a cycle across scans left out
    if (memN.size) head('Memory groups (not saved)');
    for (const name of [...memN.keys()].sort()) {
      const has = (sc: Scan) => this.memOf(sc.s.scan).has(name), solo = this.soloGroup === name;
      const r = this.layerRow(`group:${name}`, '#f5b971', `${name}${scansWith(has) > 1 ? ` · ${scansWith(has)} scans` : ''}`, memN.get(name) ?? 0, [
        ['S', solo ? 'show all the points again' : 'show only this group', () => {
          this.soloGroup = solo ? null : name;
          if (!solo) this.hidden.delete(`group:${name}`);
          this.refresh();
        }, solo],
        ['Sel', 'select the group', pickSrc(`group:${name}`)],
        ['+', 'move the selected points into this group (out of any other memory group)', () => this.editMemGroup('add', name)],
        ['−', 'take the selected points out of the group', () => this.editMemGroup('remove', name)],
        ['Save', 'write it to the scan as a saved group (it leaves memory); one Undo', () => this.saveMemGroup(name)],
        ['×', 'forget the group (its points stay)', () => this.dropMemGroup(name)],
      ], 0, undefined, MEM_ICON);
      r.classList.add('mem');
      box.append(r);
    }
    if (!box.childElementCount) box.append(el('div', { class: 'help' }, 'No groups yet: Save group (in Tools) keeps the selection by name.'));
  }

  /** The Data window: the data groups (a number per point, memory only), each shown in the Data group shading or selected. */
  private dataList() {
    const box = this.$('edata');
    box.textContent = '';
    const scansWith = (f: (sc: Scan) => boolean) => [...this.scans.values()].filter(f).length;
    const pickSrc = this.pickSrc;
    const dn = this.dataNames();
    for (const name of dn) {
      const vs = [...this.scans.values()].map(sc => this.dataOf(sc.s.scan).get(name)?.v ?? null), rg = dataRange(vs);
      const has = (sc: Scan) => this.dataOf(sc.s.scan).has(name), d = [...this.scans.values()].map(sc => this.dataOf(sc.s.scan).get(name)).find(Boolean)!;
      const shown = this.v.s.mode === 11 && this.dataShow.name === name;
      const what = `${name}${scansWith(has) > 1 ? ` · ${scansWith(has)} scans` : ''} · ${rg ? `${fmtVal(rg.min)} to ${fmtVal(rg.max)}` : 'empty'}, else ${fmtVal(d.dflt)}`;
      const r = this.layerRow(`data:${name}`, '#fabb26', what, vs.reduce((a, v) => a + dataCount(v), 0), [
        ['Show', shown ? 'shown in the Data group shading' : 'show its values (Data group shading)', () => { this.dataShow.name = name; this.setShade(11); }, shown],
        ['Sel', 'select its points', pickSrc(`data:${name}`)],
        ['×', 'forget the data group (its points stay)', () => this.dropDataGroup(name)],
      ], 0, undefined, DATA_ICON);
      r.classList.add('mem');
      box.append(r);
    }
    if (!dn.length) box.append(el('div', { class: 'help' }, 'No data groups yet: Data group and Data math macro steps make them, or the line above (kept in this page only).'));
  }

  /** Switch the shading, as its menu does (main.ts takes it from there). */
  private setShade(mode: number) {
    const s = this.$('shade') as HTMLSelectElement;
    s.value = String(mode);
    s.dispatchEvent(new Event('change'));
  }

  /** Forget a data group in every scan (one undo step, with the selection). */
  private dropDataGroup(name: string) {
    this.step({ k: 'sel', snap: this.snapSel() });
    this.future = [];
    for (const [id, g] of this.data) if (g.has(name)) { const o = new Map(g); o.delete(name); this.data.set(id, o); }
    this.hidden.delete(`data:${name}`);
    this.refresh();
  }

  /** The quick evals run (newest last), and which one Up / Down is on (-1: none yet). */
  private dataEvals: string[] = [];
  private dataEvalAt = -1;

  /** The Data view's quick eval: `c = a * b` makes (or replaces) data group c point by point (dataexpr.ts); one undo step. */
  private dataEval(src: string) {
    if (!src.trim()) return;
    if (this.busy) { this.say('wait for the run to finish', true); return; }
    const q = parseDataExpr(src);
    if (typeof q === 'string') { this.say(q, true); return; }
    const scans = [...this.scans.values()], sets = dataSets(scans.map(sc => this.dataOf(sc.s.scan)));
    // the measures it reads (names no data group has): the point's position, or a data step's source
    const dens: MacroStep[] = q.uses.includes('density5') ? [{ t: 'dataNew', p: { src: 'density', dr: '0.05' }, mode: 'replace' }] : [];   // the points density is measured over
    const mem: Mem = {}, measures = measuresFor(scans.map(sc => this.cloudOf(sc)), this.ruleCtx(dens, scans), q, sets, mem);
    if (typeof measures === 'string') { this.say(measures, true); return; }
    if (mem.dens) this.densCache = this.editDens(mem.dens, scans);   // measured once: the next one (or a Density rule) takes it
    const d = evalDataExpr(q, sets, scans.length, scans.map(sc => this.held(sc)), measures);
    if (typeof d === 'string') { this.say(d, true); return; }
    this.step({ k: 'sel', snap: this.snapSel() });
    this.future = [];
    scans.forEach((sc, k) => { const g = dataLanded(this.dataOf(sc.s.scan), { [q.name]: d }, k); if (g) this.data.set(sc.s.scan, g); });
    this.dataShow.name = q.name;
    const h = this.dataEvals, t = src.trim();
    if (h[h.length - 1] !== t) h.push(t);
    this.dataEvalAt = -1;
    this.inp('dataeval').select();
    const rg = dataRange(d.v), n = d.v.reduce((a, v) => a + dataCount(v), 0);
    this.refresh();
    this.say(`data group "${q.name}": ${n.toLocaleString()} points${rg ? `, ${fmtVal(rg.min)} to ${fmtVal(rg.max)}` : ''}`);   // after refresh's scan count
  }

  /** What the shown data group is per scan, as uploaded (to send only what changed). */
  private dataUp = new Map<string, { v: Float32Array | null; c: unknown }>();
  /** The Data group shading's group ('': none yet; the first one a step makes is shown). */
  private dataShow = { name: '' };
  /** the group the View's Min/Max sliders are for (they go back to its ends when another is shown) */
  private dataCutName = '';

  /**
   * Data group shading (mode 11): the group picked (its values per scan, those not in it dark grey), the ramp from
   * From to To (auto: the least to the most of its values, or its 2nd to 98th percentile). The picker lists the data groups there are.
   */
  private dataShown() {
    const names = this.dataNames(), sl = this.$('dataname') as HTMLSelectElement;
    if (!names.includes(this.dataShow.name)) this.dataShow.name = names[0] ?? '';
    const name = this.dataShow.name;
    if (sl.options.length !== names.length || [...sl.options].some((o, i) => o.value !== names[i])) {
      sl.textContent = '';
      for (const n of names) sl.append(el('option', { value: n }, n));
    }
    sl.value = name;
    const box = this.$('datacut'), clo = this.inp('datacutlo'), chi = this.inp('datacuthi');
    if (this.dataCutName !== name) { this.dataCutName = name; clo.value = '0'; chi.value = '1000'; }   // another group: all shown again
    box.hidden = this.v.s.mode !== 11 || !name;
    if (box.hidden && this.v.valCut) { this.v.valCut = null; this.v.dirty = true; }
    if (this.v.s.mode !== 11) return;
    const scans = [...this.scans.values()], vs = scans.map(sc => this.dataOf(sc.s.scan).get(name)?.v ?? null);
    scans.forEach((sc, k) => {   // again when the group changed, or the scan's cloud was built again
      const id = sc.s.scan, c = this.v.edits.get(id)?.chunks[0]?.edit, up = this.dataUp.get(id);
      if (up && up.v === vs[k] && up.c === c) return;
      this.v.setEditValues(id, vs[k]);
      this.dataUp.set(id, { v: vs[k], c });
    });
    const rg = dataRange(vs), lo = this.inp('datalo'), hi = this.inp('datahi'), auto = (this.$('dataauto') as HTMLSelectElement).value;
    if (auto && rg) {   // least to most, or clipped to the percentiles (when they differ); all one value: it to one more
      const [a, b] = auto === 'clip' && rg.lo < rg.hi ? [rg.lo, rg.hi] : [rg.min, rg.max > rg.min ? rg.max : rg.min + 1];
      lo.value = fmtVal(a); hi.value = fmtVal(b);
    }
    const a = Number.isFinite(+lo.value) ? +lo.value : 0, b = +hi.value;
    this.v.valRange = [a, Number.isFinite(b) && b !== a ? b : a + 1];
    const p0 = +clo.value, p1 = +chi.value, at = (q: number) => rg ? rg.min + q / 1000 * (rg.max - rg.min) : 0;
    this.v.valCut = rg && (p0 > 0 || p1 < 1000) ? [at(p0), at(p1)] : null;
    this.$('datacutlov').textContent = rg ? fmtVal(at(p0)) : ''; this.$('datacuthiv').textContent = rg ? fmtVal(at(p1)) : '';
    this.v.dirty = true;
    const d = scans.map(sc => this.dataOf(sc.s.scan).get(name)).find(Boolean);
    this.$('datalegend').textContent = !name ? 'no data groups yet: a Data group macro step makes one'
      : `${vs.reduce((n, v) => n + dataCount(v), 0).toLocaleString()} points${rg ? `, ${fmtVal(rg.min)} to ${fmtVal(rg.max)}` : ''}; blue at From, red at To; dark grey: not in it (reads as ${fmtVal(d?.dflt ?? 0)})`;
  }

  /** The Layers list: show/hide and act on the deleted points and parts (by name, across scans); classes, objects and groups are in the Classes window. */
  private layers(partN: Map<string, number>, del: number) {
    const box = this.$('elayers');
    box.textContent = '';
    const head = (t: string) => box.append(el('div', { class: 'layer hd' }, t));
    const row = (key: string, color: string, name: string, count: number, buttons: LayerButton[] = []) => box.append(this.layerRow(key, color, name, count, buttons));
    const scansWith = (f: (sc: Scan) => boolean) => [...this.scans.values()].filter(f).length;
    const pickSrc = this.pickSrc;
    row('deleted', '#c0392b', 'deleted', del, [['Sel', 'select the deleted points (to restore them)', pickSrc('deleted')]]);
    if (partN.size) head('Split parts');
    for (const [name, count] of partN) {
      const has = (sc: Scan) => idsNamed(sc.s.state.parts, name).length > 0, k = scansWith(has);
      row(`part:${name}`, hueCss(name.length, 0.31), `${name}${k > 1 ? ` · ${k} scans` : ''}`, count, [
        ['Sel', 'select its points', pickSrc(`part:${name}`)],
        ['Merge', 'put its points back in their scans', () => this.byName(`merge "${name}" back`, has, sc => ({ op: 'part.merge', part: idsNamed(sc.s.state.parts, name)[0] }) as OpBody)],
      ]);
    }
    this.treeLayers(box);
  }

  // ── the scan path: RTAB-Map's links, coloured by stretch ──────────────
  /** Show the scan paths (Alignment section). */
  private showPath = false;

  /**
   * A scan's path as line set 'epath:<scan>': each RTAB-Map link between two frames' poses, as the editor
   * places them (T · E(f)), coloured by how far that stretches it from RTAB-Map's measurement (springs.ts).
   * Its locked frames' cameras too, shown or not (drawLocked).
   * mats: per frame index, E(f) as previewed (a move in progress); otherwise the tree's.
   */
  private drawPath(sc: Scan, mats?: Float32Array) {
    const s = sc.s, g = s.links, id = `epath:${s.scan}`, pv = mats ? undefined : this.framePreview?.get(s.scan);
    if (pv) mats = pv.mats;
    const ix = new Map(s.frameIds.map((f, i) => [f, i])), t = s.state.tree, memo = new Map<number, Rigid>();
    const E = (f: number) => {
      let e = memo.get(f);
      if (!e) { const i = ix.get(f); e = pv?.E.get(f) ?? (mats && i !== undefined ? matRow(mats, i) : frameTransform(t, f)); memo.set(f, e); }
      return e;
    };
    this.drawLocked(sc, E);
    this.drawCameras(sc, E);
    if (!this.showPath || !g?.links.length) {
      if (sc.path !== undefined) { this.v.setLines(id, new Float32Array(0), [1, 1, 1, 1], false); sc.path = undefined; }
      return;
    }
    const xyz = new Float32Array(6 * g.links.length), col = new Float32Array(8 * g.links.length);
    let cm = 0, deg = 0;
    g.links.forEach((l, k) => {
      const { dt, dr } = linkStretch(g, l, E), c = stretchColor(stretchScale(dt, dr)), a = l.type === 0 ? 1 : 0.55;
      cm = Math.max(cm, 100 * dt); deg = Math.max(deg, (dr * 180) / Math.PI);
      [l.a, l.b].forEach((f, j) => {
        xyz.set(apply(compose(s.T, E(f)), g.pose.get(f)!.t), 6 * k + 3 * j);
        col.set([...c, a], 8 * k + 4 * j);
      });
    });
    this.v.setLines(id, xyz, [1, 1, 1, 1], true, false, s.scan, col);
    sc.path = { cm, deg, n: g.links.length };
  }

  /**
   * A scan's locked frames as line set 'elock:<scan>': each one's camera where the editor places it
   * (T · E(f) · pose), a small frustum looking along the pose's x (RTAB-Map's base frame: x forward, z up)
   * with a tick up from its far edge. Frames without a pose (no pose graph) have none to show.
   */
  private drawLocked(sc: Scan, E: (f: number) => Rigid) {
    const s = sc.s, g = s.links, id = `elock:${s.scan}`;
    const lk = g ? [...s.state.tree.locked].filter(f => g.pose.has(f)) : [];
    if (!lk.length) { if (sc.locked) { this.v.setLines(id, new Float32Array(0), [1, 1, 1, 1], false); sc.locked = false; } return; }
    const seg = baseFrustum();
    const xyz = new Float32Array(6 * seg.length * lk.length);
    lk.forEach((f, k) => {
      const P = compose(s.T, compose(E(f), g!.pose.get(f)!));
      seg.forEach(([a, b], j) => { xyz.set(apply(P, a), 6 * (seg.length * k + j)); xyz.set(apply(P, b), 6 * (seg.length * k + j) + 3); });
    });
    this.v.setLines(id, xyz, [1, 1, 1, 1], true, false, s.scan);
    sc.locked = true;
  }

  /**
   * Show cameras: each selected frame's camera (refresh() finds them) as line set 'ecam:<scan>', where the editor
   * places it (T · E(f) · pose): the optical camera's frustum from its image's intrinsics, or without images
   * the pose graph's base frame (as drawLocked). Frames with a shown image are yellow. Shown images move
   * with their frames, selected or not.
   */
  private drawCameras(sc: Scan, E: (f: number) => Rigid) {
    const s = sc.s, id = `ecam:${s.scan}`, ci = s.images, g = s.links;
    for (const k of this.shownImgs) {
      if (!k.startsWith(s.scan + ':')) continue;
      const f = +k.slice(s.scan.length + 1), cam = ci?.cams.get(f);
      if (cam) this.v.setImageModel('eimg:' + k, mat4(compose(s.T, compose(E(f), cam.pose))));
    }
    for (const f of this.wShown.get(s.scan)?.frames ?? []) {
      const cam = ci?.cams.get(f);
      if (cam) this.v.setImageModel(`wimg:${s.scan}:${f}`, mat4(compose(s.T, compose(E(f), cam.pose))));
    }
    this.camHits = this.camHits.filter(h => h.sc !== sc);
    const fr = [...(sc.camFrames ?? [])].filter(f => ci?.cams.has(f) || g?.pose.has(f));
    if (!fr.length) { if (sc.cams) { this.v.setLines(id, new Float32Array(0), [1, 1, 1, 1], false); sc.cams = false; } return; }
    const xyz: number[] = [], col: number[] = [];
    for (const f of fr) {
      const cam = ci?.cams.get(f), P = compose(s.T, compose(E(f), cam ? cam.pose : g!.pose.get(f)!));
      const seg = (cam ? opticalFrustum(cam.K, ci!.dw, ci!.dh) : baseFrustum()).map(([a, b]): [V3, V3] => [apply(P, a), apply(P, b)]);
      const c = this.shownImgs.has(`${s.scan}:${f}`) ? [1, 0.85, 0.2, 1] : [0.35, 0.8, 1, 1];
      for (const [a, b] of seg) { xyz.push(...a, ...b); col.push(...c, ...c); }
      const fwd: V3 = cam ? [P.R[2], P.R[5], P.R[8]] : [P.R[0], P.R[3], P.R[6]];   // optical z, or the base frame's x
      this.camHits.push({ sc, f, c: P.t, fwd, seg });
    }
    this.v.setLines(id, new Float32Array(xyz), [1, 1, 1, 1], true, false, s.scan, new Float32Array(col));
    sc.cams = true;
  }

  /** The drawn camera with its position or a frustum line nearest (x, y) on the canvas, within r px. */
  private camAt(x: number, y: number, r = 8) {
    let best: CamHit | null = null, bd = r;
    for (const h of this.camHits) {
      if (this.off(h.sc)) continue;
      const c = this.screen(h.c);
      if (c && Math.hypot(c[0] - x, c[1] - y) <= bd) { bd = Math.hypot(c[0] - x, c[1] - y); best = h; }
      for (const [p, q] of h.seg) {
        const a = this.screen(p), b = this.screen(q);
        if (!a || !b) continue;
        const dx = b[0] - a[0], dy = b[1] - a[1], L = dx * dx + dy * dy, t = L ? Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / L)) : 0;
        const d = Math.hypot(a[0] + t * dx - x, a[1] + t * dy - y);
        if (d <= bd) { bd = d; best = h; }
      }
    }
    return best;
  }

  /**
   * A camera's menu: put the view where the camera was, show its frame's photo in the view (or hide it), select the frame, hide every shown photo.
   * Follows the camera as the view moves; Esc, a click anywhere else or any of its buttons closes it.
   */
  private openCamMenu(hit: CamHit) {
    this.closePtMenu(); this.closeViewMenu(); this.closeBoxMenu();
    let cm = this.camMenu;
    if (!cm) {
      const box = el('div', { class: 'ptmenu' });
      const off = (e: PointerEvent) => { if (!box.contains(e.target as Node) && e.target !== this.v.canvas) this.closeCamMenu(); };   // on the view: a drag moves it, a tap elsewhere closes it (tap)
      addEventListener('pointerdown', off, true);
      document.body.append(box);
      cm = this.camMenu = { box, hit, raf: 0, off };
      const tick = () => { this.placeCamMenu(); if (this.camMenu) this.camMenu.raf = requestAnimationFrame(tick); };
      cm.raf = requestAnimationFrame(tick);
    }
    cm.hit = hit;
    const { sc, f } = hit, s = sc.s, key = `${s.scan}:${f}`, ci = s.images, has = !!ci?.cams.has(f);
    const shown = this.shownImgs.has(key) || this.loadingImgs.has(key), n = this.shownImgs.size;
    const scanName = this.v.clouds.find(x => x.meta.id === s.scan)?.meta.name ?? s.scan;
    const go = (fn: () => void) => () => { this.closeCamMenu(); fn(); };
    const b = (t: string, tip: string, fn: () => void, off = false) => { const e = el('button', { class: 'small', title: tip }, t) as HTMLButtonElement; e.onclick = go(fn); e.disabled = off; return e; };
    const box = cm.box;
    box.textContent = '';
    const head = el('div', { class: 'pmhead' });
    head.append(el('b', {}, `camera · frame ${f}`));
    box.append(head,
      el('div', { class: 'help' }, `${this.scans.size > 1 ? `${scanName} · ` : ''}${has ? `photo ${ci!.w} × ${ci!.h}` : 'no photo in this build'}`),
      b(shown ? 'Hide image' : 'Show image',
        !has ? "this scan's build has no camera images (prep/images.py --scene, then Publish)"
          : shown ? "take this frame's photo out of the view"
            : "draw this frame's photo where it was taken, each pixel at its measured depth; it moves with the frame",
        () => (shown ? this.hideImages(key) : void this.showImage(sc, f)), !has),
      b('Set to view', "move the view's camera to this frame's camera, looking where it looked (kept upright: the view has no roll)", () => this.viewFromCam(hit)),
      b(`Select frame ${f}`, `select the points of frame ${f}`, () => this.selFrames([{ sc, f }], { t: 'camera', f })),
      ...(this.photo.feats.size ? [b(this.photoMarks ? 'Hide photo features' : 'Show photo features',
        'draw what the photo steps found on the shown photos: lines (in their plane\'s colour), corners, the depth\'s jumps (cyan) and creases (blue)',
        () => { this.photoMarks = !this.photoMarks; this.redrawPhotos(); }),
        b('Clear photo results', 'forget what the photo steps found (features, matches, 3D edges): photos and the view drawn without them', () => { this.photo.clear(); this.featVer++; this.shading(); this.drawPhotoEdges(); this.redrawPhotos(); })] : []),
      ...(n ? [b(`Hide all images (${n})`, 'take every shown photo out of the view', () => this.hideImages())] : []));
    this.placeCamMenu();
  }

  /** Move the view's camera to a frame's camera, looking along it (out of plan view; upright, as the view has no roll). */
  private viewFromCam(hit: CamHit) {
    const cam = this.v.cam;
    if (cam.mode === 'plan') this.$('modes').querySelector<HTMLButtonElement>('button[data-m="orbit"]')?.click();
    const d = hit.fwd, n = Math.hypot(d[0], d[1], d[2]) || 1;
    cam.eye = [...hit.c];
    cam.yaw = Math.atan2(d[1], d[0]);
    cam.pitch = clamp(Math.asin(clamp(d[2] / n, -1, 1)), -1.55, 1.55);   // as orbit keeps it
    this.ctl.pivot = null;
    this.ctl.touched();
  }

  /** Put the camera's menu beside it, inside the window; hidden while it is behind the camera. */
  private placeCamMenu() {
    const cm = this.camMenu;
    if (!cm) return;
    const s = this.screen(cm.hit.c), box = cm.box;
    box.style.visibility = s ? '' : 'hidden';
    if (!s) return;
    const r = this.v.canvas.getBoundingClientRect(), w = box.offsetWidth, h = box.offsetHeight;
    box.style.left = `${Math.max(8, Math.min(innerWidth - w - 8, r.left + s[0] + 12))}px`;
    box.style.top = `${Math.max(8, Math.min(innerHeight - h - 8, r.top + s[1] - 12))}px`;
  }

  private closeCamMenu() {
    const cm = this.camMenu;
    if (!cm) return;
    this.camMenu = null;
    cancelAnimationFrame(cm.raf);
    removeEventListener('pointerdown', cm.off, true);
    cm.box.remove();
  }

  /** Draw frame f's photo on its depth map (Viewer image 'eimg:<scan>:<f>'), placed as the frame is now. */
  private async showImage(sc: Scan, f: number) {
    const s = sc.s, key = `${s.scan}:${f}`, cam = s.images?.cams.get(f);
    if (!cam || this.shownImgs.has(key) || this.loadingImgs.has(key)) return;
    this.loadingImgs.add(key);
    this.say(`loading frame ${f}'s photo…`);
    try {
      let { img, depth } = await loadImage(s.images!, f);
      const ff = this.photoMarks ? this.photo.feats.get(key) ?? (this.v.s.mode === 9 ? this.featCache.get(key) : undefined) : undefined;
      if (ff) { const marked = await overlay(img, ff, { agreed: this.photo.edgeMatches.get(key)?.agreed }); img.close(); img = marked; }
      if (!this.loadingImgs.delete(key) || this.scans.get(s.scan) !== sc) { img.close(); return; }   // hidden (or the scans dropped) meanwhile
      const ci = s.images!;
      this.v.setImage('eimg:' + key, { depth, dw: ci.dw, dh: ci.dh, K: cam.K, img, model: mat4(identity()), of: s.scan });
      this.shownImgs.add(key);
      this.say(`frame ${f}'s photo shown (its camera's menu hides it)`);
      this.drawPath(sc);   // places it (and colours its camera)
    } catch (err) {
      this.loadingImgs.delete(key);
      this.say(`frame ${f}'s photo: ${(err as Error).message}`, true);
    }
  }

  /** Take camera image key ('<scan>:<frame>') out of the view, or every one. */
  private hideImages(key?: string) {
    const keys = key ? [key] : [...this.shownImgs, ...this.loadingImgs];
    for (const k of keys) { this.shownImgs.delete(k); this.loadingImgs.delete(k); this.v.removeImages('eimg:' + k, true); }
    for (const id of new Set(keys.map(k => k.slice(0, k.lastIndexOf(':'))))) { const sc = this.scans.get(id); if (sc) this.drawPath(sc); }
  }

  // ── alignment tree ────────────────────────────────────────────────────
  /** Tree rows expanded in the panel ('<scan>|<node key>'). */
  private open = new Set<string>();

  private parseNode(k: string): TreeNode | null {
    if (k === 'scan') return { k: 'scan' };
    if (k.startsWith('g:')) return { k: 'g', name: k.slice(2) };
    if (k.startsWith('f:') && +k.slice(2) > 0) return { k: 'f', frame: +k.slice(2) };
    return null;
  }

  /** Points per frame index of a scan (cached). */
  private frameCounts(sc: Scan) {
    if (!sc.fcount) { sc.fcount = new Uint32Array(sc.s.frameIds.length); for (const k of sc.s.frameIx) sc.fcount[k]++; }
    return sc.fcount;
  }

  /** The Alignment section: per scan, scan > groups > frames, each movable. */
  private treeLayers(box: HTMLElement) {
    if (!this.scans.size) return;
    box.append(el('div', { class: 'layer hd' }, 'Alignment (scan › groups › frames)'));
    if ([...this.scans.values()].some(sc => sc.s.links?.links.length)) {
      const lab = el('label', { class: 'layer', title: "RTAB-Map's links between frames, each coloured by how far the frames as placed now stretch it from what RTAB-Map measured" });
      const cb = el('input', { type: 'checkbox' }) as HTMLInputElement;
      cb.checked = this.showPath;
      cb.onchange = () => { this.showPath = cb.checked; this.refresh(); };
      lab.append(cb, ' scan path ', el('span', { class: 'help' }, 'green: as RTAB-Map measured · yellow ~2 cm / 1° · red ≥ 10 cm / 5°'));
      box.append(lab);
    }
    for (const [id, sc] of this.scans) {
      const s = sc.s, t = s.state.tree, fc = this.frameCounts(sc);
      if (!s.frame) continue;
      const counts = new Map<string, number>(), add = (k: string, c: number) => counts.set(k, (counts.get(k) ?? 0) + c);
      const byGroup = new Map<string | null, number[]>();
      s.frameIds.forEach((f, i) => {
        add('scan', fc[i]); add(`f:${f}`, fc[i]);
        for (let g = t.frameGroup.get(f) ?? null, k = 0; g !== null && k <= t.groups.size; k++) { add(`g:${g}`, fc[i]); g = t.groups.get(g)!.parent; }
        const g = t.frameGroup.get(f) ?? null;
        (byGroup.get(g) ?? byGroup.set(g, []).get(g)!).push(f);
      });
      const count = (n: TreeNode) => counts.get(nodeKey(n)) ?? 0;
      const name = (this.v.clouds.find(c => c.meta.id === id)?.meta.name ?? id).replace(/^RTAB /, '');
      const row = (n: TreeNode, depth: number, label: string, kids: boolean) => {
        const key = `${id}|${nodeKey(n)}`, hk = `tree:${key}`, off = this.hidden.has(hk);
        const r = el('div', { class: 'layer tree' + (off ? ' off' : '') });
        r.style.paddingLeft = `${depth * 12}px`;
        const eye = el('button', { class: 'eye', title: off ? 'show' : 'hide' }, off ? '◌' : '●');
        eye.onclick = () => this.toggleLayer(hk);
        const tw = el('button', { class: 'eye', title: kids ? 'expand / collapse' : '' }, kids ? (this.open.has(key) ? '▾' : '▸') : '');
        if (kids) tw.onclick = () => { if (this.open.has(key)) this.open.delete(key); else this.open.add(key); this.refreshLists(); };
        const D = nodeD(t, n).D, b = el('span', { class: 'row' });
        const btn = (lbl: string, tip: string, f: () => void) => { const x = el('button', { class: 'small', title: tip }, lbl); x.onclick = f; b.append(x); };
        btn('Sel', 'select its points', () => this.applyMasks(new Map([[id, s.framesMask(new Set(s.frameIds.filter(f => under(t, n, f))))]]), { t: 'tree', scan: id, node: nodeKey(n) }));
        btn('Move', 'move / turn it with the gizmo (arrows: along x, y, z; centre: across the floor; ⟳: turn), then Apply', () => this.moveNode(sc, n));
        if (!isIdentity(D, 1e-7)) btn('0', 'undo its own correction (back to identity)', () => this.commitTree(sc, `reset ${label}`, { op: 'tree.transform', D: { [nodeKey(n)]: { R: [1, 0, 0, 0, 1, 0, 0, 0, 1], t: [0, 0, 0] } } } as OpBody));
        if (n.k === 'g') btn('+', 'add the frames of the selected points to this group', () => this.addFramesTo(sc, n.name));
        if (n.k === 'g') btn('−', 'take the frames of the selected points out of this group (they go up a level, unmoved)', () => this.removeFramesFrom(sc, n.name));
        if (n.k !== 'f') btn('New', `new group ${n.k === 'g' ? 'inside this one' : 'here'} from the frames of the selected points`, () => this.groupSelected(sc, n.k === 'g' ? n.name : null));
        if (n.k === 'g') {
          btn('↑', 'put this group under another (empty: the scan)', () => {
            const to = prompt(`Put "${n.name}" under which group? (empty: the scan)`, '');
            if (to === null) return;
            if (to && !t.groups.has(to)) { this.say(`no group "${to}" in ${name}`); return; }
            this.commitTree(sc, `move group "${n.name}"`, { op: 'tree.move', name: n.name, under: to || null } as OpBody);
          });
          btn('×', 'ungroup (its frames and groups go up a level; nothing moves)', () => this.commitTree(sc, `ungroup "${n.name}"`, { op: 'tree.ungroup', name: n.name } as OpBody));
        }
        r.append(eye, tw, el('span', { class: 'name', title: label }, label + moved(D)), el('span', { class: 'n' }, count(n).toLocaleString()), b);
        box.append(r);
        return this.open.has(key);
      };
      const walk = (g: string | null, depth: number) => {
        for (const [gn] of [...t.groups].filter(([, x]) => x.parent === g).sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
          const fr = byGroup.get(gn) ?? [];
          if (row({ k: 'g', name: gn }, depth, `${gn} (${fr.length} frames)`, true)) walk(gn, depth + 1);
        }
        const fr = (byGroup.get(g) ?? []).filter(f => f > 0);
        const show = g === null ? this.open.has(`${id}|frames`) : true;
        if (g === null && fr.length) {
          const r = el('div', { class: 'layer tree' });
          r.style.paddingLeft = `${depth * 12}px`;
          const tw = el('button', { class: 'eye' }, show ? '▾' : '▸');
          tw.onclick = () => { const k = `${id}|frames`; if (this.open.has(k)) this.open.delete(k); else this.open.add(k); this.refreshLists(); };
          r.append(el('span'), tw, el('span', { class: 'name' }, `${fr.length} ungrouped frames`), el('span', { class: 'n' }, ''), el('span'));
          box.append(r);
        }
        if (show) for (const f of fr) row({ k: 'f', frame: f }, depth + (g === null ? 1 : 0), `frame ${f}${t.locked.has(f) ? ' 🔒' : ''}`, false);
      };
      const pth = sc.path ? ` · path stretched ≤ ${sc.path.cm.toFixed(1)} cm, ${sc.path.deg.toFixed(1)}°` : '';
      if (row({ k: 'scan' }, 0, `${name} (${s.frameIds.filter(f => f > 0).length} frames)${pth}`, true)) walk(null, 1);
    }
  }

  /** One tree op on one scan, as a normal action (history, undo). */
  private commitTree(sc: Scan, label: string, body: OpBody) {
    this.act(label, x => (x === sc ? body : null), { also: x => x === sc, keepSel: true, keepDeleted: true });
  }

  /** New group under `parent` (null: the scan) from the frames of this scan's selected points. */
  /** Frames (ids) of this scan's selected points. */
  private selectedFrames(sc: Scan) {
    const s = sc.s, fr = new Set<number>();
    if (sc.mask && s.frame) for (let v = 0; v < s.nv; v++) if (sc.mask[v] && s.frame[v]) fr.add(s.frame[v]);
    return fr;
  }

  /** Take the frames of the selected points out of a tree group: they go up to its parent, unmoved. */
  private removeFramesFrom(sc: Scan, name: string) {
    const t = sc.s.state.tree, out = this.selectedFrames(sc), g = t.groups.get(name);
    if (!g) return;
    const own = [...t.frameGroup].filter(([, x]) => x === name).map(([f]) => f), keep = own.filter(f => !out.has(f));
    if (keep.length === own.length) { this.say(`none of the selected frames are directly in "${name}"`); return; }
    this.commitTree(sc, `take ${own.length - keep.length} frames out of "${name}"`, { op: 'tree.group', name, under: g.parent, frames: keep.sort((a, b) => a - b) } as OpBody);
  }

  /** Add the frames of the selected points to an existing tree group (they move into it, unmoved). */
  private addFramesTo(sc: Scan, name: string) {
    const t = sc.s.state.tree, fr = this.selectedFrames(sc), g = t.groups.get(name);
    if (!g) return;
    const before = [...t.frameGroup].filter(([, x]) => x === name).length;
    for (const [f, x] of t.frameGroup) if (x === name) fr.add(f);
    if (fr.size === before) { this.say(`select points of frames not already in "${name}" first`); return; }
    this.commitTree(sc, `add ${fr.size - before} frames to "${name}"`, { op: 'tree.group', name, under: g.parent, frames: [...fr].sort((a, b) => a - b) } as OpBody);
  }

  // ── user classes ──────────────────────────────────────────────────────
  /** User classes by name across the loaded scans, with the colour the first scan that has it gives. */
  private userClasses(): Map<string, string> {
    const m = new Map<string, string>();
    for (const sc of this.scans.values()) for (const c of sc.s.state.classes.values()) if (!m.has(c.name)) m.set(c.name, c.color);
    return new Map([...m].sort((a, b) => (a[0] < b[0] ? -1 : 1)));
  }

  /** Each class name's index in the tint table (built-ins 0-3, then user classes by name). */
  private classIndex() {
    const m = new Map<string, number>(CLS.map((c, i) => [c, i]));
    [...this.userClasses().keys()].forEach((n, i) => m.set(n, Math.min(MAX_CLASS, CLS.length + i)));
    return m;
  }

  /** This scan's class ids -> tint table index. */
  private clsIx(sc: Scan, ix = this.classIndex()) {
    const a = new Uint8Array(MAX_CLASS + 1);
    for (let k = 0; k < CLS.length; k++) a[k] = k;
    for (const [id, c] of sc.s.state.classes) a[id] = ix.get(c.name) ?? 0;
    return a;
  }

  private classColors(ix: Map<string, number>, users: Map<string, string>) {
    const a = new Uint8Array(4 * (MAX_CLASS + 1));
    this.builtinColors().forEach((color, k) => a.set([...hexRgb(color), 255], 4 * k));
    for (const [n, c] of users) a.set([...hexRgb(c), 255], 4 * ix.get(n)!);
    return a;
  }

  /** The built-in classes' colours by id (0-3): the first loaded scan that set one (class.color), else the default. */
  private builtinColors(): string[] {
    return CLS.map((_, k) => {
      for (const sc of this.scans.values()) { const c = sc.s.state.clsColors.get(k); if (c) return c; }
      return CLS_LAYER.find(l => l[0] === `cls:${k}`)![2];
    });
  }

  private freeColor() {
    const used = new Set([...this.userClasses().values()]);
    return CLASS_PALETTE.find(c => !used.has(c)) ?? CLASS_PALETTE[used.size % CLASS_PALETTE.length];
  }

  /** A new user class in every loaded scan; the + on its row (Classes, top right) classifies. */
  private newClass() {
    const name = prompt('Name of the new class (letters, digits, space . : + -)', '');
    if (!name) return;
    if (!isClassName(name)) { this.say('bad class name (or a built-in one)'); return; }
    if (this.userClasses().has(name)) { this.say(`"${name}" exists: use the + on its row`); return; }
    const color = this.freeColor();
    this.act(`new class "${name}"`, sc => {
      const id = nextClassId(sc.s.state);
      return id > MAX_CLASS ? null : ({ op: 'class.new', cls: id, name, color }) as OpBody;
    }, { also: () => true, keepSel: true, keepDeleted: true });
  }

  /** A memory group's + / −: change it with the selected points, as a Group edit step does; Undo takes it back. */
  private editMemGroup(act: 'add' | 'remove', name: string) {
    if (this.busy) return;
    const before = this.snapSel();
    let n = 0;
    for (const sc of this.scans.values()) {
      const g = sc.mask && memEdit(this.memOf(sc.s.scan), sc.mask.slice(), act, name);
      if (g) { this.mem.set(sc.s.scan, g); n++; }
    }
    if (!n) { this.say(act === 'add' ? 'select some points first' : 'none of the selected points are in it'); return; }
    this.step({ k: 'sel', snap: before });
    this.future = [];
    this.refresh();
  }

  /** Forget a memory group in every scan (Undo brings it back). */
  private dropMemGroup(name: string) {
    if (this.busy) return;
    this.step({ k: 'sel', snap: this.snapSel() });
    this.future = [];
    for (const [id, g] of this.mem) if (g.has(name)) { const o = new Map(g); o.delete(name); this.mem.set(id, o); }
    if (this.soloGroup === name) this.soloGroup = null;
    this.refresh();
  }

  /** A memory group's Save: write it as a saved group (a name asked, its own by default), as a Group save step does. */
  private saveMemGroup(name: string) {
    if (this.job) { this.say(`${this.job.title} is running: edits wait until it is done (or Cancel it)`, true); return; }
    const as = prompt(`Save memory group "${name}" to the scan as the group (letters, digits, space . : + -; a saved group by that name has its points replaced)`, name)?.trim();
    if (!as) return;
    if (!GROUP_NAME.test(as)) { this.say('bad group name'); return; }
    const before = this.snapSel(), stamp = stampNow(`save group "${as}"`), done: Action = [];
    this.run(`save group "${as}"`, async () => {
      try { await this.groupSaveStep({ name, as }, stamp, done); } finally {
        if (done.length) { this.step({ k: 'edit', snap: before }); this.stepStamp = stamp.t; this.future = []; }
      }
      if (this.soloGroup === name && as !== name) this.soloGroup = as;
    });
  }

  /** Point groups whose sub-groups are folded away in the panel. */
  private closedGroups = new Set<string>();

  /** Take the selected points out of a saved point group (its own points; groups inside it keep theirs). */
  private removeFromGroup(name: string) {
    this.act(`take out of group "${name}"`, (sc, m) => this.groupOps(sc, m, 'remove', name), { keepDeleted: true, keepSel: true });
  }

  /**
   * The ops editing a scan's point group with its selected points m (deleted ones too): add moves them into it (out
   * of any other, making it if missing), remove takes them out of its own points, clear empties it (making it empty
   * if missing). null: nothing to change in this scan.
   */
  private groupOps(sc: Scan, m: Uint8Array, act: 'add' | 'remove' | 'clear', name: string): OpBody[] | null {
    const has = sc.s.state.groups.has(name);
    if (act === 'clear') return has && !sc.s.state.groups.get(name)!.n ? null : [{ op: 'group.save', name, set: encodeSet([]) } as OpBody];
    if (!m.some(x => x)) return null;
    const g = sc.s.group(name);
    if (act === 'add') {
      for (let i = 0; i < g.length; i++) g[i] |= m[i];
      // a point is in one group at a time: out of the others first, then into this one (one action)
      return [...this.outOfOtherGroups(sc, m, name), { op: 'group.save', name, set: sc.s.toSet(g) } as OpBody];
    }
    if (!has) return null;
    let k = 0;
    for (let i = 0; i < g.length; i++) if (g[i] && m[i]) { g[i] = 0; k++; }
    return k ? [{ op: 'group.save', name, set: sc.s.toSet(g) } as OpBody] : null;
  }

  /** The loaded scans' point group names, sorted. */
  private groupNames() { return [...new Set([...this.scans.values()].flatMap(sc => [...sc.s.state.groups.keys(), ...this.memOf(sc.s.scan).keys()]))].sort(); }

  /** A new group inside `parent` from the selected points (in each scan with a selection; the parent is made there if missing). */
  private newSubGroup(parent: string) {
    const name = prompt(`Name of the new group inside "${parent}" (letters, digits, space . : + -)`, '');
    if (!name) return;
    if (!GROUP_NAME.test(name)) { this.say('bad group name'); return; }
    if (name === parent) { this.say('a group cannot contain itself'); return; }
    this.act(`new group "${name}" in "${parent}"`, (sc, m) => {
      const st = sc.s.state;
      if (st.groups.has(name) && groupAndBelow(st, name).includes(parent)) return null;   // would contain itself
      const ops: OpBody[] = this.outOfOtherGroups(sc, m, name);   // (the parent included: they go into the new group)
      if (!st.groups.has(parent)) ops.push({ op: 'group.save', name: parent, set: encodeSet([]) } as OpBody);
      const g = sc.s.group(name);
      for (let i = 0; i < g.length; i++) g[i] |= m[i];
      ops.push({ op: 'group.save', name, set: sc.s.toSet(g), under: parent } as OpBody);
      return ops;
    }, { keepDeleted: true, keepSel: true });
  }

  /** Put a group inside another (empty: top level), in every scan that has it. */
  private moveGroup(name: string) {
    const to = prompt(`Put "${name}" inside which group? (empty: top level)`, '');
    if (to === null) return;
    const under = to.trim() || null;
    if (under !== null && !GROUP_NAME.test(under)) { this.say('bad group name'); return; }
    for (const sc of this.scans.values())
      if (under !== null && sc.s.state.groups.has(name) && groupAndBelow(sc.s.state, name).includes(under)) { this.say(`"${under}" is inside "${name}"`); return; }
    this.byName(`put "${name}" in ${under ? `"${under}"` : 'top level'}`, sc => sc.s.state.groups.has(name), sc => {
      const ops: OpBody[] = [];
      if (under !== null && !sc.s.state.groups.has(under)) ops.push({ op: 'group.save', name: under, set: encodeSet([]) } as OpBody);
      ops.push({ op: 'group.move', name, under } as OpBody);
      return ops;
    });
  }

  /** Move the selected points into a saved point group (out of any other), in every scan with a selection (creating it where missing). */
  private addToGroup(name: string) {
    this.act(`add to group "${name}"`, (sc, m) => this.groupOps(sc, m, 'add', name), { keepDeleted: true, keepSel: true });
  }

  private groupSelected(sc: Scan, parent: string | null) {
    const t = sc.s.state.tree, fr = this.selectedFrames(sc);
    if (!fr.size) { this.say('select some points of the frames to group first (e.g. Frames, or Sel on a row)'); return; }
    const name = prompt(`Group name for ${fr.size} frame(s) (letters, digits, space . : + -)`, `group ${t.groups.size + 1}`);
    if (!name) return;
    if (!GROUP_NAME.test(name)) { this.say('bad group name'); return; }
    const old = t.groups.get(name);
    if (old && old.parent !== parent) { this.say(`"${name}" already exists elsewhere in the tree`); return; }
    if (old) for (const [f, g] of t.frameGroup) if (g === name) fr.add(f);   // adding to an existing group keeps its frames
    this.commitTree(sc, `group ${fr.size} frames as "${name}"`, { op: 'tree.group', name, under: parent, frames: [...fr].sort((a, b) => a - b) } as OpBody);
  }

  /** The move / turn gizmo on the screen, if any (one at a time). */
  private gizmo: Gizmo | null = null;

  /**
   * Move a node (the scan, a group or a frame) with the gizmo, previewed live: W is a move in the world,
   * and a frame under the node is drawn at T⁻¹·W·T·E(f) (T: the scan's placement). Apply commits
   * D' = (T·P)⁻¹ · W · (T·P) · D for the node (P: the transform of what it sits in), one undoable edit.
   */
  private moveNode(sc: Scan, n: TreeNode) {
    this.gizmo?.cancel();
    if (n.k === 'f' && sc.s.state.tree.locked.has(n.frame)) { this.say(`frame ${n.frame} is locked: unlock it to move it`); return; }
    const s = this.placed(sc), t = s.state.tree, inNode = s.frameIds.map(f => under(t, n, f));
    // the gizmo sits at the centre of the node's points that are shown (not deleted or hidden ones)
    const live = this.live(sc), c = [0, 0, 0];
    let k = 0;
    for (let i = 0; i < s.nv; i++) if (live[i] && inNode[s.frameIx[i]]) { c[0] += s.P[3 * i]; c[1] += s.P[3 * i + 1]; c[2] += s.P[3 * i + 2]; k++; }
    if (!k) { this.say('no points shown there'); return; }
    const id = s.scan, base = s.frameMats.slice(), T = s.T, Ti = inverse(T);
    const preview = (W: Rigid) => {
      const m = base.slice(), A = compose(Ti, compose(W, T));
      inNode.forEach((on, i) => {
        if (!on) return;
        const E = compose(A, matRow(base, i)), R = E.R, tt = E.t;
        m.set([R[0], R[1], R[2], tt[0], R[3], R[4], R[5], tt[1], R[6], R[7], R[8], tt[2]], 12 * i);
      });
      this.v.setEditFrames(id, m);
      this.drawPath(sc, m);
    };
    const label = n.k === 'scan' ? 'the scan' : n.k === 'g' ? `group "${n.name}"` : `frame ${n.frame}`;
    this.gizmo = new Gizmo(this.v, [c[0] / k, c[1] / k, c[2] / k], `Move ${label}`, {
      change: preview,
      apply: (W, made) => {
        this.gizmo = null;
        this.v.setEditFrames(id, base);
        if (isIdentity(W, 1e-9)) return;
        const { D, prefix } = nodeD(t, n);
        const cm = made.shift.map(x => (x * 100).toFixed(1)).join(' ');
        this.commitTree(sc, `move ${nodeKey(n)} (moved ${cm} cm, turned ${made.yawDeg.toFixed(2)}°)`, { op: 'tree.transform', D: { [nodeKey(n)]: worldToNodeD(s.T, prefix, D, W) } } as OpBody);
      },
      cancel: () => { this.gizmo = null; this.v.setEditFrames(id, base); },
    });
  }


  /** One history across scans: ops with the same time and message are one action. */
  private history() {
    const box = this.$('ehist');
    box.textContent = '';
    type E = { t: string; label: string; ops: { scan: string; id: string | null }[]; head: boolean; scans: Set<string> };
    const entries = new Map<string, E>();
    const add = (cls: string, scan: string, id: string, t: string, label: string, head: boolean) => {
      const k = `${cls}|${t}|${label}`;
      const e = entries.get(k) ?? { t, label, ops: [], head: false, scans: new Set<string>() };
      e.ops.push({ scan, id }); e.scans.add(scan); e.head ||= head;
      entries.set(k, e);
    };
    const tags = new Map<string, Set<string>>();     // checkpoint name -> scans
    const refs: Record<string, { scan: string; id: string }[]> = {};
    for (const [scan, sc] of this.scans) {
      const g = sc.s.graph;
      for (const o of sc.s.path()) add('', scan, o.id, o.t, o.msg ?? OP_LABEL[o.op], o.id === g.head);
      for (const o of g.ops.filter(o => o.parent === g.head)) add('undone', scan, o.id, o.t, o.msg ?? OP_LABEL[o.op], false);
      for (const [name, id] of Object.entries(g.refs)) { (refs[name] ??= []).push({ scan, id }); (tags.get(name) ?? tags.set(name, new Set()).get(name)!).add(scan); }
    }
    const shortName = (id: string) => (this.v.clouds.find(c => c.meta.id === id)?.meta.name ?? id).replace(/^RTAB /, '').replace(/ \(.*\)$/, '');
    const line = (e: E, cls: string, prefix = '') => {
      const where = e.scans.size > 1 ? `${e.scans.size} scans` : shortName([...e.scans][0]);
      const r = el('div', { class: 'hist ' + cls + (e.head ? ' head' : ''), title: [...e.scans].map(shortName).join(', ') });
      r.append(el('span', {}, `${prefix}${e.label}`), el('span', { class: 't' }, `${where} · ${ago(e.t)}`));
      r.onclick = () => this.run('checkout', async () => {
        for (const o of e.ops) { const sc = this.scans.get(o.scan); if (sc && sc.s.head !== o.id) await sc.s.checkout(o.id); }
        this.past = this.past.filter(x => x.k === 'sel'); this.future = [];   // edit steps no longer line up with HEAD
      });
      box.append(r);
    };
    const all = [...entries.entries()].sort((a, b) => (a[1].t < b[1].t ? 1 : -1));
    for (const [, e] of all.filter(([k]) => k.startsWith('undone|')).slice(0, 3)) line(e, 'off', '↷ ');
    for (const [, e] of all.filter(([k]) => k.startsWith('|')).slice(0, 80)) line(e, '');
    const names = Object.keys(refs);
    if (names.length) {
      box.append(el('div', { class: 'help' }, 'Checkpoints:'));
      for (const name of names) line({ t: '', label: `⚑ ${name}`, ops: refs[name], head: false, scans: tags.get(name)! }, 'off');
    }
    if (!all.length) box.append(el('div', { class: 'help' }, 'No edits yet.'));
  }
}

/** An input for a variable no step's input says what it takes (it is only passed to a macro not saved): any text. */
/** A field anyField made (a Points source's is never blank). */
const isAny = (f: Field) => f.k === 'src' && f.dflt === '';
/** A data value for the page: a few significant figures. */
const fmtVal = (x: number) => (Number.isInteger(x) ? String(x) : String(+x.toPrecision(4)));
